import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { FixedSizeList as VirtualList, VariableSizeList as VirtualVarList } from "react-window";
import CommandBar from "../Components/Premium/CommandBar";
import DailyBriefingCard from "../Components/Premium/DailyBriefingCard";
import { InjectSkeletonKeyframes, SkeletonList } from "../Components/Premium/Skeleton";
import MobileBottomNav from "../Components/Premium/MobileBottomNav";
import QuickLookModal from "../Components/Premium/QuickLookModal";
import {
  BlockDesignInspector,
  RichTextBlockEditor,
  blockInnerTypographyStyle,
  blockOuterStyle,
  buttonPreviewStyle,
  mergeDefaultStyle,
  plainTextFromHtml
} from "../Components/EmailTemplate/TemplateEditorEnhancements";
import { exportTemplateToMjml } from "../Components/EmailTemplate/mjmlExport";

const resolveApiBaseUrl = () => {
  const configured = String(process.env.REACT_APP_API_BASE_URL || "").trim();
  if (configured) return configured;
  // Dev/E2E: use same-origin + CRA proxy (avoids CORS + keeps cookies reliable).
  return process.env.NODE_ENV === "production" ? "/" : "";
};

const apiBaseUrl = resolveApiBaseUrl();

const isWebSocketEnabled = () => {
  const explicit = String(process.env.REACT_APP_WS_ENABLED || "").trim().toLowerCase();
  if (explicit) {
    return explicit === "1" || explicit === "true" || explicit === "yes";
  }
  return false;
};

const getWebSocketUrl = () => {
  const explicitWsUrl = String(process.env.REACT_APP_WS_URL || "").trim();
  if (explicitWsUrl) return explicitWsUrl;
  if (typeof window !== "undefined") {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}/ws`;
  }
  if (apiBaseUrl.startsWith("https://")) {
    return `wss://${apiBaseUrl.slice("https://".length).replace(/\/+$/, "")}/ws`;
  }
  if (apiBaseUrl.startsWith("http://")) {
    return `ws://${apiBaseUrl.slice("http://".length).replace(/\/+$/, "")}/ws`;
  }
  return "ws://127.0.0.1:4000/ws";
};

const api = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
  timeout: Number(process.env.REACT_APP_API_TIMEOUT_MS || 15000)
});

// Used to confirm session validity without triggering the global 401 handler.
const authProbe = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
  timeout: Number(process.env.REACT_APP_API_TIMEOUT_MS || 15000)
});

const CACHE_PREFIX = "netluq_cache_v1:";
const cacheGet = (key) => {
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (Number(parsed.expiresAt || 0) < Date.now()) return null;
    return parsed.value ?? null;
  } catch (_e) {
    return null;
  }
};

const cacheSet = (key, value, ttlMs) => {
  try {
    const payload = { value, expiresAt: Date.now() + Math.max(1000, Number(ttlMs || 0)) };
    window.localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(payload));
  } catch (_e) {
    // ignore
  }
};

const normalizeHay = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const localMailSearch = (messages, parsed, limit = 10) => {
  const q = normalizeHay(parsed?.text || "");
  const f = parsed?.filters || {};
  const fromNeedle = normalizeHay(f.from || "");
  const subjectNeedle = normalizeHay(f.subject || "");
  const out = [];
  for (const m of messages || []) {
    if (!m) continue;
    if (f.unread && Boolean(m.is_read)) continue;
    if (f.starred && !Boolean(m.is_starred)) continue;
    if (f.archived && !Boolean(m.is_archived)) continue;
    const from = normalizeHay(`${m.from_name || ""} ${m.from_email || ""}`);
    const subj = normalizeHay(m.subject || "");
    if (fromNeedle && !from.includes(fromNeedle)) continue;
    if (subjectNeedle && !subj.includes(subjectNeedle)) continue;
    const hay = normalizeHay(`${m.subject || ""} ${m.snippet || ""} ${m.from_name || ""} ${m.from_email || ""}`);
    if (q && !hay.includes(q)) continue;
    out.push(m);
    if (out.length >= limit) break;
  }
  return out;
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status;
    const rel = String(error?.config?.url || error?.config?.baseURL + (error?.config?.url || "") || "");
    const isAuthForm =
      rel.includes("/api/auth/login") || rel.includes("/api/auth/register");
    if (status === 401 && !isAuthForm) {
      if (typeof window !== "undefined" && window.__NETLUQ_E2E_DISABLE_AUTH_EXPIRED) {
        return Promise.reject(error);
      }
      let hadSession = false;
      try {
        hadSession = Boolean(window.localStorage.getItem("netluq_auth_user"));
      } catch (_e) {
        hadSession = false;
      }
      // Only force-logout if we previously believed we had a session.
      if (!hadSession) {
        return Promise.reject(error);
      }

      // Avoid false-positive logouts: confirm session is really invalid.
      try {
        if (!String(rel || "").includes("/api/auth/me")) {
          const me = await authProbe.get("/api/auth/me");
          if (me?.status === 200 && me?.data?.user) {
            return Promise.reject(error);
          }
        }
      } catch (_e) {
        // continue to logout
      }
      try {
        window.localStorage.removeItem("netluq_auth_user");
      } catch (_e) {
        // ignore
      }
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("netluq:auth-expired"));
      }
    }
    return Promise.reject(error);
  }
);

const defaultCompose = {
  to: [],
  cc: [],
  bcc: [],
  subject: "",
  text: "",
  html: "",
  inReplyTo: "",
  references: [],
  attachments: []
};

const composeEmojiOptions = [
  "😀","😃","😄","😁","😆","😅","😂","🤣","😊","🙂","😉","😍","🥰","😘","😜","🤪","😎","🤓","🤩","🤗",
  "😌","😔","😕","🙁","☹️","😢","😭","😤","😠","😡","🤯","😱","😳","😬","🤔","🤭","🤫","🤝","👏","🙏",
  "👍","👎","❤️","💙","💚","💛","🧡","💜","✨","🎉","🔥","⭐","📌","📣","📅","✅","⚠️","🚀","💡","📎"
];

const composeFontOptions = ["Arial", "Helvetica", "Times New Roman", "Georgia", "Verdana", "Tahoma", "Courier New"];
const composeFontSizeOptions = [
  { label: "12", value: "2" },
  { label: "14", value: "3" },
  { label: "16", value: "4" },
  { label: "18", value: "5" },
  { label: "24", value: "6" }
];

const AUTH_STORAGE_KEY = "netluq_auth_user";
const ACTIVE_PAGE_STORAGE_KEY = "netluq_active_page";
const INBOX_VIEW_MODE_STORAGE_KEY = "netluq_inbox_view_mode";
const INBOX_DENSITY_STORAGE_KEY = "netluq_inbox_density_mode";

const buildTemplateDraft = (template = {}) => ({
  id: template.id || `custom-${Date.now()}`,
  name: template.name || "New Template",
  category: template.category || "General",
  subject: template.subject || "",
  body: template.body || "",
  heroImageUrl: template.heroImageUrl || "",
  ctaText: template.ctaText || "Learn more",
  ctaUrl: template.ctaUrl || "",
  blocks: normalizeTemplateBlocks(template)
});

const templateElementBlocks = [
  { id: "text", label: "Text", icon: "T" },
  { id: "columns", label: "2 Columns", icon: "⊞" },
  { id: "image", label: "Image", icon: "🖼" },
  { id: "video", label: "Video", icon: "▶" },
  { id: "audio", label: "Audio", icon: "♪" },
  { id: "button", label: "Button", icon: "▭" },
  { id: "logo", label: "Logo", icon: "◉" },
  { id: "divider", label: "Divider", icon: "—" },
  { id: "social", label: "Social", icon: "◎" },
  { id: "footer", label: "Footer", icon: "▤" },
  { id: "code", label: "Code", icon: "</>" },
  { id: "gallery", label: "Gallery", icon: "▦" }
];

const MEDIA_LIBRARY_TTL_MS = 6 * 60 * 60 * 1000;

const createTemplateBlock = (type, content = "", extras = {}) => {
  const { style: extraStyle, html: extraHtml, rightContent: rc, rightHtml: rh, columnGap: cg, stackMobile: sm, ...rest } = extras;
  const richTypes = ["heading", "text", "footer", "social"];
  const base = {
    id: `blk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    content,
    ...rest
  };
  if (richTypes.includes(type)) {
    base.html = extraHtml !== undefined ? String(extraHtml) : "";
  }
  if (type === "columns") {
    base.html = extraHtml !== undefined ? String(extraHtml) : "";
    base.rightContent = rc !== undefined ? String(rc) : "Right column";
    base.rightHtml = rh !== undefined ? String(rh) : "";
    base.columnGap = cg !== undefined ? Number(cg) : 20;
    base.stackMobile = sm !== false;
  }
  base.style = mergeDefaultStyle({ type, style: extraStyle });
  return base;
};

const normalizeTemplateBlocks = (template = {}) => {
  if (Array.isArray(template.blocks) && template.blocks.length > 0) {
    return template.blocks.map((block) => {
      const row = {
        id: block.id || `blk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: block.type || "text",
        content: String(block.content || ""),
        html: block.html !== undefined && block.html !== null ? String(block.html) : "",
        href: block.href || "",
        style: mergeDefaultStyle(block)
      };
      if (row.type === "columns") {
        row.rightContent = String(block.rightContent ?? "Right column");
        row.rightHtml = String(block.rightHtml ?? "");
        row.columnGap = Number(block.columnGap) || 20;
        row.stackMobile = block.stackMobile !== false;
      }
      return row;
    });
  }
  const blocks = [];
  if (template.subject) blocks.push(createTemplateBlock("heading", template.subject));
  if (template.heroImageUrl) blocks.push(createTemplateBlock("image", template.heroImageUrl));
  if (template.body) blocks.push(createTemplateBlock("text", template.body));
  if (template.ctaText) blocks.push(createTemplateBlock("button", template.ctaText, { href: template.ctaUrl || "" }));
  if (blocks.length === 0) {
    blocks.push(createTemplateBlock("heading", "Template heading"));
    blocks.push(createTemplateBlock("text", "Write your content..."));
  }
  return blocks;
};

const inferMediaType = (mimeType = "", name = "") => {
  const mime = String(mimeType || "").toLowerCase();
  const lowerName = String(name || "").toLowerCase();
  if (mime.startsWith("image/") || lowerName.match(/\.(png|jpg|jpeg|gif|webp|svg)$/)) return "image";
  if (mime.startsWith("audio/") || lowerName.match(/\.(mp3|wav|ogg|m4a|aac)$/)) return "audio";
  if (mime.startsWith("video/") || lowerName.match(/\.(mp4|mov|webm|mkv)$/)) return "video";
  return "";
};

const createMediaAsset = ({ name, url, type, shared = false }) => {
  const now = Date.now();
  return {
    id: `asset-${now}-${Math.random().toString(36).slice(2, 8)}`,
    name: String(name || "Asset"),
    url: String(url || ""),
    type: String(type || ""),
    shared: Boolean(shared),
    createdAt: now,
    expiresAt: now + MEDIA_LIBRARY_TTL_MS
  };
};

const cleanupMediaAssets = (items = []) => {
  const now = Date.now();
  return (items || []).filter((item) => Number(item?.expiresAt || 0) > now && item?.url && item?.type);
};

const formatAssetTtl = (expiresAt) => {
  const diff = Math.max(0, Number(expiresAt || 0) - Date.now());
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m left`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return `${hrs}h ${rem}m left`;
};

const emailTemplateCatalog = [
  {
    id: "welcome",
    name: "Welcome Email",
    category: "Onboarding",
    subject: "Welcome to our service",
    body: "Hi {{firstName}},\n\nWelcome to our service. We are excited to have you here.\n\nIf you need help getting started, just reply to this email.\n\nBest regards,\n{{senderName}}"
  },
  {
    id: "followup",
    name: "Follow-Up",
    category: "Sales",
    subject: "Quick follow-up on our last conversation",
    body: "Hi {{firstName}},\n\nFollowing up on our last conversation.\n\nWould you be available this week for a quick call?\n\nThanks,\n{{senderName}}"
  },
  {
    id: "promo",
    name: "Promotion",
    category: "Marketing",
    subject: "Special offer just for you",
    body: "Hi {{firstName}},\n\nWe have a special offer available for a limited time.\n\nUse code {{promoCode}} at checkout.\n\nCheers,\n{{senderName}}"
  },
  {
    id: "newsletter",
    name: "Newsletter",
    category: "Updates",
    subject: "Monthly product updates",
    body: "Hi {{firstName}},\n\nHere are the latest updates from this month:\n- New features\n- Performance improvements\n- Bug fixes\n\nThanks for staying with us.\n\n{{senderName}}"
  },
  {
    id: "event",
    name: "Event Invite",
    category: "Events",
    subject: "You are invited: {{eventName}}",
    body: "Hi {{firstName}},\n\nYou are invited to {{eventName}} on {{eventDate}}.\n\nClick here to RSVP: {{rsvpLink}}\n\nSee you there,\n{{senderName}}"
  },
  {
    id: "reengage",
    name: "Re-Engagement",
    category: "Retention",
    subject: "We miss you at {{companyName}}",
    body: "Hi {{firstName}},\n\nIt has been a while since we last saw you.\n\nCome back and see what is new.\n\nBest,\n{{senderName}}"
  }
];

const socialPlatformMeta = [
  { id: "instagram", label: "Instagram", icon: "IG", color: "bg-pink-500" },
  { id: "facebook", label: "Facebook", icon: "f", color: "bg-blue-600" },
  { id: "x", label: "X", icon: "X", color: "bg-slate-900" },
  { id: "linkedin", label: "LinkedIn", icon: "in", color: "bg-sky-700" },
  { id: "youtube", label: "YouTube", icon: "YT", color: "bg-red-600" },
  { id: "tiktok", label: "TikTok", icon: "TT", color: "bg-slate-800" }
];

const integrationCatalog = {
  messaging: [
    { id: "linkedin", label: "LinkedIn", icon: "in", featuresCount: 45 },
    { id: "facebook", label: "Facebook", icon: "f", featuresCount: 21 },
    { id: "gmail", label: "Gmail", icon: "G", featuresCount: 11 },
    { id: "google", label: "Google", icon: "31", featuresCount: 11 },
    { id: "whatsapp", label: "WhatsApp", icon: "WA", featuresCount: 17 },
    { id: "outlook", label: "Outlook", icon: "O", featuresCount: 11 },
    { id: "microsoft", label: "Microsoft", icon: "M", featuresCount: 11 },
    { id: "instagram", label: "Instagram", icon: "IG", featuresCount: 16 },
    { id: "youtube", label: "YouTube", icon: "YT", featuresCount: 12 },
    { id: "tiktok", label: "TikTok", icon: "TT", featuresCount: 12 },
    { id: "imap", label: "IMAP", icon: "IM", featuresCount: 11 },
    { id: "messenger", label: "Messenger", icon: "MS", featuresCount: 15 },
    { id: "telegram", label: "Telegram", icon: "TG", featuresCount: 16 },
    { id: "x", label: "X", icon: "X", featuresCount: 17 }
  ],
  email: [
    { id: "gmail", label: "Gmail", icon: "G", featuresCount: 11 },
    { id: "outlook", label: "Outlook", icon: "O", featuresCount: 11 },
    { id: "imap", label: "IMAP", icon: "IM", featuresCount: 11 }
  ],
  calendar: [
    { id: "google", label: "Google", icon: "31", featuresCount: 9 },
    { id: "microsoft", label: "Microsoft", icon: "M", featuresCount: 9 }
  ]
};

const integrationFeatureMap = {
  linkedin: {
    title: "LinkedIn Features",
    sections: [
      {
        title: "Messages",
        items: ["Send & Reply Messages", "List Messages, Chats & Attendees", "Reaction List", "Track read receipts"]
      },
      {
        title: "Invitations",
        items: [
          "LinkedIn User Invitation",
          "LinkedIn Outreach Sequence",
          "LinkedIn Pending Invitations",
          "LinkedIn Delete Invitations",
          "Detect LinkedIn Accepted Invitations"
        ]
      },
      {
        title: "Profiles",
        items: ["Retrieve Users Profiles", "Retrieve own profile"]
      },
      {
        title: "Webhooks",
        items: ["Account status"]
      }
    ]
  }
};

const appFont = {
  fontFamily: `"Segoe UI", "Segoe UI Variable", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif`
};

const socialPlatformBadge = {
  instagram: {},
  facebook: {},
  x: {},
  linkedin: {},
  youtube: {},
  tiktok: {},
  whatsapp: {},
  messenger: {},
  telegram: {}
};

const allowedActivePages = new Set(["mail", "compose", "templates", "settings", "social", "blocked", "platform"]);

function SocialProviderIcon({ platform, className = "w-[15px] h-[15px]" }) {
  const cls = className;
  const p = String(platform || "")
    .toLowerCase()
    .replace("twitter", "x");
  if (p === "gmail") {
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
        <path
          fill="#EA4335"
          d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"
        />
      </svg>
    );
  }
  if (p === "google") {
    return (
      <svg viewBox="0 0 48 48" className={cls} aria-hidden="true">
        <path
          fill="#FFC107"
          d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
        />
        <path
          fill="#FF3D00"
          d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
        />
        <path
          fill="#4CAF50"
          d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.86 11.86 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
        />
        <path
          fill="#1976D2"
          d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C35.111 39.89 40 32.302 40 24c0-1.341-.138-2.65-.389-3.917z"
        />
      </svg>
    );
  }
  if (p === "outlook") {
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
        <path
          fill="#0078D4"
          d="M21.75 4.5H2.25A1.75 1.75 0 0 0 .5 6.25v11.5c0 .97.78 1.75 1.75 1.75h19.5c.97 0 1.75-.78 1.75-1.75V6.25c0-.97-.78-1.75-1.75-1.75Zm-.38 3.38-8.25 5.31a.87.87 0 0 1-.94 0L4.63 7.88a.67.67 0 1 1 .74-1.12l7.38 4.75 7.38-4.75a.67.67 0 1 1 .74 1.12Z"
        />
      </svg>
    );
  }
  if (p === "microsoft") {
    return (
      <svg viewBox="0 0 21 21" className={cls} aria-hidden="true">
        <path fill="#F25022" d="M0 0h10v10H0z" />
        <path fill="#7FBA00" d="M11 0h10v10H11z" />
        <path fill="#00A4EF" d="M0 11h10v10H0z" />
        <path fill="#FFB900" d="M11 11h10v10H11z" />
      </svg>
    );
  }
  if (p === "imap") {
    return (
      <svg viewBox="0 0 24 24" className={cls} aria-hidden="true" fill="none">
        <rect x="3" y="5" width="18" height="14" rx="2" stroke="#475569" strokeWidth="1.75" />
        <path stroke="#475569" strokeWidth="1.75" strokeLinecap="round" d="M3 8l9 5 9-5" />
      </svg>
    );
  }
  if (p === "linkedin") {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="#0A66C2">
        <path d="M4.98 3.5A2.49 2.49 0 1 0 5 8.48 2.49 2.49 0 0 0 4.98 3.5ZM3 9h4v12H3zM9 9h3.8v1.7h.05c.53-1 1.84-2.05 3.8-2.05C20 8.65 21 10.73 21 13.7V21h-4v-6.4c0-1.53-.03-3.5-2.13-3.5-2.13 0-2.46 1.66-2.46 3.38V21H9z" />
      </svg>
    );
  }
  if (p === "facebook") {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="#1877F2">
        <path d="M14 8h3V4h-3c-3 0-5 2-5 5v3H6v4h3v5h4v-5h3.2l.8-4H13V9c0-.7.3-1 1-1Z" />
      </svg>
    );
  }
  if (p === "instagram") {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="none" strokeWidth="2">
        <defs>
          <linearGradient id="igGrad" x1="4" y1="20" x2="20" y2="4">
            <stop offset="0" stopColor="#FEDA75" />
            <stop offset="0.35" stopColor="#FA7E1E" />
            <stop offset="0.65" stopColor="#D62976" />
            <stop offset="1" stopColor="#4F5BD5" />
          </linearGradient>
        </defs>
        <rect x="4" y="4" width="16" height="16" rx="5" stroke="url(#igGrad)" />
        <circle cx="12" cy="12" r="4" stroke="url(#igGrad)" />
        <circle cx="17" cy="7" r="1" fill="#D62976" stroke="none" />
      </svg>
    );
  }
  if (p === "x") {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="#111827">
        <path d="M18.9 3h2.9l-6.4 7.4L23 21h-6l-4.7-6.2L6.8 21H3.9l6.8-7.9L1 3h6.2l4.2 5.6L18.9 3Zm-1 16h1.7L6.2 4.9H4.4L17.9 19Z" />
      </svg>
    );
  }
  if (p === "youtube") {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="#FF0000">
        <path d="M21.6 7.2c-.2-1-1-1.8-2-2C17.8 4.8 12 4.8 12 4.8s-5.8 0-7.6.4c-1 .2-1.8 1-2 2C2 9 2 12 2 12s0 3 .4 4.8c.2 1 1 1.8 2 2 1.8.4 7.6.4 7.6.4s5.8 0 7.6-.4c1-.2 1.8-1 2-2 .4-1.8.4-4.8.4-4.8s0-3-.4-4.8ZM10 15.5v-7l6 3.5-6 3.5Z" />
      </svg>
    );
  }
  if (p === "tiktok") {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="#111827">
        <path d="M14 4h3.2c.3 1.3 1.3 2.3 2.8 2.5V10c-1.5 0-2.9-.4-4-1.2V14a5 5 0 1 1-5-5h.5V12h-.5a2 2 0 1 0 2 2V4Z" />
      </svg>
    );
  }
  if (p === "whatsapp") {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="#25D366">
        <path d="M12 3a9 9 0 0 0-7.8 13.5L3 21l4.7-1.2A9 9 0 1 0 12 3Zm5.1 12.6c-.2.6-1.1 1.1-1.8 1.2-.5.1-1.2.2-3.5-.7-2.9-1.2-4.8-4.2-5-4.5-.2-.3-1.2-1.6-1.2-3s.8-2.2 1.1-2.5c.3-.3.6-.4.8-.4h.6c.2 0 .4 0 .6.5.2.6.8 2 .9 2.2.1.2.1.4 0 .6-.1.2-.2.4-.4.6l-.5.5c-.2.2-.4.4-.2.7.2.3 1 1.7 2.2 2.7 1.5 1.3 2.7 1.6 3 1.7.3.1.5.1.7-.1.2-.2.8-.9 1-1.2.2-.3.4-.2.7-.1.3.1 2 .9 2.3 1 .3.2.5.2.6.4.1.1.1.8-.2 1.4Z" />
      </svg>
    );
  }
  if (p === "telegram") {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="#229ED9">
        <path d="m20.7 4.3-2.8 14c-.2 1-1 1.2-1.8.8l-4-3-1.9 1.8c-.2.2-.4.4-.8.4l.3-4.1 7.5-6.8c.3-.3-.1-.4-.5-.2L7.4 13 3.5 11.8c-.9-.3-.9-.9.2-1.3L19 4.6c.8-.3 1.5.2 1.2 1.7Z" />
      </svg>
    );
  }
  if (p === "messenger") {
    return (
      <svg viewBox="0 0 24 24" className={cls} fill="#0084FF">
        <path d="M12 3C6.9 3 3 6.8 3 11.5c0 2.7 1.3 5.2 3.5 6.8V21l2.6-1.4c.9.3 1.9.4 2.9.4 5.1 0 9-3.8 9-8.5S17.1 3 12 3Zm1 10.8-2.3-2.4-4.5 2.4L11.2 8l2.4 2.4L18 8l-5 5.8Z" />
      </svg>
    );
  }
  return <span className="text-[10px] font-bold text-slate-500">{String(p || "?").slice(0, 1).toUpperCase()}</span>;
}

const socialProviderIds = ["linkedin", "facebook", "instagram", "x", "youtube", "tiktok", "whatsapp", "telegram", "messenger"];
const oauthSupportedSocialProviders = ["linkedin", "facebook", "instagram", "x", "whatsapp", "messenger"];
const syncSupportedSocialProviders = [...socialProviderIds];
const emailOauthSupportedProviders = ["google", "microsoft"];

function isSocialProvider(providerId) {
  return socialProviderIds.includes(String(providerId || "").toLowerCase());
}

const socialCompanyCycle = ["NexaTech", "Synthex", "Orbix", "Vortex", "Quark", "Nova", "Zephyr", "Fluxo"];
const socialStatusCycle = [
  { label: "Responsive", dot: "bg-gold-500" },
  { label: "Interested", dot: "bg-success-600" },
  { label: "Proposal", dot: "bg-accent-600" },
  { label: "Responsive", dot: "bg-gold-500" },
  { label: "Closed Won", dot: "bg-violet-500" },
  { label: "Responsive", dot: "bg-gold-500" },
  { label: "Proposal", dot: "bg-accent-600" },
  { label: "Responsive", dot: "bg-success-600" }
];

function socialPlatformLabel(platformId) {
  const normalized = String(platformId || "").toLowerCase();
  if (normalized === "x") return "X";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function mapSettingsProviderToAccountProvider(providerId) {
  const normalized = String(providerId || "").toLowerCase();
  if (normalized === "gmail") return "google";
  if (normalized === "outlook") return "microsoft";
  return normalized;
}

function formatAiCategory(category) {
  if (!category) return "";
  const raw = String(category).replace(/[_-]+/g, " ").trim();
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function aiCategoryTone(category) {
  const normalized = String(category || "").toLowerCase();
  if (normalized.includes("priority") || normalized.includes("important")) return "bg-red-100 text-rose-800";
  if (normalized.includes("promotion") || normalized.includes("marketing")) return "bg-amber-100 text-amber-800";
  if (normalized.includes("social")) return "bg-indigo-100 text-indigo-800";
  if (normalized.includes("update") || normalized.includes("notification")) return "bg-sky-100 text-sky-900";
  return "bg-emerald-100 text-emerald-800";
}

function FolderIcon({ type }) {
  const base = "w-[22px] h-[22px] stroke-[1.9] text-slateLux-900";
  if (type === "inbox") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={base}>
        <path d="M4 9.5 6.4 6h11.2L20 9.5v8.5H4V9.5Z" stroke="currentColor" />
        <path d="M4 13h4l2 2h4l2-2h4" stroke="currentColor" />
      </svg>
    );
  }
  if (type === "pinned") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={base}>
        <path d="M8 4h8l-1 5 2 2H7l2-2-1-5Z" stroke="currentColor" />
        <path d="M12 11v9" stroke="currentColor" />
      </svg>
    );
  }
  if (type === "sent") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={base}>
        <path d="m3 11 17-7-5 17-3-7-9-3Z" stroke="currentColor" />
      </svg>
    );
  }
  if (type === "drafts") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={base}>
        <path d="M6 3h9l3 3v15H6V3Z" stroke="currentColor" />
        <path d="M9 12h6M9 16h6" stroke="currentColor" />
      </svg>
    );
  }
  if (type === "archive") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={base}>
        <path d="M4 7h16v13H4V7Z" stroke="currentColor" />
        <path d="M3 4h18v3H3V4Z" stroke="currentColor" />
      </svg>
    );
  }
  if (type === "junk") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={base}>
        <path d="M6 7h12l-1 14H7L6 7Z" stroke="currentColor" />
        <path d="M9 7V4h6v3" stroke="currentColor" />
      </svg>
    );
  }
  if (type === "blocked" || type === "manageBlocked") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={base}>
        <path d="M12 3 6 5v6c0 5 2.5 8.4 6 10 3.5-1.6 6-5 6-10V5l-6-2Z" stroke="currentColor" />
      </svg>
    );
  }
  if (type === "starred") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={base}>
        <path d="m12 4 2.7 5.5 6.1.9-4.4 4.3 1 6.1L12 18l-5.4 2.8 1-6.1-4.4-4.3 6.1-.9L12 4Z" stroke="currentColor" />
      </svg>
    );
  }
  if (type === "templates") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={base}>
        <path d="M4 8.5h16v11H4v-11Z" stroke="currentColor" />
        <path d="M4 8.5 7 4.5h10l3 4M8 12h8M8 15h5" stroke="currentColor" />
      </svg>
    );
  }
  if (type === "platform") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={base}>
        <path d="M12 2 4 5v6c0 5.5 3.8 10.7 8 12 4.2-1.3 8-6.5 8-12V5l-8-3Z" stroke="currentColor" />
        <path d="M9 12.5 11 15l4-5" stroke="currentColor" />
      </svg>
    );
  }
  return null;
}

function ToolbarIcon({ name }) {
  const cls = "w-[18px] h-[18px] stroke-[1.9] text-accent-700";
  if (name === "trash") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={cls}>
        <path d="M6 7h12l-1 13H7L6 7Z" stroke="currentColor" />
        <path d="M9 7V4h6v3M4 7h16" stroke="currentColor" />
      </svg>
    );
  }
  if (name === "archive") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={cls}>
        <path d="M4 8h16v12H4V8Z" stroke="currentColor" />
        <path d="M3 4h18v4H3V4Z" stroke="currentColor" />
      </svg>
    );
  }
  if (name === "warning") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={cls}>
        <path d="M12 3 2.5 20h19L12 3Z" stroke="currentColor" />
        <path d="M12 9v5M12 17h.01" stroke="currentColor" />
      </svg>
    );
  }
  if (name === "folder") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={cls}>
        <path d="M3 7h7l2 2h9v10H3V7Z" stroke="currentColor" />
      </svg>
    );
  }
  if (name === "tag") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={cls}>
        <path d="M3 12V5h7l10 10-7 7L3 12Z" stroke="currentColor" />
        <path d="M7 9h.01" stroke="currentColor" />
      </svg>
    );
  }
  if (name === "replyLeft") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={cls}>
        <path d="M10 7 4 12l6 5" stroke="currentColor" />
        <path d="M5 12h9a6 6 0 0 1 6 6" stroke="currentColor" />
      </svg>
    );
  }
  if (name === "replyRight") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={cls}>
        <path d="m14 7 6 5-6 5" stroke="currentColor" />
        <path d="M19 12h-9a6 6 0 0 0-6 6" stroke="currentColor" />
      </svg>
    );
  }
  if (name === "refresh") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={cls}>
        <path d="M20 11a8 8 0 1 0 2 5.3" stroke="currentColor" />
        <path d="M20 4v7h-7" stroke="currentColor" />
      </svg>
    );
  }
  if (name === "undo") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={cls}>
        <path d="M9 7 4 12l5 5" stroke="currentColor" />
        <path d="M5 12h9a6 6 0 1 1 0 12" stroke="currentColor" />
      </svg>
    );
  }
  if (name === "print") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={cls}>
        <path d="M7 8V4h10v4M6 17H4v-6h16v6h-2" stroke="currentColor" />
        <path d="M7 14h10v6H7v-6Z" stroke="currentColor" />
      </svg>
    );
  }
  if (name === "pin") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={cls}>
        <path d="M8 4h8l-1 5 2 2H7l2-2-1-5Z" stroke="currentColor" />
        <path d="M12 11v9" stroke="currentColor" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" className={cls}>
      <path d="M5 12h14" stroke="currentColor" />
      <path d="M5 7h14M5 17h14" stroke="currentColor" />
    </svg>
  );
}

function NewMailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="w-[18px] h-[18px] stroke-[2] text-white">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" />
      <path d="m4 7 8 6 8-6" stroke="currentColor" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" className="w-[14px] h-[14px]">
      <path d="m5 7 5 6 5-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function formatFileSize(bytes) {
  const size = Number(bytes || 0);
  if (size <= 0) return "0 KB";
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function getAttachmentGlyph(fileName = "", mimeType = "") {
  const lowerName = String(fileName).toLowerCase();
  const lowerMime = String(mimeType).toLowerCase();
  if (lowerName.endsWith(".pdf") || lowerMime.includes("pdf")) return "PDF";
  if (lowerName.match(/\.(doc|docx)$/) || lowerMime.includes("word")) return "DOC";
  if (lowerName.match(/\.(xls|xlsx|csv)$/) || lowerMime.includes("sheet") || lowerMime.includes("excel")) return "XLS";
  if (lowerName.match(/\.(ppt|pptx)$/) || lowerMime.includes("presentation")) return "PPT";
  if (lowerName.match(/\.(png|jpg|jpeg|gif|webp)$/) || lowerMime.startsWith("image/")) return "IMG";
  if (lowerName.match(/\.(mp4|mov|mkv|webm)$/) || lowerMime.startsWith("video/")) return "VID";
  if (lowerName.match(/\.(zip|rar|7z)$/) || lowerMime.includes("zip")) return "ZIP";
  return "FILE";
}

const Inbox = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  /** Mobile: briefing opens in a sheet — inbox column stays messages-only. */
  const [briefingSheetOpen, setBriefingSheetOpen] = useState(false);
  const [quickLookOpen, setQuickLookOpen] = useState(false);
  const [quickLookAttachment, setQuickLookAttachment] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 900px)");
    const update = () => setIsMobile(Boolean(mq.matches));
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const haptic = useCallback((ms = 10) => {
    try {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate(ms);
      }
    } catch (_e) {}
  }, []);

  const openQuickLook = useCallback((attachment) => {
    if (!attachment) return;
    setQuickLookAttachment(attachment);
    setQuickLookOpen(true);
    haptic(8);
  }, [haptic]);
  const readStoredUser = () => {
    try {
      const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && parsed.email) return parsed;
      return null;
    } catch (_error) {
      return null;
    }
  };
  const storedUser = typeof window !== "undefined" ? readStoredUser() : null;
  const initialResetToken =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("token") || ""
      : "";
  const readStoredActivePage = () => {
    try {
      const raw = window.localStorage.getItem(ACTIVE_PAGE_STORAGE_KEY);
      return allowedActivePages.has(raw) ? raw : "mail";
    } catch (_error) {
      return "mail";
    }
  };
  const readStoredInboxViewMode = () => {
    try {
      const raw = window.localStorage.getItem(INBOX_VIEW_MODE_STORAGE_KEY);
      return raw === "chronological" ? "chronological" : "grouped";
    } catch (_error) {
      return "grouped";
    }
  };
  const readStoredDensityMode = () => {
    try {
      const raw = window.localStorage.getItem(INBOX_DENSITY_STORAGE_KEY);
      return raw === "compact" ? "compact" : "comfortable";
    } catch (_error) {
      return "comfortable";
    }
  };
  const [activePage, setActivePage] = useState(typeof window !== "undefined" ? readStoredActivePage() : "mail");
  const [auth, setAuth] = useState({
    email: "",
    phoneNumber: "",
    password: "",
    displayName: "",
    birthMonth: "",
    birthDay: "",
    birthYear: ""
  });
  const [authPhoneCode, setAuthPhoneCode] = useState("+1");
  const [showAuthPassword, setShowAuthPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [authFlow, setAuthFlow] = useState(
    typeof window !== "undefined" && window.location.pathname === "/reset-password" ? "forgot" : "intro"
  );
  const [loggedIn, setLoggedIn] = useState(Boolean(storedUser));
  /** True until first `/api/auth/me` completes — prevents mail/API calls with stale localStorage + no cookie. */
  const [authBooting, setAuthBooting] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [activeAccountId, setActiveAccountId] = useState("");
  const [folders, setFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState("INBOX");
  const [unifiedMode, setUnifiedMode] = useState(true);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [timelineCollapsed, setTimelineCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ unread: false, starred: false, archived: false });
  const [inboxViewMode, setInboxViewMode] = useState(
    typeof window !== "undefined" ? readStoredInboxViewMode() : "grouped"
  );
  const [inboxDensityMode, setInboxDensityMode] = useState(
    typeof window !== "undefined" ? readStoredDensityMode() : "comfortable"
  );
  const [showShortcutLegend, setShowShortcutLegend] = useState(false);
  const [compose, setCompose] = useState(defaultCompose);
  const [draftId, setDraftId] = useState("");
  const [outboundStatus, setOutboundStatus] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [connectForm, setConnectForm] = useState({
    emailAddress: "",
    password: "",
    authType: "app_password",
    manualMode: false,
    imap: { host: "", port: 993, secure: true, username: "", password: "" },
    smtp: { host: "", port: 587, secure: true, username: "", password: "" }
  });
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [activeSocialAccountId, setActiveSocialAccountId] = useState("");
  const [socialMessages, setSocialMessages] = useState([]);
  const [socialConnectForm, setSocialConnectForm] = useState({
    platform: "instagram",
    handle: "",
    displayName: "",
    authType: "token",
    accessToken: ""
  });
  const [socialOauth, setSocialOauth] = useState({ providers: [], state: "", code: "", platform: "instagram" });
  const [emailOauth, setEmailOauth] = useState({ providers: [], state: "", code: "", provider: "google", emailAddressHint: "" });
  const [socialReply, setSocialReply] = useState({ socialMessageId: "", text: "", replyId: "", status: "" });
  const [socialReplySuggestions, setSocialReplySuggestions] = useState([]);
  const [socialReplySuggestionsSource, setSocialReplySuggestionsSource] = useState("");
  const [socialReplySuggestionMode, setSocialReplySuggestionMode] = useState("replace");
  const [socialReplySuggestionStatus, setSocialReplySuggestionStatus] = useState("");
  const [socialAiPolicy, setSocialAiPolicy] = useState({ requiresHumanApproval: true, autoSendEnabled: false });
  const [socialViewTab, setSocialViewTab] = useState("contacts");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotResetToken, setForgotResetToken] = useState(initialResetToken);
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [blockedSenders, setBlockedSenders] = useState([]);
  const [blockedSenderValue, setBlockedSenderValue] = useState("");
  const [blockedSenderLabel, setBlockedSenderLabel] = useState("");
  const [blockedSenderChannel, setBlockedSenderChannel] = useState("all");
  const [selectedSocialContactId, setSelectedSocialContactId] = useState("");
  const [showCcField, setShowCcField] = useState(false);
  const [showBccField, setShowBccField] = useState(false);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showComposeEmojiPicker, setShowComposeEmojiPicker] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [templateCatalog, setTemplateCatalog] = useState(emailTemplateCatalog);
  const [predefinedTemplateCatalog, setPredefinedTemplateCatalog] = useState([]);
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState("Emails");
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  const [templateDraft, setTemplateDraft] = useState(buildTemplateDraft());
  const [templateEditorTab, setTemplateEditorTab] = useState("edit");
  const [templatePreviewDevice, setTemplatePreviewDevice] = useState("desktop");
  const [templateSavedAt, setTemplateSavedAt] = useState("");
  const [selectedTemplateBlockId, setSelectedTemplateBlockId] = useState("");
  const [draggingTemplateBlockId, setDraggingTemplateBlockId] = useState("");
  const [lockedTemplateBlockIds, setLockedTemplateBlockIds] = useState([]);
  const [personalMediaLibrary, setPersonalMediaLibrary] = useState([]);
  const [sharedMediaLibrary, setSharedMediaLibrary] = useState([]);
  const [showAssetsManager, setShowAssetsManager] = useState(false);
  const [assetsManagerSearch, setAssetsManagerSearch] = useState("");
  const [assetsManagerTypeFilter, setAssetsManagerTypeFilter] = useState("all");
  const [templateHistory, setTemplateHistory] = useState([]);
  const [templateHistoryIndex, setTemplateHistoryIndex] = useState(-1);
  const [currentUser, setCurrentUser] = useState(storedUser);
  const [chatOpen, setChatOpen] = useState(false);
  /** On narrow viewports: full-width conversation list vs full-width thread (master/detail). */
  const [chatMobileShowList, setChatMobileShowList] = useState(true);
  const [chatIdentifier, setChatIdentifier] = useState("");
  const [chatContacts, setChatContacts] = useState([]);
  const [chatContactPickerOpen, setChatContactPickerOpen] = useState(false);
  const [chatConversations, setChatConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [chatDraft, setChatDraft] = useState("");
  const [chatUnreadByConversation, setChatUnreadByConversation] = useState({});
  const [chatSoundEnabled, setChatSoundEnabled] = useState(true);
  const [desktopNotifEnabled, setDesktopNotifEnabled] = useState(false);
  const [composeFontFamily, setComposeFontFamily] = useState("Arial");
  const [composeFontSize, setComposeFontSize] = useState("4");
  const [composeTextColor, setComposeTextColor] = useState("#111827");
  const [composeHighlightColor, setComposeHighlightColor] = useState("#fff59d");
  const [replySuggestions, setReplySuggestions] = useState([]);
  const [replySuggestionsSource, setReplySuggestionsSource] = useState("");
  const [replySuggestionInsertMode, setReplySuggestionInsertMode] = useState("replace");
  const [replySuggestionStatus, setReplySuggestionStatus] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [loadingBriefing, setLoadingBriefing] = useState(false);
  const [mailView, setMailView] = useState("inbox"); // inbox | future
  const [futureItems, setFutureItems] = useState([]);
  const [loadingFuture, setLoadingFuture] = useState(false);
  const [aiDraftOpen, setAiDraftOpen] = useState(false);
  const [aiDraftIntent, setAiDraftIntent] = useState("Write a concise, professional email.");
  const [aiDraftTone, setAiDraftTone] = useState("professional-warm");
  const [aiDraftResult, setAiDraftResult] = useState("");
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const [commandBarOpen, setCommandBarOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState("");
  const [commandMailResults, setCommandMailResults] = useState([]);
  const [commandMailLoading, setCommandMailLoading] = useState(false);
  const commandMailDebounceRef = useRef(null);
  const commandMailAbortRef = useRef(null);
  const selectMessageRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const bootstrap = async () => {
      try {
        attempts += 1;
        const res = await api.get("/api/auth/me");
        const nextUser = res?.data?.user ?? null;
        if (!cancelled) {
          if (nextUser) {
            setCurrentUser(nextUser);
            setLoggedIn(true);
            try {
              window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextUser));
            } catch (_e) {}
          } else {
            setCurrentUser(null);
            setLoggedIn(false);
            try {
              window.localStorage.removeItem(AUTH_STORAGE_KEY);
            } catch (_e) {}
          }
          setAuthBooting(false);
        }
      } catch (_e) {
        if (!cancelled && attempts < 2) {
          setTimeout(() => {
            if (!cancelled) bootstrap();
          }, 450);
          return;
        }
        if (!cancelled) {
          setCurrentUser(null);
          setLoggedIn(false);
          try {
            window.localStorage.removeItem(AUTH_STORAGE_KEY);
          } catch (_err) {}
          setAuthBooting(false);
        }
      }
    };
    bootstrap();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      window.__netluqCommandBarOpen = Boolean(commandBarOpen);
    } catch (_e) {}
  }, [commandBarOpen]);

  useEffect(() => {
    try {
      window.__netluqLoggedIn = Boolean(loggedIn);
    } catch (_e) {}
  }, [loggedIn]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.__NETLUQ_E2E_ENABLE) return;
    try {
      window.__netluqE2ELogin = (user) => {
        try {
          if (user) window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
          window.localStorage.setItem(ACTIVE_PAGE_STORAGE_KEY, "mail");
        } catch (_e) {}
        setCurrentUser(user || null);
        setLoggedIn(Boolean(user));
      };
    } catch (_e) {}
  }, []);
  const [busyAction, setBusyAction] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [settingsCategory, setSettingsCategory] = useState("messaging");
  const [settingsProvider, setSettingsProvider] = useState("linkedin");
  const [billingPlans, setBillingPlans] = useState([]);
  const [billingMe, setBillingMe] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [billingCheckoutBusy, setBillingCheckoutBusy] = useState("");
  const [upgradeNudge, setUpgradeNudge] = useState("");
  const [healthSummary, setHealthSummary] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState("");
  const [securityPulseDomain, setSecurityPulseDomain] = useState("");
  const [securityPulseResult, setSecurityPulseResult] = useState(null);
  const [securityPulseLoading, setSecurityPulseLoading] = useState(false);
  const newMenuRef = useRef(null);
  const settingsMenuRef = useRef(null);
  const composeEmojiPickerRef = useRef(null);
  const chatWidgetRef = useRef(null);
  const inboxListScrollRef = useRef(null);
  const [inboxListViewportHeight, setInboxListViewportHeight] = useState(520);
  const chatToggleRef = useRef(null);

  useLayoutEffect(() => {
    const el = inboxListScrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const h = Math.floor(el.getBoundingClientRect().height);
      setInboxListViewportHeight((prev) => {
        const next = Math.max(200, h);
        return next === prev ? prev : next;
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [activePage, isMobile, mailView, loadingBriefing, briefing]);
  const socialSuggestionPanelRef = useRef(null);
  const socialReplyInputRef = useRef(null);
  const composeTextRef = useRef(null);
  const syncingComposeEditorRef = useRef(false);
  const composeSavedRangeRef = useRef(null);
  const composeFileInputRef = useRef(null);
  const templateHistoryRestoreRef = useRef(false);

  const pushNotification = useCallback((message) => {
    setNotifications((prev) => [{ id: Date.now(), message }, ...prev].slice(0, 6));
  }, []);

  const friendlyErrorMessage = useCallback((apiError, fallback = "Something went wrong. Please try again.") => {
    const responseData = apiError?.response?.data;
    const rawError = responseData?.error;
    const status = Number(apiError?.response?.status || 0);

    const pickLaravelMessage = (data) => {
      if (!data || typeof data !== "object") return "";
      const errs = data.errors;
      if (errs && typeof errs === "object") {
        for (const key of Object.keys(errs)) {
          const v = errs[key];
          if (Array.isArray(v) && v.length > 0 && String(v[0] || "").trim()) {
            return String(v[0]).trim();
          }
          if (typeof v === "string" && v.trim()) return v.trim();
        }
      }
      if (typeof data.message === "string" && data.message.trim()) {
        const m = data.message.trim();
        if (m !== "The given data was invalid.") return m;
      }
      return "";
    };
    const laravelMsg = pickLaravelMessage(responseData);
    if (laravelMsg) return laravelMsg;

    const normalizeValidationIssues = (issues) => {
      if (!Array.isArray(issues) || issues.length === 0) return "";
      const firstIssue = issues[0];
      const field = Array.isArray(firstIssue?.path) && firstIssue.path.length > 0 ? String(firstIssue.path[0]) : "";
      const message = String(firstIssue?.message || "").trim();
      if (!message) return "";
      if (field === "emailAddress" || field === "email") return "Please enter a valid email address.";
      if (field === "password" && message.toLowerCase().includes("at least 1")) return "Please enter your password.";
      if (field) {
        const prettyField = field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, " $1");
        return `${prettyField}: ${message}`;
      }
      return message;
    };

    if (Array.isArray(rawError)) {
      const parsed = normalizeValidationIssues(rawError);
      if (parsed) return parsed;
    }

    if (typeof rawError === "string") {
      const trimmed = rawError.trim();
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed);
          const normalized = normalizeValidationIssues(parsed);
          if (normalized) return normalized;
        } catch (_parseError) {
          // fall through to generic mapping below
        }
      }
      if (trimmed) return trimmed;
    }

    if (status === 401) return "Your session is unauthorized or expired. Please log in again.";
    if (status === 422) return "Please check your email and password and try again.";
    if (status === 409) return "This account already exists. Please log in instead.";
    if (status === 400) return "Please check the form values and try again.";
    if (status >= 500) return "Server error. Please try again in a moment.";

    return fallback;
  }, []);

  const playChatPing = useCallback(() => {
    if (!chatSoundEnabled) return;
    try {
      const AudioContextImpl = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextImpl) return;
      const audioContext = new AudioContextImpl();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(920, audioContext.currentTime);
      gainNode.gain.setValueAtTime(0.0001, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.06, audioContext.currentTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.18);
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (_error) {
      // Skip sound if blocked by browser/user settings.
    }
  }, [chatSoundEnabled]);

  const login = async () => {
    setError("");
    const emailTrim = String(auth.email || "").trim().toLowerCase();
    const passwordTrim = String(auth.password || "").trim();
    try {
      setLoading(true);
      await api.post("/api/auth/register", {
        email: emailTrim,
        password: passwordTrim,
        displayName: String(auth.displayName || "User").trim() || "User"
      });
    } catch (_error) {
      // Register may fail if user already exists; login below still handles it.
    }
    try {
      const response = await api.post("/api/auth/login", {
        email: emailTrim,
        password: passwordTrim
      });
      const nextUser = response.data.user || null;
      setCurrentUser(nextUser);
      try {
        if (nextUser) window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextUser));
      } catch (_error) {
        // ignore storage failures
      }
      setLoggedIn(true);
    } catch (loginError) {
      setError(friendlyErrorMessage(loginError, "Failed to log in."));
    } finally {
      setLoading(false);
    }
  };

  const loginOnly = async () => {
    setError("");
    if (!String(auth.email || "").trim()) {
      setError("Email is required.");
      return;
    }
    if (!String(auth.password || "").trim()) {
      setError("Password is required.");
      return;
    }
    try {
      setLoading(true);
      const response = await api.post("/api/auth/login", {
        email: String(auth.email || "").trim().toLowerCase(),
        password: String(auth.password || "").trim()
      });
      const nextUser = response.data.user || null;
      setCurrentUser(nextUser);
      try {
        if (nextUser) window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextUser));
      } catch (_error) {
        // ignore storage failures
      }
      setLoggedIn(true);
    } catch (loginError) {
      setError(friendlyErrorMessage(loginError, "Failed to log in."));
    } finally {
      setLoading(false);
    }
  };

  const registerAndLogin = async () => {
    const normalizedPhone = `${String(authPhoneCode || "").trim()}${String(auth.phoneNumber || "").trim()}`;
    if (!String(auth.email || "").trim() || !String(normalizedPhone || "").trim()) {
      setError("Email and phone number are required.");
      return;
    }
    if (!String(auth.password || "").trim()) {
      setError("Password is required.");
      return;
    }
    if (String(auth.password || "").trim().length < 10) {
      setError("Password must be at least 10 characters.");
      return;
    }
    await login();
  };

  const requestForgotPassword = async () => {
    if (!String(forgotPasswordEmail || "").trim()) {
      setError("Email is required.");
      return;
    }
    setError("");
    const response = await api.post("/api/auth/forgot-password", {
      email: String(forgotPasswordEmail || "").trim()
    });
    if (response.data?.resetToken) {
      setForgotResetToken(String(response.data.resetToken || ""));
      pushNotification("Reset token generated for local dev. Use it below.");
    } else {
      pushNotification("If this email exists, a reset link has been sent.");
    }
  };

  const submitPasswordReset = async () => {
    if (!String(forgotResetToken || "").trim() || !String(forgotNewPassword || "").trim()) {
      setError("Reset token and new password are required.");
      return;
    }
    setError("");
    await api.post("/api/auth/reset-password", {
      token: String(forgotResetToken || "").trim(),
      newPassword: String(forgotNewPassword || "")
    });
    pushNotification("Password reset complete. Please log in with your new password.");
    setForgotResetToken("");
    setForgotNewPassword("");
    setAuthFlow("login");
  };

  const loadBlockedSenders = useCallback(async () => {
    if (authBooting || !loggedIn) return;
    const response = await api.get("/api/compliance/blocked-senders");
    setBlockedSenders(Array.isArray(response.data?.blockedSenders) ? response.data.blockedSenders : []);
  }, [authBooting, loggedIn]);

  const [platformVerifiedDomains, setPlatformVerifiedDomains] = useState([]);
  const [platformBlockedDomains, setPlatformBlockedDomains] = useState([]);
  const [platformAdminLoading, setPlatformAdminLoading] = useState(false);
  const [platformNewVerified, setPlatformNewVerified] = useState("");
  const [platformNewBlocked, setPlatformNewBlocked] = useState("");
  const [platformSettings, setPlatformSettings] = useState(null);
  const [platformIntegrations, setPlatformIntegrations] = useState(null);
  const [platformPolicyDraft, setPlatformPolicyDraft] = useState("allowlist");
  const [platformMigrationWarning, setPlatformMigrationWarning] = useState("");
  const [platformInspectInput, setPlatformInspectInput] = useState("");
  const [platformInspectResult, setPlatformInspectResult] = useState(null);

  const loadPlatformAdmin = useCallback(async () => {
    if (authBooting || !loggedIn) return;
    if (String(currentUser?.role || "").toLowerCase() !== "super_admin") return;
    setPlatformAdminLoading(true);
    try {
      const [vRes, bRes, sRes] = await Promise.all([
        api.get("/api/admin/verified-domains"),
        api.get("/api/admin/blocked-domains"),
        api.get("/api/admin/settings")
      ]);
      setPlatformVerifiedDomains(Array.isArray(vRes.data?.domains) ? vRes.data.domains : []);
      setPlatformBlockedDomains(Array.isArray(bRes.data?.domains) ? bRes.data.domains : []);
      setPlatformMigrationWarning(
        bRes.data?.migrationNeeded
          ? String(bRes.data?.message || "Database migration required: run php artisan migrate --force on the server.")
          : ""
      );
      const st = sRes.data?.settings || null;
      setPlatformSettings(st);
      setPlatformIntegrations(sRes.data?.integrations || null);
      if (st?.emailDomainPolicy) {
        setPlatformPolicyDraft(String(st.emailDomainPolicy));
      }
    } finally {
      setPlatformAdminLoading(false);
    }
  }, [authBooting, loggedIn, currentUser?.role]);

  const createBlockedSender = async () => {
    if (!String(blockedSenderValue || "").trim()) {
      setError("Enter an email, handle, or sender name to block.");
      return;
    }
    setError("");
    const response = await api.post("/api/compliance/blocked-senders", {
      value: String(blockedSenderValue || "").trim(),
      label: String(blockedSenderLabel || "").trim() || undefined,
      channel: blockedSenderChannel
    });
    setBlockedSenders(Array.isArray(response.data?.blockedSenders) ? response.data.blockedSenders : []);
    setBlockedSenderValue("");
    setBlockedSenderLabel("");
  };

  const deleteBlockedSender = async (blockedSenderId) => {
    const response = await api.delete(`/api/compliance/blocked-senders/${blockedSenderId}`);
    setBlockedSenders(Array.isArray(response.data?.blockedSenders) ? response.data.blockedSenders : []);
  };

  const runAction = async (name, fn) => {
    try {
      setBusyAction(name);
      setError("");
      await fn();
    } catch (actionError) {
      setError(friendlyErrorMessage(actionError, "Action failed."));
    } finally {
      setBusyAction("");
    }
  };

  const normalizeComposePayload = (draftLike, { requireTo = false } = {}) => {
    const toList = (v) => {
      if (Array.isArray(v)) return v.map((item) => String(item).trim()).filter(Boolean);
      if (typeof v === "string") return toAddressArray(v);
      return [];
    };
    const to = toList(draftLike?.to);
    const cc = toList(draftLike?.cc);
    const bcc = toList(draftLike?.bcc);
    if (requireTo && to.length === 0) {
      throw new Error("Please add at least one recipient in the To field before sending.");
    }
    return {
      subject: draftLike?.subject || "",
      text: draftLike?.text || "",
      html: draftLike?.html || "",
      inReplyTo: draftLike?.inReplyTo || undefined,
      references: Array.isArray(draftLike?.references) && draftLike.references.length > 0 ? draftLike.references : undefined,
      attachments: Array.isArray(draftLike?.attachments) && draftLike.attachments.length > 0 ? draftLike.attachments : [],
      ...(to.length > 0 ? { to } : {}),
      ...(cc.length > 0 ? { cc } : {}),
      ...(bcc.length > 0 ? { bcc } : {})
    };
  };

  const toAddressArray = (value) =>
    String(value || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

  const escapeHtml = (value) =>
    String(value || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");

  const htmlToPlainText = useCallback((html) => {
    if (typeof document === "undefined") return String(html || "");
    const tmp = document.createElement("div");
    tmp.innerHTML = String(html || "");
    return (tmp.innerText || tmp.textContent || "").replace(/\u00a0/g, " ");
  }, []);

  const syncComposeFromEditor = useCallback(() => {
    const el = composeTextRef.current;
    if (!el) return null;
    const html = el.innerHTML;
    const text = htmlToPlainText(html);
    syncingComposeEditorRef.current = true;
    setCompose((prev) => ({ ...prev, html, text }));
    return { html, text };
  }, [htmlToPlainText]);

  const saveComposeSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    composeSavedRangeRef.current = selection.getRangeAt(0).cloneRange();
  };

  const restoreComposeSelection = () => {
    const selection = window.getSelection();
    const saved = composeSavedRangeRef.current;
    if (!selection || !saved) return;
    selection.removeAllRanges();
    selection.addRange(saved);
  };

  const placeComposeCaretAtEnd = () => {
    const el = composeTextRef.current;
    if (!el) return;
    el.focus();
    const selection = window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    saveComposeSelection();
  };

  const runComposeCommand = (command, value = null) => {
    const el = composeTextRef.current;
    if (!el) return;
    restoreComposeSelection();
    el.focus();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand(command, false, value);
    syncComposeFromEditor();
    saveComposeSelection();
  };

  const insertComposeTextAtCaret = (value) => {
    const el = composeTextRef.current;
    if (!el) return;
    el.focus();
    const inserted = document.execCommand("insertText", false, value);
    if (!inserted) {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        el.innerHTML += escapeHtml(value);
      } else {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const textNode = document.createTextNode(value);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    syncComposeFromEditor();
  };

  const insertComposeLink = () => {
    const url = window.prompt("Enter URL", "https://");
    if (!url) return;
    runComposeCommand("createLink", url);
  };

  const replaceComposeBodyText = (nextText) => {
    const safeText = String(nextText || "");
    const html = escapeHtml(safeText).replace(/\n/g, "<br>");
    setCompose((prev) => ({ ...prev, text: safeText, html }));
    syncingComposeEditorRef.current = true;
    setTimeout(() => placeComposeCaretAtEnd(), 0);
  };

  const loadReplySuggestions = async () => {
    if (!selectedMessage || selectedMessage.source_type === "social") return;
    if (!selectedAccountForMessage) return;
    const response = await api.post(
      `/api/mail/${selectedAccountForMessage}/messages/${selectedMessage.provider_message_id}/reply-suggestions`,
      {
        subject: selectedMessage.subject || "",
        snippet: selectedMessage.snippet || "",
        fromName: selectedMessage.from_name || "",
        fromEmail: selectedMessage.from_email || "",
        tone: "professional"
      }
    );
    setReplySuggestions(Array.isArray(response.data?.suggestions) ? response.data.suggestions : []);
    setReplySuggestionsSource(String(response.data?.source || ""));
    setReplySuggestionStatus("");
  };

  const runComposeGrammarCheck = async () => {
    if (!activeAccountId || !String(compose.text || "").trim()) return;
    const response = await api.post(`/api/mail/${activeAccountId}/compose/grammar-check`, {
      text: compose.text || ""
    });
    const corrected = String(response.data?.correctedText || compose.text || "");
    if (corrected && corrected !== compose.text) {
      replaceComposeBodyText(corrected);
      pushNotification("Grammar and spelling suggestions applied.");
    } else {
      pushNotification("No grammar updates needed.");
    }
  };

  const applyReplySuggestion = (suggestion) => {
    const text = String(suggestion || "");
    if (!text) return;
    if (replySuggestionInsertMode === "append") {
      const hasExisting = String(compose.text || "").trim().length > 0;
      insertComposeTextAtCaret(`${hasExisting ? "\n\n" : ""}${text}`);
      setReplySuggestionStatus("Appended");
      return;
    }
    replaceComposeBodyText(text);
    setReplySuggestionStatus("Replaced");
  };

  const copyReplySuggestion = async (suggestion) => {
    const text = String(suggestion || "");
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setReplySuggestionStatus("Copied");
        return;
      }
    } catch (_error) {
      // fallback below
    }
    const helper = document.createElement("textarea");
    helper.value = text;
    helper.style.position = "fixed";
    helper.style.opacity = "0";
    document.body.appendChild(helper);
    helper.focus();
    helper.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(helper);
    if (ok) setReplySuggestionStatus("Copied");
  };

  useEffect(() => {
    const el = composeTextRef.current;
    if (!el) return;
    if (syncingComposeEditorRef.current) {
      syncingComposeEditorRef.current = false;
      return;
    }
    const targetHtml = compose.html || escapeHtml(compose.text || "").replace(/\n/g, "<br>");
    if (el.innerHTML !== targetHtml) {
      el.innerHTML = targetHtml;
    }
  }, [compose.html, compose.text, activePage]);

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        const base64 = result.includes(",") ? result.split(",")[1] : "";
        resolve({
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          size: file.size,
          contentBase64: base64
        });
      };
      reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
      reader.readAsDataURL(file);
    });

  const addComposeAttachments = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const mapped = await Promise.all(files.map((file) => readFileAsBase64(file)));
    setCompose((prev) => ({
      ...prev,
      attachments: [...(prev.attachments || []), ...mapped]
    }));
  };

  const loadAccounts = useCallback(async () => {
    if (authBooting || !loggedIn) return;
    const cached = cacheGet(`accounts:${currentUser?.id || "me"}`);
    if (Array.isArray(cached) && cached.length > 0) {
      setAccounts(cached);
      if (!activeAccountId && cached.length > 0) {
        setActiveAccountId(cached[0].id);
      }
    }
    setLoadingAccounts(true);
    try {
      const response = await api.get("/api/accounts");
      const nextAccounts = response.data.accounts || [];
      setAccounts(nextAccounts);
      cacheSet(`accounts:${currentUser?.id || "me"}`, nextAccounts, 2 * 60 * 1000);
      if (!activeAccountId && nextAccounts.length > 0) {
        setActiveAccountId(nextAccounts[0].id);
      }
    } catch (e) {
      const status = Number(e?.response?.status || 0);
      if (status === 401) {
        setLoggedIn(false);
        setCurrentUser(null);
        setAuthFlow("intro");
        try {
          window.localStorage.removeItem(AUTH_STORAGE_KEY);
        } catch (_error) {}
        return;
      }
      if (status >= 400) {
        try {
          setError("We couldn’t load your accounts yet. Pull to refresh or try again.");
        } catch (_err) {}
      }
    } finally {
      setLoadingAccounts(false);
    }
  }, [authBooting, loggedIn, activeAccountId, currentUser?.id]);

  const loadFolders = useCallback(async () => {
    if (authBooting || !loggedIn || !activeAccountId) return;
    const cached = cacheGet(`folders:${currentUser?.id || "me"}:${activeAccountId}`);
    if (Array.isArray(cached) && cached.length > 0) {
      setFolders(cached);
    }
    setLoadingFolders(true);
    try {
      const response = await api.get(`/api/mail/${activeAccountId}/folders`);
      setFolders(response.data.folders || []);
      cacheSet(`folders:${currentUser?.id || "me"}:${activeAccountId}`, response.data.folders || [], 3 * 60 * 1000);
      if (response.data.folders?.length > 0 && !activeFolderId) {
        setActiveFolderId(response.data.folders[0].id);
      }
    } finally {
      setLoadingFolders(false);
    }
  }, [authBooting, loggedIn, activeAccountId, activeFolderId, currentUser?.id]);

  const loadSocialAccounts = useCallback(async () => {
    if (authBooting || !loggedIn) return;
    const response = await api.get("/api/social/accounts");
    const items = response.data.accounts || [];
    setSocialAccounts(items);
    if (!activeSocialAccountId && items.length > 0) {
      setActiveSocialAccountId(items[0].id);
    }
  }, [authBooting, loggedIn, activeSocialAccountId]);

  const loadSocialMessages = useCallback(async () => {
    if (authBooting || !loggedIn || !activeSocialAccountId) return;
    const response = await api.get(`/api/social/accounts/${activeSocialAccountId}/messages`, {
      params: { limit: 100 }
    });
    setSocialMessages(response.data.messages || []);
  }, [authBooting, loggedIn, activeSocialAccountId]);

  const loadChatConversations = useCallback(async () => {
    if (authBooting || !loggedIn) return;
    const response = await api.get("/api/chat/conversations");
    const conversations = response.data.conversations || [];
    setChatConversations(conversations);
    if (!activeConversationId && conversations.length > 0) {
      setActiveConversationId(conversations[0].id);
    }
  }, [authBooting, loggedIn, activeConversationId]);

  const loadChatContacts = useCallback(async () => {
    if (authBooting || !loggedIn) return;
    const response = await api.get("/api/chat/contacts");
    setChatContacts(response.data.contacts || []);
  }, [authBooting, loggedIn]);

  const loadConversationMessages = useCallback(async () => {
    if (authBooting || !loggedIn || !activeConversationId) {
      setChatMessages([]);
      return;
    }
    const response = await api.get(`/api/chat/conversations/${activeConversationId}/messages`);
    setChatMessages(response.data.messages || []);
  }, [activeConversationId, authBooting, loggedIn]);

  const startChatWithIdentifier = async (rawIdentifier) => {
    const identifier = String(rawIdentifier || "").trim();
    if (!identifier) return;
    try {
      const response = await api.post("/api/chat/conversations/start", { identifier });
      const conversationId = response.data.conversationId;
      setActiveConversationId(conversationId);
      setChatIdentifier("");
      setChatContactPickerOpen(false);
      await loadChatConversations();
      const messageResponse = await api.get(`/api/chat/conversations/${conversationId}/messages`);
      setChatMessages(messageResponse.data.messages || []);
      setChatMobileShowList(false);
    } catch (chatError) {
      setError(friendlyErrorMessage(chatError, "Could not start chat."));
    }
  };

  const startChatByIdentifier = async () => {
    await startChatWithIdentifier(chatIdentifier);
  };

  const sendChatMessage = async () => {
    if (!activeConversationId || !String(chatDraft || "").trim()) return;
    try {
      await api.post(`/api/chat/conversations/${activeConversationId}/messages`, {
        body: chatDraft
      });
      setChatDraft("");
      await loadConversationMessages();
      await loadChatConversations();
    } catch (chatError) {
      setError(friendlyErrorMessage(chatError, "Could not send chat message."));
    }
  };

  const loadMessages = useCallback(async () => {
    if (authBooting || !loggedIn) return;
    if (!activeAccountId && !unifiedMode) return;
    const cacheKey = `msgs:${currentUser?.id || "me"}:${unifiedMode ? "unified" : activeAccountId}:${String(activeFolderId || "INBOX").toUpperCase()}:q=${String(search || "")}:u=${filters.unread ? 1 : 0}:s=${filters.starred ? 1 : 0}:a=${filters.archived ? 1 : 0}`;
    const cached = cacheGet(cacheKey);
    if (Array.isArray(cached)) {
      setMessages(cached);
    }
    setLoadingMessages(true);
    if (!unifiedMode && activeFolderId === "DRAFTS" && activeAccountId) {
      try {
        const draftResponse = await api.get(`/api/mail/${activeAccountId}/drafts`);
        const draftMessages = (draftResponse.data.drafts || []).map((draft) => ({
          id: `draft-${draft.id}`,
          draft_id: draft.id,
          account_id: draft.accountId || activeAccountId,
          provider_message_id: `draft:${draft.id}`,
          source_type: "mail",
          is_draft: true,
          subject: draft.subject || "(No subject)",
          snippet: "Draft message",
          from_name: "Draft",
          from_email: "",
          received_at: draft.updatedAt || draft.createdAt,
          is_read: 1,
          has_attachments: 0
        }));
        setMessages(draftMessages);
        return;
      } finally {
        setLoadingMessages(false);
      }
    }
    const endpoint = unifiedMode
      ? "/api/mail/unified/messages"
      : `/api/mail/${activeAccountId}/messages`;
    try {
      const response = await api.get(endpoint, {
        params: {
          folderId: activeFolderId || "INBOX",
          q: search || undefined,
          unread: filters.unread || undefined,
          starred: filters.starred || undefined,
          archived: filters.archived || undefined,
          limit: 100
        }
      });
      setMessages(response.data.messages || []);
      cacheSet(cacheKey, response.data.messages || [], 90 * 1000);
      // Keep a rolling index for Cmd+K instant local search.
      const indexKey = `index:mail:${currentUser?.id || "me"}`;
      const nextIndex = (response.data.messages || []).slice(0, 500);
      cacheSet(indexKey, nextIndex, 15 * 60 * 1000);
    } finally {
      setLoadingMessages(false);
    }
  }, [
    authBooting,
    loggedIn,
    activeAccountId,
    activeFolderId,
    filters.archived,
    filters.starred,
    filters.unread,
    search,
    unifiedMode,
    currentUser?.id
  ]);

  const loadBriefing = useCallback(async () => {
    if (authBooting || !loggedIn) return;
    setLoadingBriefing(true);
    try {
      const res = await api.get("/api/mail/briefing");
      setBriefing(res.data.briefing || null);
    } catch (_e) {
      setBriefing(null);
    } finally {
      setLoadingBriefing(false);
    }
  }, [authBooting, loggedIn]);

  const loadFuture = useCallback(async () => {
    if (authBooting || !loggedIn) return;
    setLoadingFuture(true);
    try {
      const res = await api.get("/api/mail/future");
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      const mapped = items.map((item) => ({
        id: `future:${item.id}`,
        __snoozeId: item.id,
        __wakeAt: item.wakeAt,
        account_id: item.accountId,
        provider_message_id: item.providerMessageId,
        subject: item.preview?.subject || "(Snoozed message)",
        snippet: item.preview?.snippet || "",
        from_name: item.preview?.from_name || "",
        from_email: item.preview?.from_email || "",
        received_at: item.preview?.received_at || item.createdAt || "",
        is_read: 1,
        is_starred: 0,
        is_archived: 0,
        has_attachments: 0,
        source_type: "mail",
        thread_id: null
      }));
      setFutureItems(mapped);
    } catch (_e) {
      setFutureItems([]);
    } finally {
      setLoadingFuture(false);
    }
  }, [authBooting, loggedIn]);

  const syncMailNow = useCallback(async () => {
    if (!activeAccountId) return;
    try {
      await api.post(`/api/mail/${activeAccountId}/sync`);
      await Promise.all([loadFolders(), loadMessages(), loadBriefing()]);
    } catch (err) {
      const msg =
        (err && err.response && err.response.data && err.response.data.message) ||
        (err && err.message) ||
        "Mail sync failed";
      pushNotification(String(msg));
    }
  }, [activeAccountId, loadBriefing, loadFolders, loadMessages, pushNotification]);

  const snoozeSelectedMessage = useCallback(
    async (preset) => {
      if (!selectedMessage?.account_id || !selectedMessage?.provider_message_id) return;
      const now = new Date();
      let wake = new Date(now.getTime() + 60 * 60 * 1000);
      if (preset === "laterToday") {
        wake = new Date(now);
        wake.setHours(18, 0, 0, 0);
        if (wake.getTime() <= now.getTime()) wake = new Date(now.getTime() + 60 * 60 * 1000);
      } else if (preset === "tomorrowMorning") {
        wake = new Date(now);
        wake.setDate(wake.getDate() + 1);
        wake.setHours(9, 0, 0, 0);
      } else if (preset === "nextWeek") {
        wake = new Date(now);
        wake.setDate(wake.getDate() + 7);
        wake.setHours(9, 0, 0, 0);
      }
      await api.post("/api/mail/snooze", {
        sourceType: "mail",
        accountId: String(selectedMessage.account_id),
        providerMessageId: String(selectedMessage.provider_message_id),
        wakeAt: wake.toISOString()
      });
      pushNotification("Snoozed");
      await Promise.all([loadMessages(), loadBriefing()]);
      if (mailView === "future") await loadFuture();
      setSelectedMessage(null);
      if (isMobile) setMobileDetailOpen(false);
      haptic(12);
    },
    [
      selectedMessage?.account_id,
      selectedMessage?.provider_message_id,
      loadMessages,
      loadBriefing,
      mailView,
      loadFuture,
      isMobile,
      haptic,
      pushNotification
    ]
  );

  const generateAiDraft = useCallback(async () => {
    if (activePage !== "compose") return;
    setAiDraftLoading(true);
    try {
      syncComposeFromEditor();
      const res = await api.post("/api/mail/compose/ghostwrite", {
        subject: compose.subject || "",
        to: compose.to || [],
        intent: aiDraftIntent || "",
        tone: aiDraftTone || "",
        currentDraft: compose.text || "",
        context: selectedMessage
          ? {
              fromName: selectedMessage.from_name || "",
              fromEmail: selectedMessage.from_email || "",
              snippet: selectedMessage.snippet || ""
            }
          : undefined
      });
      const draft = String(res.data?.draft || "").trim();
      setAiDraftResult(draft);
      setAiDraftOpen(true);
    } catch (_e) {
      pushNotification("AI draft unavailable right now.");
    } finally {
      setAiDraftLoading(false);
    }
  }, [activePage, aiDraftIntent, aiDraftTone, compose.subject, compose.text, compose.to, selectedMessage, pushNotification, syncComposeFromEditor]);

  const parseCommandQuery = useCallback((raw) => {
    const txt = String(raw || "").trim();
    const tokens = txt.split(/\s+/).filter(Boolean);
    const filtersOut = { unread: false, starred: false, archived: false, from: "", subject: "" };
    const remaining = [];
    for (const t of tokens) {
      const lower = t.toLowerCase();
      if (lower === "unread") {
        filtersOut.unread = true;
        continue;
      }
      if (lower === "starred" || lower === "pinned") {
        filtersOut.starred = true;
        continue;
      }
      if (lower === "archived") {
        filtersOut.archived = true;
        continue;
      }
      if (lower.startsWith("from:")) {
        filtersOut.from = t.slice("from:".length);
        continue;
      }
      if (lower.startsWith("subject:")) {
        filtersOut.subject = t.slice("subject:".length);
        continue;
      }
      remaining.push(t);
    }
    return { filters: filtersOut, text: remaining.join(" ") };
  }, []);

  const loadCommandMailResults = useCallback(
    async (q) => {
      const parsed = parseCommandQuery(q);
      const query = String(parsed.text || "").trim();
      if (authBooting || !loggedIn || query.length < 2) {
        setCommandMailResults([]);
        return;
      }
      // Instant local results first (sub-100ms feel).
      const localIndex = cacheGet(`index:mail:${currentUser?.id || "me"}`) || [];
      if (Array.isArray(localIndex) && localIndex.length > 0) {
        const localHits = localMailSearch(localIndex, parsed, 10);
        if (localHits.length > 0) {
          setCommandMailResults(localHits);
        }
      }
      if (commandMailAbortRef.current) {
        try {
          commandMailAbortRef.current.abort();
        } catch (_e) {
          // ignore
        }
      }
      const controller = new AbortController();
      commandMailAbortRef.current = controller;

      setCommandMailLoading(true);
      try {
        const res = await api.get("/api/mail/unified/messages", {
          params: {
            q: query,
            from: parsed.filters.from || undefined,
            subject: parsed.filters.subject || undefined,
            unread: parsed.filters.unread || undefined,
            starred: parsed.filters.starred || undefined,
            archived: parsed.filters.archived || undefined,
            limit: 10
          },
          signal: controller.signal
        });
        setCommandMailResults(res.data.messages || []);
      } catch (_e) {
        // ignore aborted/failed command search
      } finally {
        setCommandMailLoading(false);
      }
    },
    [authBooting, loggedIn, parseCommandQuery, currentUser?.id]
  );

  const loadHealthSummary = useCallback(async () => {
    setHealthError("");
    setHealthLoading(true);
    try {
      const res = await api.get("/api/ops/health/summary");
      setHealthSummary(res.data || null);
    } catch (error) {
      setHealthSummary(null);
      setHealthError(friendlyErrorMessage(error, "Unable to load system health right now."));
    } finally {
      setHealthLoading(false);
    }
  }, [friendlyErrorMessage]);

  const commandActions = useMemo(() => {
    const items = [];
    const parsed = parseCommandQuery(commandQuery);
    const q = String(parsed.text || "").trim();

    const addFilterAction = (id, title, subtitle, next) => {
      items.push({
        id,
        title,
        subtitle,
        icon: "⟡",
        keywords: ["filter", "triage", "focus", "inbox"],
        run: async () => {
          setActivePage("mail");
          setFilters((prev) => ({ ...prev, ...next.filters }));
          setSearch(next.search ?? "");
          await loadMessages();
        }
      });
    };

    if (!q) {
      items.push({ id: "hdr-filters", type: "header", title: "Suggested filters" });
      addFilterAction(
        "filter-unread",
        "Filter: Unread",
        "Show only unread messages",
        { filters: { unread: true, starred: false, archived: false }, search: "" }
      );
      addFilterAction(
        "filter-starred",
        "Filter: Starred",
        "Show only starred/pinned messages",
        { filters: { unread: false, starred: true, archived: false }, search: "" }
      );
      addFilterAction(
        "filter-archive",
        "Filter: Archived",
        "Show archived messages",
        { filters: { unread: false, starred: false, archived: true }, search: "" }
      );
      items.push({
        id: "filter-clear",
        title: "Clear filters",
        subtitle: "Reset inbox filters and search",
        icon: "↺",
        run: async () => {
          setFilters({ unread: false, starred: false, archived: false });
          setSearch("");
          await loadMessages();
        }
      });
    }

    if (q.length >= 2) {
      items.push({ id: "hdr-mail", type: "header", title: "Top matches" });
      (commandMailResults || []).forEach((m) => {
        const sender = m.from_name || m.from_email || "Unknown sender";
        items.push({
          id: `msg-${m.account_id}-${m.provider_message_id}`,
          title: m.subject || "(No subject)",
          subtitle: `${sender} • ${m.received_at ? new Date(m.received_at).toLocaleString() : "recent"}`,
          icon: "✦",
          keywords: [sender, m.snippet || "", m.from_email || "", m.from_name || ""],
          run: () => selectMessageRef.current?.(m)
        });
      });
      if (commandMailLoading) {
        items.push({
          id: "mail-search-loading",
          title: "Searching mail…",
          subtitle: "Fetching results (try: unread from:acme subject:invoice)",
          icon: "⌛",
          keywords: ["loading"]
        });
      }
    }
    items.push({ id: "hdr-actions", type: "header", title: "Actions" });
    items.push({
      id: "compose",
      title: "Compose new message",
      subtitle: "Open the compose panel",
      icon: "✉",
      hint: "C",
      run: () => beginCompose()
    });
    items.push({
      id: "refresh-all",
      title: "Refresh",
      subtitle: "Reload accounts, folders, and inbox",
      icon: "⟳",
      run: async () => {
        await Promise.all([loadAccounts(), loadFolders(), loadMessages(), loadBriefing()]);
      }
    });
    if (activeAccountId) {
      items.push({
        id: "sync",
        title: "Sync now",
        subtitle: "Pull latest messages via IMAP",
        icon: "⟲",
        run: () => syncMailNow()
      });
    }
    items.push({
      id: "toggle-unified",
      title: unifiedMode ? "Switch to single inbox" : "Switch to unified inbox",
      subtitle: unifiedMode ? "Use the selected account" : "See all accounts together",
      icon: "⇄",
      run: () => setUnifiedMode((v) => !v)
    });
    if ((accounts || []).length > 0) {
      items.push({ id: "hdr-accounts", type: "header", title: "Accounts" });
    }
    (accounts || []).forEach((acc) => {
      items.push({
        id: `acct-${acc.id}`,
        title: `Account: ${acc.emailAddress || acc.email_address || acc.id}`,
        subtitle: "Switch active account",
        icon: "👤",
        run: () => {
          setUnifiedMode(false);
          setActiveAccountId(acc.id);
          setActivePage("mail");
        }
      });
    });
    if ((folders || []).length > 0) {
      items.push({ id: "hdr-folders", type: "header", title: "Folders" });
    }
    (folders || []).forEach((f) => {
      items.push({
        id: `folder-${f.id}`,
        title: `Folder: ${f.name || f.id}`,
        subtitle: "Jump to folder",
        icon: "📁",
        run: () => {
          setActiveFolderId(String(f.id || "INBOX").toUpperCase());
          setActivePage("mail");
        }
      });
    });
    items.push({
      id: "daily-briefing",
      title: "Refresh Daily Briefing",
      subtitle: "Update highlights and action items",
      icon: "📰",
      run: () => loadBriefing()
    });
    items.push({
      id: "open-settings",
      title: "Open settings",
      subtitle: "Plans, limits, and connected accounts",
      icon: "⚙",
      run: () => setActivePage("settings")
    });
    items.push({
      id: "open-health",
      title: "Open system health",
      subtitle: "Queues, sync lag, last send, database",
      icon: "🩺",
      run: () => {
        setActivePage("health");
        loadHealthSummary();
      }
    });
    return items;
  }, [
    accounts,
    folders,
    unifiedMode,
    activeAccountId,
    loadAccounts,
    loadBriefing,
    loadFolders,
    loadMessages,
    syncMailNow,
    loadHealthSummary,
    commandQuery,
    commandMailResults,
    commandMailLoading,
    selectMessageRef,
    parseCommandQuery
  ]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const key = String(e.key || "").toLowerCase();
      const isMac = typeof navigator !== "undefined" && /mac/i.test(navigator.platform || "");
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (mod && key === "k") {
        e.preventDefault();
        setCommandBarOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    // Test/telemetry hook: enables deterministic perf gating in headless runs.
    try {
      window.__netluqOpenCommandBar = () => setCommandBarOpen(true);
    } catch (_e) {}
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!commandBarOpen) return;
    const q = String(commandQuery || "").trim();
    if (commandMailDebounceRef.current) {
      clearTimeout(commandMailDebounceRef.current);
    }
    commandMailDebounceRef.current = setTimeout(() => {
      loadCommandMailResults(q);
    }, 180);
    return () => {
      if (commandMailDebounceRef.current) {
        clearTimeout(commandMailDebounceRef.current);
      }
    };
  }, [commandBarOpen, commandQuery, loadCommandMailResults]);

  useEffect(() => {
    if (authBooting || !loggedIn) return;
    // Quiet background refresh: keeps inbox & briefing fresh.
    let timer = null;
    const tick = async () => {
      try {
        if (document.visibilityState !== "visible") return;
        if (loadingMessages || loadingBriefing || busyAction) return;
        await Promise.all([loadMessages(), loadBriefing()]);
      } catch (_e) {
        // ignore transient background failures
      }
    };
    timer = setInterval(tick, 45 * 1000);
    const onOnline = () => tick();
    window.addEventListener("online", onOnline);
    return () => {
      if (timer) clearInterval(timer);
      window.removeEventListener("online", onOnline);
    };
  }, [authBooting, busyAction, loadBriefing, loadMessages, loadingBriefing, loadingMessages, loggedIn]);

  useEffect(() => {
    if (authBooting || !loggedIn) return;
    loadBriefing();
  }, [authBooting, loggedIn, loadBriefing]);

  useEffect(() => {
    if (authBooting || !loggedIn) return;
    if (mailView !== "future") return;
    loadFuture();
  }, [authBooting, loggedIn, mailView, loadFuture]);

  const selectMessage = async (message) => {
    if (message?.is_draft && message?.draft_id && message?.account_id) {
      try {
        const draftResponse = await api.get(`/api/mail/${message.account_id}/drafts/${message.draft_id}`);
        const draft = draftResponse.data.draft;
        setDraftId(draft.id);
        beginCompose({
          to: draft.to || [],
          cc: draft.cc || [],
          bcc: draft.bcc || [],
          subject: draft.subject || "",
          text: draft.text || "",
          html: draft.html || "",
          inReplyTo: draft.inReplyTo || "",
          references: draft.references || [],
          attachments: draft.attachments || []
        });
      } catch (draftError) {
        setError(friendlyErrorMessage(draftError, "Failed to open draft."));
      }
      return;
    }
    setSelectedMessage(message);
    setError("");
    if (message.source_type === "social") {
      const socialAccountId = message.social_account_id || message.account_id || activeSocialAccountId;
      setActivePage("social");
      setThreadMessages([]);
      setReplySuggestions([]);
      setReplySuggestionsSource("");
      setReplySuggestionStatus("");
      if (!socialAccountId) return;
      try {
        if (socialAccountId !== activeSocialAccountId) {
          setActiveSocialAccountId(socialAccountId);
        }
        const response = await api.get(`/api/social/accounts/${socialAccountId}/messages`, {
          params: { limit: 100 }
        });
        const scopedMessages = response.data.messages || [];
        setSocialMessages(scopedMessages);
        const providerMessageId = String(message.provider_message_id || message.providerMessageId || "");
        const target =
          scopedMessages.find(
            (item) =>
              String(item.id || "") === String(message.id || "") ||
              String(item.providerMessageId || "") === providerMessageId
          ) || null;
        const contactKey =
          target?.authorHandle ||
          target?.authorName ||
          target?.id ||
          message.from_email ||
          message.from_name ||
          "";
        if (contactKey) {
          setSelectedSocialContactId(String(contactKey));
        } else {
          setSelectedSocialContactId("");
        }
        setSocialReply((prev) => ({
          ...prev,
          socialMessageId: target?.id || "",
          text: ""
        }));
      } catch (_error) {
        setError("Could not open this social conversation.");
      }
      return;
    }
    setActivePage("mail");
    if (isMobile) {
      setMobileDetailOpen(true);
    }
    try {
      setLoadingThread(true);
      const accountForMessage = message.account_id || activeAccountId;
      const detail = await api.get(`/api/mail/${accountForMessage}/messages/${message.provider_message_id}`, {
        params: { folderId: activeFolderId }
      });
      setSelectedMessage({
        ...(detail.data.message || message),
        enrichment: detail.data.enrichment || null,
        conversationTrail: Array.isArray(detail.data.conversationTrail) ? detail.data.conversationTrail : []
      });
      if (detail.data.message?.thread_id) {
        const thread = await api.get(`/api/mail/${accountForMessage}/threads/${detail.data.message.thread_id}`);
        setThreadMessages(thread.data.messages || []);
      } else {
        setThreadMessages([]);
      }
      await optimisticFlagUpdate(accountForMessage, message.provider_message_id, { read: true });
    } catch (_error) {
      // Selection still works with fallback summary data.
    } finally {
      setLoadingThread(false);
    }
  };
  selectMessageRef.current = selectMessage;

  const optimisticApply = (messageId, updater) => {
    setMessages((prev) =>
      prev.map((message) =>
        message.provider_message_id === messageId ? { ...message, ...updater(message) } : message
      )
    );
  };

  const optimisticFlagUpdate = async (accountIdForMessage, messageId, flags) => {
    const before = messages;
    optimisticApply(messageId, () => ({
      is_read: flags.read === undefined ? undefined : flags.read ? 1 : 0,
      is_starred: flags.starred === undefined ? undefined : flags.starred ? 1 : 0,
      is_archived: flags.archived === undefined ? undefined : flags.archived ? 1 : 0
    }));
    try {
      await api.patch(`/api/mail/${accountIdForMessage}/messages/${messageId}/flags`, flags);
    } catch (_error) {
      setMessages(before);
    }
  };

  const optimisticMove = async (accountIdForMessage, messageId, targetFolderId) => {
    const before = messages;
    setMessages((prev) => prev.filter((message) => message.provider_message_id !== messageId));
    try {
      await api.post(`/api/mail/${accountIdForMessage}/messages/${messageId}/move`, { targetFolderId });
    } catch (_error) {
      setMessages(before);
    }
  };

  const optimisticDelete = async (accountIdForMessage, messageId) => {
    const before = messages;
    setMessages((prev) => prev.filter((message) => message.provider_message_id !== messageId));
    try {
      await api.delete(`/api/mail/${accountIdForMessage}/messages/${messageId}`);
    } catch (_error) {
      setMessages(before);
    }
  };

  const quickConnectAccount = async () => {
    try {
      setLoading(true);
      const emailAddress = String(connectForm.emailAddress || "").trim();
      const password = String(connectForm.password || "");
      const authType = connectForm.authType || "app_password";
      const payload = connectForm.manualMode
        ? {
            emailAddress,
            authType,
            password,
            imap: {
              host: String(connectForm.imap?.host || "").trim(),
              port: Number(connectForm.imap?.port || 993) || 993,
              secure: Boolean(connectForm.imap?.secure),
              username: String(connectForm.imap?.username || "").trim() || emailAddress,
              password: String(connectForm.imap?.password || "") || password
            },
            smtp: {
              host: String(connectForm.smtp?.host || "").trim(),
              port: Number(connectForm.smtp?.port || 587) || 587,
              secure: Boolean(connectForm.smtp?.secure),
              username: String(connectForm.smtp?.username || "").trim() || emailAddress,
              password: String(connectForm.smtp?.password || "") || password
            }
          }
        : { emailAddress, authType, password };
      await api.post("/api/accounts/quick-connect", payload);
      await loadAccounts();
      setConnectForm({
        emailAddress: "",
        password: "",
        authType: "app_password",
        manualMode: false,
        imap: { host: "", port: 993, secure: true, username: "", password: "" },
        smtp: { host: "", port: 587, secure: true, username: "", password: "" }
      });
    } catch (connectError) {
      const msg = friendlyErrorMessage(connectError, "Failed to connect account.");
      if (isLimitOrQuotaError(connectError)) {
        setUpgradeNudge(msg);
        setActivePage("upgrade");
        setSettingsCategory("billing");
        loadBilling().catch(() => {});
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const connectSocialAccount = async () => {
    try {
      await api.post("/api/social/accounts", socialConnectForm);
      setSocialConnectForm((prev) => ({ ...prev, handle: "", displayName: "", accessToken: "" }));
      await loadSocialAccounts();
    } catch (connectError) {
      const msg = friendlyErrorMessage(connectError, "Failed to connect social account.");
      if (isLimitOrQuotaError(connectError)) {
        setUpgradeNudge(msg);
        setActivePage("upgrade");
        setSettingsCategory("billing");
        loadBilling().catch(() => {});
      } else {
        setError(msg);
      }
    }
  };

  const loadSocialOauthProviders = useCallback(async () => {
    if (authBooting || !loggedIn) return;
    const response = await api.get("/api/social/oauth/providers");
    const providers = response.data.providers || [];
    setSocialOauth((prev) => ({
      ...prev,
      providers,
      platform: providers[0]?.platform || prev.platform
    }));
  }, [authBooting, loggedIn]);

  const loadEmailOauthProviders = useCallback(async () => {
    if (authBooting || !loggedIn) return;
    const response = await api.get("/api/accounts/oauth/providers");
    const providers = response.data.providers || [];
    setEmailOauth((prev) => ({
      ...prev,
      providers,
      provider: providers[0]?.provider || prev.provider
    }));
  }, [authBooting, loggedIn]);

  const startSocialOauth = async () => {
    try {
      const response = await api.post("/api/social/oauth/init", { platform: socialOauth.platform });
      const authorizeUrl = response.data.authorizeUrl;
      setSocialOauth((prev) => ({ ...prev, state: response.data.state }));
      window.open(authorizeUrl, "_blank", "width=520,height=760");
      pushNotification("OAuth window opened. Complete auth, then paste code below.");
    } catch (oauthError) {
      setError(friendlyErrorMessage(oauthError, "Failed to initialize social OAuth."));
    }
  };

  const completeSocialOauth = async () => {
    try {
      await api.post(`/api/social/oauth/callback/${socialOauth.platform}`, {
        state: socialOauth.state,
        code: socialOauth.code
      });
      setSocialOauth((prev) => ({ ...prev, state: "", code: "" }));
      await loadSocialAccounts();
      pushNotification(`Connected ${socialOauth.platform} via OAuth.`);
    } catch (oauthError) {
      setError(friendlyErrorMessage(oauthError, "Failed to complete social OAuth."));
    }
  };

  const startEmailOauth = async () => {
    try {
      const response = await api.post("/api/accounts/oauth/init", { provider: emailOauth.provider, scopes: [] });
      const authorizeUrl = response.data.authorizeUrl;
      setEmailOauth((prev) => ({ ...prev, state: response.data.state }));
      window.open(authorizeUrl, "_blank", "width=520,height=760");
      pushNotification("Email OAuth window opened. Complete auth and paste code.");
    } catch (oauthError) {
      setError(friendlyErrorMessage(oauthError, "Failed to initialize email OAuth."));
    }
  };

  const completeEmailOauth = async () => {
    try {
      await api.post(`/api/accounts/oauth/callback/${emailOauth.provider}`, {
        state: emailOauth.state,
        code: emailOauth.code,
        emailAddressHint: emailOauth.emailAddressHint
      });
      setEmailOauth((prev) => ({ ...prev, state: "", code: "", emailAddressHint: "" }));
      await loadAccounts();
      pushNotification(`Connected ${emailOauth.provider} account.`);
    } catch (oauthError) {
      setError(friendlyErrorMessage(oauthError, "Failed to complete email OAuth."));
    }
  };

  const replyToSocialMessage = async () => {
    if (!activeSocialAccountId || !socialReply.socialMessageId || !socialReply.text.trim()) return;
    try {
      const response = await api.post(
        `/api/social/accounts/${activeSocialAccountId}/messages/${socialReply.socialMessageId}/reply`,
        { text: socialReply.text }
      );
      setSocialReply((prev) => ({
        ...prev,
        replyId: response.data.reply.id,
        status: response.data.reply.status,
        text: ""
      }));
    } catch (replyError) {
      setError(friendlyErrorMessage(replyError, "Failed to queue social reply."));
    }
  };

  const loadSocialReplySuggestions = useCallback(async (socialMessageId) => {
    if (!activeSocialAccountId || !socialMessageId) {
      setSocialReplySuggestions([]);
      setSocialReplySuggestionsSource("");
      return;
    }
    const response = await api.post(
      `/api/social/accounts/${activeSocialAccountId}/messages/${socialMessageId}/reply-suggestions`
    );
    setSocialReplySuggestions(Array.isArray(response.data?.suggestions) ? response.data.suggestions : []);
    setSocialReplySuggestionsSource(String(response.data?.source || ""));
    setSocialAiPolicy({
      requiresHumanApproval: Boolean(response.data?.policy?.requiresHumanApproval ?? true),
      autoSendEnabled: Boolean(response.data?.policy?.autoSendEnabled ?? false)
    });
    setSocialReplySuggestionStatus("");
    if (socialSuggestionPanelRef.current) {
      socialSuggestionPanelRef.current.scrollTop = 0;
    }
  }, [activeSocialAccountId]);

  const applySocialReplySuggestion = (suggestion) => {
    const next = String(suggestion || "").trim();
    if (!next) return;
    setSocialReply((prev) => ({
      ...prev,
      text:
        socialReplySuggestionMode === "append" && String(prev.text || "").trim()
          ? `${String(prev.text || "").trim()}\n${next}`
          : next
    }));
    setSocialReplySuggestionStatus(socialReplySuggestionMode === "append" ? "Appended" : "Applied");
  };

  const handleSocialReplyInput = (event) => {
    const value = event?.target?.value || "";
    setSocialReply((prev) => ({ ...prev, text: value }));
    const el = socialReplyInputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
  };

  const copySocialReplySuggestion = async (suggestion) => {
    const text = String(suggestion || "").trim();
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setSocialReplySuggestionStatus("Copied");
        return;
      }
    } catch (_e) {
      // fallback below
    }
    const helper = document.createElement("textarea");
    helper.value = text;
    helper.style.position = "fixed";
    helper.style.opacity = "0";
    document.body.appendChild(helper);
    helper.focus();
    helper.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(helper);
    if (ok) setSocialReplySuggestionStatus("Copied");
  };

  const syncSocialMessages = async () => {
    if (!activeSocialAccountId) return;
    try {
      await api.post(`/api/social/accounts/${activeSocialAccountId}/sync`);
      await loadSocialMessages();
      await loadMessages();
    } catch (syncError) {
      setError(friendlyErrorMessage(syncError, "Failed to sync social account."));
    }
  };

  const setFolderView = (folderId) => {
    setActivePage("mail");
    setSelectedMessage(null);
    setThreadMessages([]);
    if (activeAccountId) {
      // When a concrete account is selected, keep folder browsing account-scoped.
      setUnifiedMode(false);
    }
    if (folderId === "DRAFTS") {
      setUnifiedMode(false);
      setFilters((prev) => ({ ...prev, starred: false, archived: false }));
      setActiveFolderId("DRAFTS");
      return;
    }
    if (folderId === "PINNED" || folderId === "STARRED") {
      setFilters((prev) => ({ ...prev, starred: true, archived: false }));
      setActiveFolderId("INBOX");
      return;
    }
    if (folderId === "ARCHIVE") {
      setFilters((prev) => ({ ...prev, archived: true, starred: false }));
      setActiveFolderId("ARCHIVE");
      return;
    }
    setFilters((prev) => ({ ...prev, starred: false, archived: false }));
    setActiveFolderId(folderId);
  };

  const handleInboxScopeChange = (nextValue) => {
    // Clear prior message context when switching account scope.
    setSelectedMessage(null);
    setThreadMessages([]);
    setError("");
    if (nextValue === "all") {
      setUnifiedMode(true);
      setActivePage("mail");
      return;
    }
    setActiveAccountId(nextValue);
    setUnifiedMode(false);
    setActivePage("mail");
  };

  const openSocialPlatform = async (platformId) => {
    const account = socialAccounts.find(
      (item) => String(item.platform || "").toLowerCase() === String(platformId || "").toLowerCase()
    );
    if (!account) {
      setActivePage("settings");
      setSettingsCategory("messaging");
      setSettingsProvider(platformId);
      setSocialConnectForm((prev) => ({ ...prev, platform: platformId }));
      setError(`Connect ${platformId} first to open DM/comments.`);
      return;
    }
    setError("");
    setActivePage("social");
    setActiveSocialAccountId(account.id);
    setSelectedSocialContactId("");
    setSocialReply((prev) => ({ ...prev, socialMessageId: "", text: "" }));
    setSocialReplySuggestions([]);
    setSocialReplySuggestionsSource("");
    setSocialReplySuggestionStatus("");
    try {
      const response = await api.get(`/api/social/accounts/${account.id}/messages`, { params: { limit: 100 } });
      setSocialMessages(response.data.messages || []);
    } catch (_e) {
      setSocialMessages([]);
    }
  };

  const beginCompose = (seed = {}) => {
    setActivePage("compose");
    setCompose({
      ...defaultCompose,
      ...seed,
      to: Array.isArray(seed.to) ? seed.to : seed.to ? [seed.to] : []
    });
    setShowCcField(Array.isArray(seed.cc) && seed.cc.length > 0);
    setShowBccField(Array.isArray(seed.bcc) && seed.bcc.length > 0);
    setShowComposeEmojiPicker(false);
    setDraftId("");
    setReplySuggestions([]);
    setReplySuggestionsSource("");
    setReplySuggestionStatus("");
    setShowNewMenu(false);
  };

  const applyTemplateToCompose = (template) => {
    const blocks = Array.isArray(template?.blocks) ? template.blocks : [];
    const headingBlock = blocks.find((block) => block.type === "heading");
    const derivedSubject =
      (headingBlock &&
        (headingBlock.html && String(headingBlock.html).trim() !== ""
          ? plainTextFromHtml(headingBlock.html)
          : String(headingBlock.content || "").trim())) ||
      template.subject ||
      "";
    const derivedText =
      blocks
        .filter((block) => ["text", "footer", "social", "columns"].includes(block.type))
        .map((block) => {
          if (block.type === "columns") {
            const left = block.html && String(block.html).trim() !== "" ? plainTextFromHtml(block.html) : String(block.content || "").trim();
            const right =
              block.rightHtml && String(block.rightHtml).trim() !== "" ? plainTextFromHtml(block.rightHtml) : String(block.rightContent || "").trim();
            return [left, right].filter(Boolean).join("\n\n");
          }
          return block.html && String(block.html).trim() !== "" ? plainTextFromHtml(block.html) : String(block.content || "").trim();
        })
        .filter(Boolean)
        .join("\n\n") || template.body || "";
    beginCompose({
      subject: derivedSubject,
      text: derivedText
    });
  };

  const openTemplateEditor = (template = null) => {
    const nextDraft = buildTemplateDraft(template || {});
    setTemplateDraft(nextDraft);
    setSelectedTemplateBlockId(nextDraft.blocks[0]?.id || "");
    setLockedTemplateBlockIds([]);
    setTemplateHistory([nextDraft]);
    setTemplateHistoryIndex(0);
    setTemplateEditorTab("edit");
    setTemplatePreviewDevice("desktop");
    setTemplateEditorOpen(true);
  };

  const addTemplateElement = (elementId) => {
    const nextBlock =
      elementId === "image"
        ? createTemplateBlock("image", "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1200&q=60")
        : elementId === "video"
          ? createTemplateBlock("video", "")
          : elementId === "audio"
            ? createTemplateBlock("audio", "")
        : elementId === "columns"
          ? createTemplateBlock("columns", "Left column", { rightContent: "Right column" })
        : elementId === "button"
          ? createTemplateBlock("button", "Call to action", { href: "" })
          : elementId === "divider"
            ? createTemplateBlock("divider", "")
            : elementId === "social"
              ? createTemplateBlock("social", "Instagram • X • LinkedIn")
              : elementId === "footer"
                ? createTemplateBlock("footer", "You are receiving this email because you subscribed.")
                : elementId === "code"
                  ? createTemplateBlock("code", "<p>Custom HTML block</p>")
                  : elementId === "gallery"
                    ? createTemplateBlock("image", "")
                  : elementId === "logo"
                    ? createTemplateBlock("logo", "")
                    : createTemplateBlock("text", "New content block");
    setTemplateDraft((prev) => ({ ...prev, blocks: [...(prev.blocks || []), nextBlock] }));
    setSelectedTemplateBlockId(nextBlock.id);
  };

  const undoTemplateChange = useCallback(() => {
    setTemplateHistoryIndex((prev) => {
      const nextIndex = prev - 1;
      if (nextIndex < 0) return prev;
      const nextDraft = templateHistory[nextIndex];
      if (!nextDraft) return prev;
      templateHistoryRestoreRef.current = true;
      setTemplateDraft(nextDraft);
      return nextIndex;
    });
  }, [templateHistory]);

  const redoTemplateChange = useCallback(() => {
    setTemplateHistoryIndex((prev) => {
      const nextIndex = prev + 1;
      if (nextIndex >= templateHistory.length) return prev;
      const nextDraft = templateHistory[nextIndex];
      if (!nextDraft) return prev;
      templateHistoryRestoreRef.current = true;
      setTemplateDraft(nextDraft);
      return nextIndex;
    });
  }, [templateHistory]);

  const updateTemplateBlock = (blockId, patch) => {
    setTemplateDraft((prev) => ({
      ...prev,
      blocks: (prev.blocks || []).map((block) => (block.id === blockId ? { ...block, ...patch } : block))
    }));
  };

  const patchTemplateBlockStyle = (blockId, partial) => {
    setTemplateDraft((prev) => ({
      ...prev,
      blocks: (prev.blocks || []).map((block) =>
        block.id === blockId ? { ...block, style: { ...mergeDefaultStyle(block), ...partial } } : block
      )
    }));
  };

  const deleteTemplateBlock = (blockId) => {
    setTemplateDraft((prev) => ({
      ...prev,
      blocks: (prev.blocks || []).filter((block) => block.id !== blockId)
    }));
    setLockedTemplateBlockIds((prev) => prev.filter((id) => id !== blockId));
    setSelectedTemplateBlockId((prev) => (prev === blockId ? "" : prev));
  };

  const moveTemplateBlock = (blockId, direction) => {
    setTemplateDraft((prev) => {
      const list = [...(prev.blocks || [])];
      const index = list.findIndex((block) => block.id === blockId);
      if (index < 0) return prev;
      const nextIndex = direction === "up" ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= list.length) return prev;
      [list[index], list[nextIndex]] = [list[nextIndex], list[index]];
      return { ...prev, blocks: list };
    });
  };

  const duplicateTemplateBlock = (blockId) => {
    setTemplateDraft((prev) => {
      const list = [...(prev.blocks || [])];
      const index = list.findIndex((block) => block.id === blockId);
      if (index < 0) return prev;
      const source = list[index];
      const clone = { ...source, id: `blk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` };
      list.splice(index + 1, 0, clone);
      return { ...prev, blocks: list };
    });
  };

  const toggleLockTemplateBlock = (blockId) => {
    setLockedTemplateBlockIds((prev) => (prev.includes(blockId) ? prev.filter((id) => id !== blockId) : [...prev, blockId]));
  };

  const reorderTemplateBlock = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    setTemplateDraft((prev) => {
      const list = [...(prev.blocks || [])];
      const fromIndex = list.findIndex((block) => block.id === fromId);
      const toIndex = list.findIndex((block) => block.id === toId);
      if (fromIndex < 0 || toIndex < 0) return prev;
      const [moved] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, moved);
      return { ...prev, blocks: list };
    });
  };

  const saveTemplateDraft = () => {
    const blocks = Array.isArray(templateDraft.blocks) && templateDraft.blocks.length > 0 ? templateDraft.blocks : normalizeTemplateBlocks(templateDraft);
    const headingBlock = blocks.find((block) => block.type === "heading");
    const textBlock = blocks.find((block) => block.type === "text");
    const imageBlock = blocks.find((block) => block.type === "image");
    const buttonBlock = blocks.find((block) => block.type === "button");
    const normalized = buildTemplateDraft({
      ...templateDraft,
      name: String(templateDraft.name || "").trim() || "Untitled Template",
      category: String(templateDraft.category || "").trim() || "General",
      subject: String(
        headingBlock
          ? headingBlock.html && String(headingBlock.html).trim() !== ""
            ? plainTextFromHtml(headingBlock.html)
            : String(headingBlock.content || "").trim()
          : templateDraft.subject || ""
      ).trim(),
      body: String(
        textBlock
          ? textBlock.html && String(textBlock.html).trim() !== ""
            ? plainTextFromHtml(textBlock.html)
            : String(textBlock.content || "").trim()
          : templateDraft.body || ""
      ),
      heroImageUrl: String(imageBlock?.content || templateDraft.heroImageUrl || ""),
      ctaText: String(buttonBlock?.content || templateDraft.ctaText || ""),
      ctaUrl: String(buttonBlock?.href || templateDraft.ctaUrl || ""),
      blocks
    });
    setTemplateCatalog((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === normalized.id);
      if (existingIndex >= 0) {
        const next = [...prev];
        next[existingIndex] = normalized;
        return next;
      }
      return [normalized, ...prev];
    });
    setTemplateSavedAt(new Date().toISOString());
    pushNotification("Template saved.");
  };

  const isSuperAdmin = useMemo(() => {
    const role = String(currentUser?.role || "").toLowerCase();
    const email = String(currentUser?.email || "").toLowerCase();
    return role === "super_admin" || role === "admin" || email === "admin@outlook.local" || email.includes("superadmin");
  }, [currentUser?.role, currentUser?.email]);

  const addPlatformVerifiedDomain = async () => {
    const domain = String(platformNewVerified || "")
      .trim()
      .toLowerCase();
    if (!domain) {
      setError("Enter a domain (e.g. company.com).");
      return;
    }
    await api.post("/api/admin/verified-domains", { domain });
    setPlatformNewVerified("");
    await loadPlatformAdmin();
    pushNotification("Verified domain added.");
  };

  const removePlatformVerifiedDomain = async (domain) => {
    const d = encodeURIComponent(String(domain || "").toLowerCase());
    await api.delete(`/api/admin/verified-domains/${d}`);
    await loadPlatformAdmin();
    pushNotification("Domain removed from allowlist.");
  };

  const addPlatformBlockedDomain = async () => {
    const domain = String(platformNewBlocked || "")
      .trim()
      .toLowerCase();
    if (!domain) {
      setError("Enter a domain to block.");
      return;
    }
    await api.post("/api/admin/blocked-domains", { domain });
    setPlatformNewBlocked("");
    await loadPlatformAdmin();
    pushNotification("Blocked domain added.");
  };

  const removePlatformBlockedDomain = async (domain) => {
    const d = encodeURIComponent(String(domain || "").toLowerCase());
    await api.delete(`/api/admin/blocked-domains/${d}`);
    await loadPlatformAdmin();
    pushNotification("Domain removed from blocklist.");
  };

  const savePlatformLimits = async () => {
    if (!platformSettings) return;
    await api.put("/api/admin/settings", {
      maxEmailAccountsPerUser: Number(platformSettings.maxEmailAccountsPerUser),
      maxSocialAccountsPerPlatform: Number(platformSettings.maxSocialAccountsPerPlatform),
      defaultStorageLimitGb: Number(platformSettings.defaultStorageLimitGb),
      maxStorageLimitGb: Number(platformSettings.maxStorageLimitGb)
    });
    await loadPlatformAdmin();
    pushNotification("Platform limits saved.");
  };

  const savePlatformEmailPolicy = async () => {
    await api.put("/api/admin/settings", { emailDomainPolicy: platformPolicyDraft });
    await loadPlatformAdmin();
    pushNotification("Email domain policy saved to database (overrides .env until cleared).");
  };

  const clearPlatformEmailPolicyOverride = async () => {
    await api.put("/api/admin/settings", { emailDomainPolicyUseEnvDefault: true });
    await loadPlatformAdmin();
    pushNotification("Using .env default for email domain policy again.");
  };

  const runPlatformDomainInspect = async () => {
    const q = String(platformInspectInput || "").trim();
    if (!q) {
      setError("Enter a domain (e.g. acme.com) or an address like user@acme.com.");
      return;
    }
    const res = await api.post("/api/admin/domains/inspect", { domain: q });
    setPlatformInspectResult(res.data);
  };

  const allowlistInspectedDomain = async () => {
    const d = String(platformInspectResult?.domain || "")
      .trim()
      .toLowerCase();
    if (!d) return;
    await api.post("/api/admin/verified-domains", { domain: d });
    await loadPlatformAdmin();
    pushNotification(`Verified / allowlisted: ${d}`);
  };

  const blockInspectedDomain = async () => {
    const d = String(platformInspectResult?.domain || "")
      .trim()
      .toLowerCase();
    if (!d) return;
    await api.post("/api/admin/blocked-domains", { domain: d });
    await loadPlatformAdmin();
    pushNotification(`Blocked domain: ${d}`);
  };

  const publishTemplateAsPredefined = () => {
    if (!isSuperAdmin) {
      pushNotification("Only Super Admin can publish predefined templates.");
      return;
    }
    const normalized = buildTemplateDraft({
      ...templateDraft,
      name: String(templateDraft.name || "").trim() || "Untitled Template",
      category: String(templateDraft.category || "").trim() || "General"
    });
    setPredefinedTemplateCatalog((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === normalized.id);
      if (existingIndex >= 0) {
        const next = [...prev];
        next[existingIndex] = { ...normalized, isPredefined: true };
        return next;
      }
      return [{ ...normalized, isPredefined: true }, ...prev];
    });
    pushNotification("Template published to predefined catalog.");
  };

  const addMediaFilesToLibrary = async (fileList, { shared = false } = {}) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const mapped = await Promise.all(
      files.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              const type = inferMediaType(file.type, file.name);
              if (!type) return resolve(null);
              resolve(
                createMediaAsset({
                  name: file.name,
                  url: String(reader.result || ""),
                  type,
                  shared
                })
              );
            };
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
          })
      )
    );
    const valid = mapped.filter(Boolean);
    if (valid.length === 0) {
      pushNotification("No supported media files found.");
      return;
    }
    if (shared) {
      setSharedMediaLibrary((prev) => cleanupMediaAssets([...valid, ...(prev || [])]));
      pushNotification("Shared media assets published.");
      return;
    }
    setPersonalMediaLibrary((prev) => cleanupMediaAssets([...valid, ...(prev || [])]));
    pushNotification("Media added to your library.");
  };

  const applyMediaAssetToSelectedBlock = (asset) => {
    if (!selectedTemplateBlock?.id || !asset?.url) return;
    if (!["image", "audio", "video"].includes(selectedTemplateBlock.type)) {
      pushNotification("Select an image/audio/video block first.");
      return;
    }
    updateTemplateBlock(selectedTemplateBlock.id, { content: asset.url, type: asset.type });
  };

  const removeMediaAsset = (assetId, fromShared = false) => {
    if (fromShared) {
      if (!isSuperAdmin) {
        pushNotification("Only Super Admin can delete shared assets.");
        return;
      }
      setSharedMediaLibrary((prev) => (prev || []).filter((item) => item.id !== assetId));
      return;
    }
    setPersonalMediaLibrary((prev) => (prev || []).filter((item) => item.id !== assetId));
  };

  const handleNewMenuAction = (actionId) => {
    setShowNewMenu(false);
    if (actionId === "mail") {
      beginCompose();
      return;
    }
    if (actionId === "calendar") {
      const start = new Date(Date.now() + 60 * 60 * 1000);
      const end = new Date(start.getTime() + 30 * 60 * 1000);
      const pad = (value) => String(value).padStart(2, "0");
      const fmt = (value) =>
        `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())} ${pad(value.getHours())}:${pad(value.getMinutes())}`;
      beginCompose({
        subject: `Meeting invite - ${fmt(start)}`,
        text:
          `Event title: \n` +
          `Date: ${fmt(start)} to ${fmt(end)}\n` +
          `Location: \n` +
          `Attendees: \n\n` +
          `Agenda:\n- \n\n` +
          `Reply to confirm attendance.`,
        html: ""
      });
      pushNotification("Calendar invite draft opened.");
      return;
    }
    if (actionId === "contact") {
      const identifier = window.prompt("Enter contact email or username:");
      if (!identifier) return;
      runAction("newContactChat", async () => {
        await startChatWithIdentifier(identifier);
        pushNotification("Contact chat opened.");
      });
    }
  };

  const handleSettingsMenuAction = (actionId) => {
    setShowSettingsMenu(false);
    if (actionId === "platform") {
      setActivePage("platform");
      return;
    }
    if (actionId === "blocked") {
      setActivePage("blocked");
      return;
    }
    if (actionId === "upgrade") {
      setActivePage("upgrade");
      setSettingsCategory("billing");
      loadBilling().catch(() => {});
      return;
    }
    if (actionId === "settings") {
      setActivePage("settings");
    }
  };

  const signOut = async () => {
    try {
      await api.post("/api/auth/logout");
    } catch (_error) {
      // Even if backend logout fails, we still clear local session state.
    }
    setLoggedIn(false);
    setCurrentUser(null);
    setActivePage("mail");
    setSelectedMessage(null);
    setThreadMessages([]);
    setMessages([]);
    setFolders([]);
    setAccounts([]);
    setShowSettingsMenu(false);
    setShowNewMenu(false);
    setError("");
    setAuthFlow("intro");
    try {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch (_error) {
      // ignore storage failures
    }
  };

  const selectedAccountForMessage = selectedMessage?.account_id || activeAccountId;
  const selectedProviderMessageId = selectedMessage?.provider_message_id;

  const actionReply = () => {
    if (!selectedMessage || selectedMessage.source_type === "social") return;
    const toAddress = selectedMessage.from_email ? [selectedMessage.from_email] : [];
    const subject = selectedMessage.subject?.toLowerCase().startsWith("re:")
      ? selectedMessage.subject
      : `Re: ${selectedMessage.subject || ""}`.trim();
    beginCompose({
      to: toAddress,
      subject,
      inReplyTo: selectedMessage.internet_message_id || ""
    });
    loadReplySuggestions().catch(() => {});
  };

  const actionReplyAll = () => {
    if (!selectedMessage || selectedMessage.source_type === "social") return;
    const threadParticipants = threadMessages
      .map((message) => message.from_email)
      .filter(Boolean);
    const to = Array.from(new Set(threadParticipants)).slice(0, 10);
    const subject = selectedMessage.subject?.toLowerCase().startsWith("re:")
      ? selectedMessage.subject
      : `Re: ${selectedMessage.subject || ""}`.trim();
    beginCompose({
      to,
      subject,
      inReplyTo: selectedMessage.internet_message_id || ""
    });
    loadReplySuggestions().catch(() => {});
  };

  const actionForward = () => {
    if (!selectedMessage || selectedMessage.source_type === "social") return;
    const subject = selectedMessage.subject?.toLowerCase().startsWith("fwd:")
      ? selectedMessage.subject
      : `Fwd: ${selectedMessage.subject || ""}`.trim();
    beginCompose({
      subject,
      text: `\n\n--- Forwarded message ---\n${selectedMessage.snippet || ""}`,
      cc: [],
      bcc: []
    });
    setReplySuggestions([]);
    setReplySuggestionsSource("");
  };

  const actionArchive = async () => {
    if (!selectedProviderMessageId || !selectedAccountForMessage) return;
    await optimisticFlagUpdate(selectedAccountForMessage, selectedProviderMessageId, { archived: true });
  };

  const actionDelete = async () => {
    if (!selectedProviderMessageId || !selectedAccountForMessage) return;
    await optimisticDelete(selectedAccountForMessage, selectedProviderMessageId);
    setSelectedMessage(null);
  };

  const actionToggleRead = async () => {
    if (!selectedProviderMessageId || !selectedAccountForMessage) return;
    const nextRead = !Boolean(selectedMessage?.is_read);
    await optimisticFlagUpdate(selectedAccountForMessage, selectedProviderMessageId, { read: nextRead });
    setSelectedMessage((prev) => (prev ? { ...prev, is_read: nextRead ? 1 : 0 } : prev));
  };

  const actionToggleStar = async () => {
    if (!selectedProviderMessageId || !selectedAccountForMessage) return;
    const nextStar = !Boolean(selectedMessage?.is_starred);
    await optimisticFlagUpdate(selectedAccountForMessage, selectedProviderMessageId, { starred: nextStar });
    setSelectedMessage((prev) => (prev ? { ...prev, is_starred: nextStar ? 1 : 0 } : prev));
  };

  const refreshAll = async () => {
    await Promise.all([loadAccounts(), loadFolders(), loadMessages(), loadSocialAccounts(), loadSocialMessages()]);
    pushNotification("Refreshed mailbox and social feeds");
  };

  const hasSelectedMailMessage =
    Boolean(selectedMessage?.provider_message_id) && selectedMessage?.source_type !== "social";

  const saveDraft = async (composeSnapshot = null) => {
    if (!activeAccountId) return;
    const box = composeSnapshot || compose;
    const payload = normalizeComposePayload(box, { requireTo: false });
    if (!draftId) {
      const created = await api.post(`/api/mail/${activeAccountId}/drafts`, payload);
      setDraftId(created.data.draft.id);
      return created.data.draft;
    }
    const updated = await api.put(`/api/mail/${activeAccountId}/drafts/${draftId}`, payload);
    return updated.data.draft;
  };

  const sendCompose = async () => {
    if (!activeAccountId) return;
    try {
      const editorSnap = syncComposeFromEditor();
      const snapshot = editorSnap ? { ...compose, ...editorSnap } : compose;
      normalizeComposePayload(snapshot, { requireTo: true });
      const saved = await saveDraft(snapshot);
      const queued = await api.post(`/api/mail/${activeAccountId}/drafts/${saved.id}/send`);
      const sendStatus = String(queued.data?.status || "").toLowerCase();
      const sendErr = String(queued.data?.error || "").trim();
      if (sendStatus === "failed") {
        const msg =
          sendErr ||
          "Mail could not be sent. Check SMTP (same app password as IMAP for Gmail/Outlook), or reconnect the account.";
        setError(msg);
        pushNotification(msg);
        return;
      }
      setOutboundStatus({
        outboundId: queued.data.outboundId,
        status: sendStatus || "sent"
      });
      pushNotification(sendStatus === "sent" ? "Message sent." : "Send queued.");
      setCompose(defaultCompose);
      setDraftId("");
    } catch (sendError) {
      const msg = friendlyErrorMessage(sendError, "Failed to send message.");
      setError(msg);
      pushNotification(msg);
    }
  };

  const refreshOutbound = useCallback(async () => {
    if (!outboundStatus?.outboundId || !activeAccountId) return;
    const response = await api.get(`/api/mail/${activeAccountId}/outbound/${outboundStatus.outboundId}`);
    setOutboundStatus(response.data.outbound);
  }, [outboundStatus?.outboundId, activeAccountId]);

  const loadBilling = useCallback(async () => {
    setBillingError("");
    setBillingLoading(true);
    try {
      const [plansRes, meRes] = await Promise.all([
        api.get("/api/billing/plans").catch((e) => ({ __error: e })),
        api.get("/api/billing/me").catch((e) => ({ __error: e }))
      ]);
      if (plansRes?.__error) throw plansRes.__error;
      if (meRes?.__error) throw meRes.__error;
      setBillingPlans(Array.isArray(plansRes.data?.plans) ? plansRes.data.plans : []);
      setBillingMe(meRes.data?.billing || null);
    } catch (error) {
      setBillingPlans([]);
      setBillingMe(null);
      setBillingError(
        friendlyErrorMessage(
          error,
          "Billing isn’t configured on this API yet. If you just switched to the Laravel backend, point the UI proxy at it and try again."
        )
      );
    } finally {
      setBillingLoading(false);
    }
  }, [friendlyErrorMessage]);

  const startCheckout = useCallback(
    async (provider, planKey) => {
      const p = String(provider || "").toLowerCase();
      const key = String(planKey || "").toLowerCase();
      if (!p || !key) return;
      setBillingCheckoutBusy(`${p}:${key}`);
      setBillingError("");
      try {
        const res = await api.post(`/api/billing/checkout/${encodeURIComponent(p)}`, {
          planKey: key,
          successUrl: typeof window !== "undefined" ? `${window.location.origin}/?billing=success` : "",
          cancelUrl: typeof window !== "undefined" ? `${window.location.origin}/?billing=cancel` : ""
        });
        const checkout = res.data?.checkout;
        if (checkout?.checkoutUrl && typeof window !== "undefined") {
          window.location.assign(checkout.checkoutUrl);
          return;
        }
        pushNotification(
          checkout?.providerReference
            ? `Checkout created: ${checkout.providerReference}`
            : "Checkout created. Complete payment in your provider dashboard."
        );
      } catch (error) {
        setBillingError(friendlyErrorMessage(error, "Unable to start checkout right now."));
      } finally {
        setBillingCheckoutBusy("");
      }
    },
    [friendlyErrorMessage, pushNotification]
  );

  const isLimitOrQuotaError = useCallback((apiError) => {
    const status = Number(apiError?.response?.status || 0);
    const message = String(apiError?.response?.data?.message || apiError?.response?.data?.error || apiError?.message || "");
    if ([402, 403, 409, 413, 422].includes(status)) {
      if (/limit|quota|storage|upgrade|plan|subscription|too\s+many|max/i.test(message)) return true;
    }
    return /storage\s*limit|max\s*(email|social)|upgrade\s+to|quota/i.test(message);
  }, []);

  useEffect(() => {
    loadAccounts().catch(() => {});
  }, [loadAccounts]);

  useEffect(() => {
    const onExpired = () => {
      setLoggedIn(false);
      setCurrentUser(null);
      setAuthFlow("login");
    };
    window.addEventListener("netluq:auth-expired", onExpired);
    return () => window.removeEventListener("netluq:auth-expired", onExpired);
  }, []);

  useEffect(() => {
    const syncFromStorage = () => {
      try {
        const nextUser = readStoredUser();
        if (nextUser) {
          setCurrentUser(nextUser);
          setLoggedIn(true);
          return;
        }
        setCurrentUser(null);
        setLoggedIn(false);
      } catch (_e) {
        // ignore
      }
    };
    const onStorage = (e) => {
      if (!e || e.key === AUTH_STORAGE_KEY || e.type === "storage") {
        syncFromStorage();
      }
    };
    const onUpdated = () => syncFromStorage();
    window.addEventListener("storage", onStorage);
    window.addEventListener("netluq:auth-updated", onUpdated);
    try {
      window.__netluqAuthSyncReady = true;
    } catch (_e) {}
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("netluq:auth-updated", onUpdated);
    };
  }, []);

  useEffect(() => {
    if (!loggedIn) return;
    loadSocialAccounts().catch(() => {});
  }, [loadSocialAccounts, loggedIn]);

  useEffect(() => {
    if (!loggedIn) return;
    loadSocialOauthProviders().catch(() => {});
  }, [loadSocialOauthProviders, loggedIn]);

  useEffect(() => {
    if (activePage !== "blocked") return;
    loadBlockedSenders().catch(() => {});
  }, [activePage, loadBlockedSenders]);

  useEffect(() => {
    if (activePage !== "platform") return;
    loadPlatformAdmin().catch(() => {});
  }, [activePage, loadPlatformAdmin]);

  useEffect(() => {
    if (authBooting || !loggedIn) return;
    if (activePage === "platform" && String(currentUser?.role || "").toLowerCase() !== "super_admin") {
      setActivePage("mail");
    }
  }, [authBooting, loggedIn, activePage, currentUser?.role]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.pathname === "/reset-password") {
      setAuthFlow("forgot");
      const tokenFromUrl = new URLSearchParams(window.location.search).get("token") || "";
      if (tokenFromUrl) setForgotResetToken(tokenFromUrl);
    }
  }, []);

  useEffect(() => {
    loadEmailOauthProviders().catch(() => {});
  }, [loadEmailOauthProviders]);

  useEffect(() => {
    loadFolders().catch(() => {});
  }, [loadFolders]);

  useEffect(() => {
    loadMessages().catch(() => {});
  }, [loadMessages]);

  useEffect(() => {
    if (!selectedMessage?.provider_message_id) return;
    const isVisibleInCurrentScope = messages.some(
      (message) => message.provider_message_id === selectedMessage.provider_message_id
    );
    if (!isVisibleInCurrentScope) {
      setSelectedMessage(null);
      setThreadMessages([]);
    }
  }, [messages, selectedMessage]);

  useEffect(() => {
    // Social/settings errors should not linger after leaving that area.
    if (activePage !== "settings" && activePage !== "blocked" && activePage !== "platform" && error) {
      setError("");
    }
  }, [activePage, error]);

  useEffect(() => {
    try {
      window.localStorage.setItem(ACTIVE_PAGE_STORAGE_KEY, activePage);
    } catch (_error) {
      // ignore storage failures
    }
  }, [activePage]);

  useEffect(() => {
    try {
      window.localStorage.setItem(INBOX_VIEW_MODE_STORAGE_KEY, inboxViewMode);
    } catch (_error) {
      // ignore storage failures
    }
  }, [inboxViewMode]);

  useEffect(() => {
    try {
      window.localStorage.setItem(INBOX_DENSITY_STORAGE_KEY, inboxDensityMode);
    } catch (_error) {
      // ignore storage failures
    }
  }, [inboxDensityMode]);

  useEffect(() => {
    if (!showNewMenu) return undefined;
    const onPointerDown = (event) => {
      if (!newMenuRef.current?.contains(event.target)) {
        setShowNewMenu(false);
      }
    };
    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, [showNewMenu]);

  useEffect(() => {
    if (!showSettingsMenu) return undefined;
    const onPointerDown = (event) => {
      if (!settingsMenuRef.current?.contains(event.target)) {
        setShowSettingsMenu(false);
      }
    };
    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, [showSettingsMenu]);

  useEffect(() => {
    if (!showComposeEmojiPicker) return undefined;
    const onPointerDown = (event) => {
      if (!composeEmojiPickerRef.current?.contains(event.target)) {
        setShowComposeEmojiPicker(false);
      }
    };
    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, [showComposeEmojiPicker]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem("outlook_predefined_templates");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setPredefinedTemplateCatalog(parsed.map((item) => ({ ...buildTemplateDraft(item), isPredefined: true })));
      }
    } catch (_error) {
      // ignore invalid local storage payload
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem("outlook_predefined_templates", JSON.stringify(predefinedTemplateCatalog || []));
    } catch (_error) {
      // ignore storage failures
    }
  }, [predefinedTemplateCatalog]);

  useEffect(() => {
    try {
      const personalRaw = window.localStorage.getItem("outlook_personal_media_assets");
      const sharedRaw = window.localStorage.getItem("outlook_shared_media_assets");
      if (personalRaw) {
        const parsed = JSON.parse(personalRaw);
        if (Array.isArray(parsed)) setPersonalMediaLibrary(cleanupMediaAssets(parsed));
      }
      if (sharedRaw) {
        const parsed = JSON.parse(sharedRaw);
        if (Array.isArray(parsed)) setSharedMediaLibrary(cleanupMediaAssets(parsed));
      }
    } catch (_error) {
      // ignore storage failures
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem("outlook_personal_media_assets", JSON.stringify(cleanupMediaAssets(personalMediaLibrary || [])));
    } catch (_error) {
      // ignore storage failures
    }
  }, [personalMediaLibrary]);

  useEffect(() => {
    try {
      window.localStorage.setItem("outlook_shared_media_assets", JSON.stringify(cleanupMediaAssets(sharedMediaLibrary || [])));
    } catch (_error) {
      // ignore storage failures
    }
  }, [sharedMediaLibrary]);

  useEffect(() => {
    const timer = setInterval(() => {
      setPersonalMediaLibrary((prev) => cleanupMediaAssets(prev || []));
      setSharedMediaLibrary((prev) => cleanupMediaAssets(prev || []));
    }, 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!templateEditorOpen) return;
    const draftBlocks = Array.isArray(templateDraft.blocks) ? templateDraft.blocks : [];
    if (!draftBlocks.length) {
      setSelectedTemplateBlockId("");
      return;
    }
    if (!selectedTemplateBlockId || !draftBlocks.some((block) => block.id === selectedTemplateBlockId)) {
      setSelectedTemplateBlockId(draftBlocks[0].id);
    }
  }, [templateEditorOpen, templateDraft.blocks, selectedTemplateBlockId]);

  useEffect(() => {
    if (!templateEditorOpen) return;
    if (templateHistoryRestoreRef.current) {
      templateHistoryRestoreRef.current = false;
      return;
    }
    setTemplateHistory((prev) => {
      const current = prev[templateHistoryIndex];
      const nextSerialized = JSON.stringify(templateDraft);
      const currentSerialized = current ? JSON.stringify(current) : "";
      if (nextSerialized === currentSerialized) return prev;
      const trimmed = templateHistoryIndex >= 0 ? prev.slice(0, templateHistoryIndex + 1) : [];
      return [...trimmed, templateDraft];
    });
    setTemplateHistoryIndex((prev) => {
      const current = templateHistory[prev];
      const nextSerialized = JSON.stringify(templateDraft);
      const currentSerialized = current ? JSON.stringify(current) : "";
      if (nextSerialized === currentSerialized) return prev;
      return prev + 1;
    });
  }, [templateDraft, templateEditorOpen, templateHistory, templateHistoryIndex]);

  useEffect(() => {
    if (!templateEditorOpen) return undefined;
    const onKeyDown = (event) => {
      const isMeta = event.metaKey || event.ctrlKey;
      if (!isMeta) return;
      const key = String(event.key || "").toLowerCase();
      if (key === "z" && event.shiftKey) {
        event.preventDefault();
        redoTemplateChange();
        return;
      }
      if (key === "z") {
        event.preventDefault();
        undoTemplateChange();
        return;
      }
      if (key === "d") {
        if (!selectedTemplateBlockId) return;
        event.preventDefault();
        duplicateTemplateBlock(selectedTemplateBlockId);
        return;
      }
      if (event.key === "ArrowUp") {
        if (!selectedTemplateBlockId) return;
        event.preventDefault();
        moveTemplateBlock(selectedTemplateBlockId, "up");
        return;
      }
      if (event.key === "ArrowDown") {
        if (!selectedTemplateBlockId) return;
        event.preventDefault();
        moveTemplateBlock(selectedTemplateBlockId, "down");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [templateEditorOpen, selectedTemplateBlockId, templateHistory, templateHistoryIndex, undoTemplateChange, redoTemplateChange]);

  useEffect(() => {
    if (!loggedIn || activePage !== "mail") return undefined;
    const onKeyDown = (event) => {
      const tag = String(event.target?.tagName || "").toLowerCase();
      const isTyping =
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        Boolean(event.target?.isContentEditable);
      if (isTyping || event.metaKey || event.ctrlKey || event.altKey) return;
      const key = String(event.key || "").toLowerCase();
      if (key === "g") {
        setInboxViewMode("grouped");
      } else if (key === "c") {
        setInboxViewMode("chronological");
      } else if (key === "d") {
        setInboxDensityMode((prev) => (prev === "compact" ? "comfortable" : "compact"));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [loggedIn, activePage]);

  useEffect(() => {
    if (!loggedIn) return;
    loadSocialMessages().catch(() => {});
  }, [loadSocialMessages, loggedIn]);

  useEffect(() => {
    if (!loggedIn) return;
    loadChatConversations().catch(() => {});
  }, [loadChatConversations, loggedIn]);

  useEffect(() => {
    if (!loggedIn) return;
    loadChatContacts().catch(() => {});
  }, [loadChatContacts, loggedIn]);

  useEffect(() => {
    if (!loggedIn) return;
    loadConversationMessages().catch(() => {});
  }, [loadConversationMessages, loggedIn]);

  useEffect(() => {
    if (!chatOpen || !activeConversationId) return;
    setChatUnreadByConversation((prev) => {
      if (!prev[activeConversationId]) return prev;
      const next = { ...prev };
      delete next[activeConversationId];
      return next;
    });
  }, [chatOpen, activeConversationId]);

  useEffect(() => {
    if (chatOpen) setChatMobileShowList(true);
  }, [chatOpen]);

  useEffect(() => {
    if (!chatOpen) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (chatWidgetRef.current?.contains(target)) return;
      if (chatToggleRef.current?.contains(target)) return;
      setChatOpen(false);
    };
    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, [chatOpen]);

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    setDesktopNotifEnabled(Notification.permission === "granted");
  }, []);

  useEffect(() => {
    if (!outboundStatus?.outboundId) return undefined;
    const timer = setInterval(refreshOutbound, 2500);
    return () => clearInterval(timer);
  }, [outboundStatus?.outboundId, refreshOutbound]);

  useEffect(() => {
    if (!loggedIn || !isWebSocketEnabled()) return undefined;
    const socket = new WebSocket(getWebSocketUrl());
    socket.onerror = () => {
      // Ignore realtime socket transport errors in degraded mode.
    };
    socket.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        if (parsed?.type === "mail.sync.completed") {
          if (!activeAccountId || parsed.payload?.accountId === activeAccountId) {
            loadFolders().catch(() => {});
            loadMessages().catch(() => {});
            pushNotification(`Sync completed for ${parsed.payload?.folderId || "folder"}`);
          }
        }
        if (parsed?.type === "mail.message.updated") {
          if (!activeAccountId || parsed.payload?.accountId === activeAccountId) {
            loadMessages().catch(() => {});
            loadFolders().catch(() => {});
            pushNotification(`Message ${parsed.payload?.action || "updated"}`);
          }
        }
        if (parsed?.type === "mail.ai.enriched") {
          pushNotification(`AI categorized message as ${parsed.payload?.category || "primary"}`);
          loadMessages().catch(() => {});
        }
        if (parsed?.type === "mail.ai.priority") {
          pushNotification(`Priority mail: ${parsed.payload?.summary || "High-priority message"}`);
        }
        if (parsed?.type === "mail.outbound.updated") {
          if (!activeAccountId || parsed.payload?.accountId === activeAccountId) {
            if (parsed.payload?.outboundId) {
              setOutboundStatus((prev) =>
                prev && (prev.outboundId === parsed.payload.outboundId || prev.id === parsed.payload.outboundId)
                  ? { ...prev, status: parsed.payload.status }
                  : prev
              );
            }
            pushNotification(`Outbound mail ${parsed.payload?.status || "updated"}`);
          }
        }
        if (parsed?.type === "social.message.received") {
          loadSocialMessages().catch(() => {});
          loadMessages().catch(() => {});
          pushNotification(`New ${parsed.payload?.kind || "social"} on ${parsed.payload?.platform}`);
        }
        if (parsed?.type === "social.reply.updated") {
          if (parsed.payload?.replyId && parsed.payload.replyId === socialReply.replyId) {
            setSocialReply((prev) => ({ ...prev, status: parsed.payload.status || prev.status }));
          }
          pushNotification(`Social reply ${parsed.payload?.status || "updated"}`);
        }
        if (parsed?.type === "chat.message.created") {
          const conversationId = parsed.payload?.conversationId;
          const senderUserId = parsed.payload?.senderUserId;
          const body = String(parsed.payload?.body || "").trim();
          if (senderUserId && senderUserId !== currentUser?.id) {
            pushNotification(`New chat message: ${body || "Message received"}`);
            playChatPing();
            if (desktopNotifEnabled && typeof window !== "undefined" && "Notification" in window) {
              try {
                new Notification("New chat message", {
                  body: body || "You received a new message."
                });
              } catch (_notifyError) {
                // Ignore browser notification failures.
              }
            }
          }
          if (conversationId) {
            const shouldMarkUnread =
              senderUserId &&
              senderUserId !== currentUser?.id &&
              (!chatOpen || activeConversationId !== conversationId);
            if (shouldMarkUnread) {
              setChatUnreadByConversation((prev) => ({
                ...prev,
                [conversationId]: Number(prev[conversationId] || 0) + 1
              }));
            }
            if (activeConversationId === conversationId) {
              loadConversationMessages().catch(() => {});
            }
            loadChatConversations().catch(() => {});
          }
        }
      } catch (_error) {
        // Ignore malformed realtime payloads.
      }
    };
    return () => socket.close();
  }, [loggedIn, activeAccountId, activeConversationId, chatOpen, currentUser?.id, desktopNotifEnabled, loadChatConversations, loadConversationMessages, loadFolders, loadMessages, playChatPing, pushNotification, loadSocialMessages, socialReply.replyId]);

  const socialSummary = useMemo(() => {
    const byPlatform = new Map();
    for (const account of socialAccounts) {
      const current = byPlatform.get(account.platform) || 0;
      byPlatform.set(account.platform, current + 1);
    }
    return socialPlatformMeta.map((platform) => ({
      ...platform,
      connected: byPlatform.get(platform.id) || 0
    }));
  }, [socialAccounts]);

  const activeSocialPlatformId = useMemo(() => {
    const raw = socialAccounts.find((acc) => acc.id === activeSocialAccountId)?.platform || "social";
    return String(raw).toLowerCase().replace("twitter", "x");
  }, [socialAccounts, activeSocialAccountId]);

  const socialContacts = useMemo(() => {
    const map = new Map();
    for (const message of socialMessages || []) {
      const key = message.authorHandle || message.authorName || message.id;
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          name: message.authorName || message.authorHandle || "Unknown contact",
          handle: message.authorHandle || "",
          platform: activeSocialPlatformId,
          latestText: message.text || "",
          latestAt: message.receivedAt || "",
          messages: []
        });
      }
      map.get(key).messages.push(message);
      if ((message.receivedAt || "") > (map.get(key).latestAt || "")) {
        map.get(key).latestText = message.text || "";
        map.get(key).latestAt = message.receivedAt || "";
      }
    }
    return Array.from(map.values())
      .sort((a, b) => String(b.latestAt).localeCompare(String(a.latestAt)))
      .map((contact, index) => ({
        ...contact,
        company: socialCompanyCycle[index % socialCompanyCycle.length],
        statusMeta: socialStatusCycle[index % socialStatusCycle.length]
      }));
  }, [socialMessages, activeSocialPlatformId]);

  useEffect(() => {
    if (socialContacts.length === 0) {
      setSelectedSocialContactId("");
      return;
    }
    if (!selectedSocialContactId || !socialContacts.some((contact) => contact.id === selectedSocialContactId)) {
      setSelectedSocialContactId(socialContacts[0].id);
    }
  }, [socialContacts, selectedSocialContactId]);

  useEffect(() => {
    if (activePage !== "social") return;
    if (!socialReply.socialMessageId) {
      setSocialReplySuggestions([]);
      setSocialReplySuggestionsSource("");
      return;
    }
    loadSocialReplySuggestions(socialReply.socialMessageId).catch(() => {
      setSocialReplySuggestions([]);
      setSocialReplySuggestionsSource("");
    });
  }, [activePage, socialReply.socialMessageId, loadSocialReplySuggestions]);

  const activeSocialContact = socialContacts.find((contact) => contact.id === selectedSocialContactId) || socialContacts[0] || null;
  const activeSocialConversation = activeSocialContact?.messages || [];
  const activeChatConversation = useMemo(
    () => chatConversations.find((conversation) => conversation.id === activeConversationId) || null,
    [chatConversations, activeConversationId]
  );
  const filteredChatContacts = useMemo(() => {
    const query = String(chatIdentifier || "").trim().toLowerCase();
    if (!query) return chatContacts.slice(0, 8);
    return chatContacts
      .filter((contact) => {
        const label = `${contact.displayName || ""} ${contact.username || ""} ${contact.email || ""}`.toLowerCase();
        return label.includes(query);
      })
      .slice(0, 8);
  }, [chatContacts, chatIdentifier]);
  const chatUnreadCount = useMemo(
    () => Object.values(chatUnreadByConversation).reduce((sum, count) => sum + Number(count || 0), 0),
    [chatUnreadByConversation]
  );
  const chatPresenceLabel = useMemo(() => {
    const lastAt = activeChatConversation?.lastMessageAt;
    if (!lastAt) return "No activity yet";
    const diffMs = Date.now() - new Date(lastAt).getTime();
    if (diffMs <= 2 * 60 * 1000) return "Active now";
    const minutes = Math.max(1, Math.floor(diffMs / 60000));
    if (minutes < 60) return `Last seen ${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Last seen ${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `Last seen ${days}d ago`;
  }, [activeChatConversation?.lastMessageAt]);
  const visibleTemplateCatalog = useMemo(() => {
    const merged = [...(predefinedTemplateCatalog || []), ...(templateCatalog || [])];
    const seen = new Set();
    return merged.filter((item) => {
      const key = String(item.id || "");
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [predefinedTemplateCatalog, templateCatalog]);

  const filteredTemplates = useMemo(() => {
    const q = String(templateSearch || "").trim().toLowerCase();
    const selectedCategory = String(templateCategoryFilter || "Emails").toLowerCase();
    return visibleTemplateCatalog.filter((template) => {
      const haystack = `${template.name} ${template.category} ${template.subject} ${template.body}`.toLowerCase();
      const searchOk = !q || haystack.includes(q);
      const categoryOk =
        selectedCategory === "emails" ||
        haystack.includes(selectedCategory) ||
        String(template.category || "").toLowerCase().includes(selectedCategory);
      return searchOk && categoryOk;
    });
  }, [templateSearch, visibleTemplateCatalog, templateCategoryFilter]);
  const templateBlocks = templateDraft.blocks || [];
  const selectedTemplateBlock = templateBlocks.find((block) => block.id === selectedTemplateBlockId) || templateBlocks[0] || null;
  const availableMediaAssets = useMemo(() => cleanupMediaAssets([...(sharedMediaLibrary || []), ...(personalMediaLibrary || [])]), [sharedMediaLibrary, personalMediaLibrary]);
  const mediaAssetsForSelectedBlock = useMemo(() => {
    if (!selectedTemplateBlock || !["image", "audio", "video"].includes(selectedTemplateBlock.type)) return availableMediaAssets;
    return availableMediaAssets.filter((item) => item.type === selectedTemplateBlock.type);
  }, [availableMediaAssets, selectedTemplateBlock]);
  const filteredManagerAssets = useMemo(() => {
    const q = String(assetsManagerSearch || "").trim().toLowerCase();
    return availableMediaAssets.filter((asset) => {
      const typeOk = assetsManagerTypeFilter === "all" || asset.type === assetsManagerTypeFilter;
      const searchOk = !q || `${asset.name} ${asset.type}`.toLowerCase().includes(q);
      return typeOk && searchOk;
    });
  }, [availableMediaAssets, assetsManagerSearch, assetsManagerTypeFilter]);
  const templatePreviewWidthClass =
    templatePreviewDevice === "desktop" ? "w-full max-w-full" : templatePreviewDevice === "tablet" ? "max-w-[520px]" : "max-w-[360px]";
  const templateEditorFullBleed = !isMobile && activePage === "templates" && templateEditorOpen;
  const templatePreviewMediaHeightClass =
    templatePreviewDevice === "desktop" ? "h-[220px]" : templatePreviewDevice === "tablet" ? "h-[180px]" : "h-[140px]";
  const socialRailItems = useMemo(
    () =>
      ["linkedin", "facebook", "instagram", "x", "youtube", "tiktok", "whatsapp", "telegram", "messenger"].map((platformId) => {
        const pid = String(platformId).toLowerCase();
        const account =
          socialAccounts.find((item) => String(item.platform || "").toLowerCase() === pid) || null;
        return {
          id: platformId,
          accountId: account?.id || "",
          connected: Boolean(account),
          ...(socialPlatformBadge[platformId] || { label: platformId.slice(0, 2).toUpperCase(), color: "bg-slate-400" })
        };
      }),
    [socialAccounts]
  );
  const selectedSocialConnection = useMemo(
    () => socialAccounts.find((account) => account.platform === settingsProvider) || null,
    [socialAccounts, settingsProvider]
  );
  const normalizedSettingsEmailProvider = mapSettingsProviderToAccountProvider(settingsProvider);
  const selectedEmailConnections = useMemo(
    () => accounts.filter((account) => account.provider === normalizedSettingsEmailProvider),
    [accounts, normalizedSettingsEmailProvider]
  );
  const emailConnectionConnected = selectedEmailConnections.length > 0;

  const executeSettingsAction = async (item) => {
    const action = String(item || "").toLowerCase();
    if (isSocialProvider(settingsProvider)) {
      if (action.includes("connect")) {
        await connectSocialAccount();
        return;
      }
      if (action.includes("sync")) {
        if (!selectedSocialConnection) throw new Error(`Connect ${settingsProvider} before syncing.`);
        setActiveSocialAccountId(selectedSocialConnection.id);
        await syncSocialMessages();
        return;
      }
      if (action.includes("oauth")) {
        if (action.includes("complete")) {
          setSocialOauth((prev) => ({ ...prev, platform: settingsProvider }));
          await completeSocialOauth();
          return;
        }
        setSocialOauth((prev) => ({ ...prev, platform: settingsProvider }));
        await startSocialOauth();
        return;
      }
      if (action.includes("test") || action.includes("message")) {
        setActivePage("social");
        pushNotification(`Open ${settingsProvider} inbox and send a reply to test.`);
        return;
      }
      return;
    }

    if (action.includes("connect")) {
      if (emailOauthSupportedProviders.includes(normalizedSettingsEmailProvider)) {
        setEmailOauth((prev) => ({ ...prev, provider: normalizedSettingsEmailProvider }));
        if (emailOauth.code && emailOauth.state) {
          await completeEmailOauth();
        } else {
          await startEmailOauth();
        }
        return;
      }
      await quickConnectAccount();
      return;
    }
    if (action.includes("sync")) {
      await Promise.all([loadAccounts(), loadFolders(), loadMessages()]);
      pushNotification("Email account sync requested.");
      return;
    }
    if (action.includes("test") || action.includes("message")) {
      beginCompose({
        to: connectForm.emailAddress ? [connectForm.emailAddress] : [],
        subject: "Connection Test",
        text: `Connection test for ${settingsProvider} integration.`
      });
      return;
    }
    if (action.includes("oauth")) {
      setEmailOauth((prev) => ({ ...prev, provider: normalizedSettingsEmailProvider }));
      if (action.includes("complete")) {
        await completeEmailOauth();
        return;
      }
      await startEmailOauth();
    }
  };

  if (authBooting) {
    return (
      <main className="min-h-screen bg-pearl-50 flex items-center justify-center p-6 text-slateLux-900">
        <div className="w-[min(520px,96vw)] bg-white border border-pearl-200 rounded-xl3 shadow-luxSoft p-8">
          <div className="text-center text-[42px]">⌛</div>
          <h2 className="text-[34px] leading-none font-semibold mt-4 text-slateLux-900">Opening your workspace</h2>
          <p className="mt-3 text-sm text-slateLux-500">Restoring your session…</p>
          <div className="mt-6">
            <SkeletonList rows={6} />
          </div>
        </div>
      </main>
    );
  }

  if (!loggedIn) {
    return (
      <main className="min-h-screen bg-pearl-50 flex items-center justify-center p-6 text-slateLux-900">
        {authFlow === "intro" ? (
          <div className="w-[min(1180px,96vw)] h-[min(740px,92vh)] bg-white border border-pearl-200 rounded-xl3 shadow-luxSoft overflow-hidden grid grid-cols-[56%_44%]">
            <section className="relative bg-gradient-to-br from-accent-600 via-accent-600 to-accent-700 text-white p-10">
              <div className="absolute inset-0 opacity-15 [background-image:radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]" />
              <div className="relative">
                <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-white/75">
                  NetluQ
                </p>
                <h1 className="mt-10 flex max-w-[min(28rem,92%)] flex-col gap-2 text-[clamp(2.125rem,3.2vw+1.25rem,3.5rem)] font-semibold leading-[1.06] tracking-[-0.035em] text-balance text-white [text-shadow:0_1px_28px_rgba(0,0,0,0.12)]">
                  <span className="block">One inbox.</span>
                  <span className="block">Every account.</span>
                  <span className="block">Zero chaos.</span>
                </h1>
                <p className="mt-9 max-w-[min(28rem,92%)] text-[17px] leading-[1.65] tracking-[0.01em] text-white/88 text-balance">
                  Tired of tab-hopping and lost threads? NetluQ brings your email into one calm home—so you
                  see what needs you, reply with context, and get back to real work.
                </p>
              </div>
            </section>
            <section className="p-10 flex flex-col justify-center">
              <div className="text-[48px] leading-none mb-10">🐦</div>
              <h2 className="text-[52px] leading-[1.04] font-semibold text-slateLux-900">Join NetluQ</h2>
              <div className="mt-8 space-y-3">
                <button className="w-full h-11 rounded-full bg-accent-600 text-white text-sm font-semibold hover:bg-accent-700 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/35" onClick={() => setAuthFlow("register")}>
                  Sign up with phone or email
                </button>
              </div>
              <div className="mt-6 text-sm text-slateLux-500">
                Already have a NetluQ account?{" "}
                <button className="text-accent-600 hover:underline" onClick={() => setAuthFlow("login")}>
                  Log in
                </button>
              </div>
              <div className="mt-6 text-xs text-slateLux-500">
                Trusted by 5,000+ forward-thinking teams globally.
              </div>
            </section>
          </div>
        ) : authFlow === "login" ? (
          <div className="w-[min(520px,96vw)] bg-white border border-pearl-200 rounded-xl3 shadow-luxSoft p-8">
            <div className="text-center text-[42px]">🐦</div>
            <h2 className="text-[46px] leading-none font-semibold mt-4">Log in to NetluQ</h2>
            <div className="mt-7 space-y-4">
              <input
                className="w-full h-14 border border-pearl-200 rounded-xl2 px-4 text-lg bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                placeholder="Phone number, email address"
                value={auth.email}
                onChange={(e) => setAuth((prev) => ({ ...prev, email: e.target.value }))}
              />
              <input
                className="w-full h-14 border border-pearl-200 rounded-xl2 px-4 text-lg bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                placeholder="Password"
                type={showAuthPassword ? "text" : "password"}
                value={auth.password}
                onChange={(e) => setAuth((prev) => ({ ...prev, password: e.target.value }))}
              />
              <button
                type="button"
                className="text-sm text-accent-600 hover:underline"
                onClick={() => setShowAuthPassword((prev) => !prev)}
              >
                {showAuthPassword ? "Hide password" : "Show password"}
              </button>
            </div>
            <button className="mt-6 w-full h-12 rounded-full bg-accent-600 text-white text-lg font-semibold hover:bg-accent-700 transition disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/35" onClick={loginOnly} disabled={loading}>
              {loading ? "Please wait..." : "Log In"}
            </button>
            <div className="mt-5 flex items-center justify-between text-sm">
              <button className="text-accent-600 hover:underline" onClick={() => setAuthFlow("forgot")}>
                Forgot password?
              </button>
              <button className="text-accent-600 hover:underline" onClick={() => setAuthFlow("register")}>
                Sign up to NetluQ
              </button>
            </div>
            <div className="mt-3 text-center text-sm">
              <button className="text-slate-500 hover:underline" onClick={() => setAuthFlow("intro")}>
                Back
              </button>
            </div>
            {error ? <p className="text-red-600 text-sm mt-4">{error}</p> : null}
          </div>
        ) : authFlow === "forgot" ? (
          <div className="w-[min(560px,96vw)] bg-white border border-pearl-200 rounded-xl3 shadow-luxSoft p-8">
            <div className="text-center text-[42px]">🔐</div>
            <h2 className="text-[38px] leading-none font-semibold mt-4">Reset password</h2>
            <p className="mt-3 text-sm text-slateLux-500">
              Request a reset token, then submit token + new password.
            </p>
            <div className="mt-6 space-y-3">
              <input
                className="w-full h-12 border border-pearl-200 rounded-xl2 px-4 text-base bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                placeholder="Email address"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
              />
              <button
                className="w-full h-11 rounded-full bg-accent-600 text-white text-sm font-semibold hover:bg-accent-700 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/35"
                onClick={() => runAction("forgotPassword", requestForgotPassword)}
                disabled={loading || busyAction === "forgotPassword"}
              >
                {busyAction === "forgotPassword" ? "Requesting..." : "Request reset"}
              </button>
              <input
                className="w-full h-12 border border-pearl-200 rounded-xl2 px-4 text-base bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                placeholder="Reset token"
                value={forgotResetToken}
                onChange={(e) => setForgotResetToken(e.target.value)}
              />
              <input
                className="w-full h-12 border border-pearl-200 rounded-xl2 px-4 text-base bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                placeholder="New password (min 10 chars)"
                type={showForgotPassword ? "text" : "password"}
                value={forgotNewPassword}
                onChange={(e) => setForgotNewPassword(e.target.value)}
              />
              <button
                type="button"
                className="text-left text-sm text-accent-600 hover:underline"
                onClick={() => setShowForgotPassword((prev) => !prev)}
              >
                {showForgotPassword ? "Hide password" : "Show password"}
              </button>
              <button
                className="w-full h-11 rounded-full bg-accent-600 text-white text-sm font-semibold hover:bg-accent-700 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/35"
                onClick={() => runAction("resetPassword", submitPasswordReset)}
                disabled={loading || busyAction === "resetPassword"}
              >
                {busyAction === "resetPassword" ? "Resetting..." : "Set new password"}
              </button>
            </div>
            <div className="mt-4 text-center text-sm">
              <button className="text-slate-500 hover:underline" onClick={() => setAuthFlow("login")}>
                Back to login
              </button>
            </div>
            {error ? <p className="text-red-600 text-sm mt-4">{error}</p> : null}
          </div>
        ) : (
          <div className="w-[min(700px,96vw)] bg-white border border-pearl-200 rounded-xl3 shadow-luxSoft p-8">
            <div className="text-center text-[42px]">🐦</div>
            <h2 className="text-[44px] leading-none font-semibold mt-4">Create an account</h2>
            <div className="mt-8 space-y-4">
              <input className="w-full h-14 border border-pearl-200 rounded-xl2 px-4 text-lg bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25" placeholder="Name" value={auth.displayName} onChange={(e) => setAuth((prev) => ({ ...prev, displayName: e.target.value }))} />
              <div className="grid grid-cols-[120px_1fr] gap-3">
                <select
                  className="h-14 border border-pearl-200 rounded-xl2 px-3 text-lg bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                  value={authPhoneCode}
                  onChange={(e) => setAuthPhoneCode(e.target.value)}
                >
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+234">+234</option>
                  <option value="+91">+91</option>
                  <option value="+971">+971</option>
                </select>
                <input
                  className="w-full h-14 border border-pearl-200 rounded-xl2 px-4 text-lg bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                  placeholder="Phone number (required)"
                  value={auth.phoneNumber}
                  onChange={(e) => setAuth((prev) => ({ ...prev, phoneNumber: e.target.value }))}
                />
              </div>
              <input className="w-full h-14 border border-pearl-200 rounded-xl2 px-4 text-lg bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25" placeholder="Email (required)" value={auth.email} onChange={(e) => setAuth((prev) => ({ ...prev, email: e.target.value }))} />
              <input
                className="w-full h-14 border border-pearl-200 rounded-xl2 px-4 text-lg bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                placeholder="Password (min 10 chars)"
                type={showAuthPassword ? "text" : "password"}
                value={auth.password}
                onChange={(e) => setAuth((prev) => ({ ...prev, password: e.target.value }))}
              />
              <button
                type="button"
                className="text-sm text-accent-600 hover:underline text-left"
                onClick={() => setShowAuthPassword((prev) => !prev)}
              >
                {showAuthPassword ? "Hide password" : "Show password"}
              </button>
            </div>
            <div className="mt-6">
              <div className="font-semibold text-[26px]">Date of birth</div>
              <div className="text-sm text-slateLux-500 mt-1">This will not be shown publicly.</div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <input className="h-12 border border-pearl-200 rounded-xl2 px-3 bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25" placeholder="Month" value={auth.birthMonth} onChange={(e) => setAuth((prev) => ({ ...prev, birthMonth: e.target.value }))} />
                <input className="h-12 border border-pearl-200 rounded-xl2 px-3 bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25" placeholder="Day" value={auth.birthDay} onChange={(e) => setAuth((prev) => ({ ...prev, birthDay: e.target.value }))} />
                <input className="h-12 border border-pearl-200 rounded-xl2 px-3 bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25" placeholder="Year" value={auth.birthYear} onChange={(e) => setAuth((prev) => ({ ...prev, birthYear: e.target.value }))} />
              </div>
            </div>
            <button className="mt-8 w-full h-12 rounded-full bg-accent-600 text-white text-lg font-semibold hover:bg-accent-700 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/35 disabled:opacity-60" onClick={registerAndLogin} disabled={loading}>
              {loading ? "Please wait..." : "Next"}
            </button>
            <div className="mt-3 text-center text-sm text-slateLux-500">
              <button className="text-accent-600 hover:underline" onClick={() => setAuthFlow("login")}>
                Already have an account? Log in
              </button>
            </div>
            {error ? <p className="text-red-600 text-sm mt-4">{error}</p> : null}
          </div>
        )}
      </main>
    );
  }

  const selectedAttachments = selectedMessage?.attachments || [];
  const scopedMessages =
    activePage === "social"
      ? messages.filter((message) => message.source_type === "social")
      : messages.filter((message) => message.source_type !== "social");
  const visibleMessages =
    inboxViewMode === "chronological"
      ? [...scopedMessages].sort((a, b) => {
          const left = new Date(a?.received_at || a?.sent_at || 0).getTime();
          const right = new Date(b?.received_at || b?.sent_at || 0).getTime();
          return right - left;
        })
      : scopedMessages;
  const listMessages = mailView === "future" ? futureItems : visibleMessages;
  const folderMap = new Map((folders || []).map((folder) => [String(folder.id || "").toUpperCase(), folder]));
  const starredMessageCount = Number(messages.filter((item) => Boolean(item.is_starred) && item.source_type !== "social").length || 0);
  const quickFolders = [
    { id: "INBOX", label: "Inbox", iconType: "inbox", count: Number(folderMap.get("INBOX")?.unread || 0) },
    { id: "PINNED", label: "Pinned", iconType: "pinned", count: starredMessageCount },
    { id: "SENT", label: "Sent Items", iconType: "sent", count: Number(folderMap.get("SENT")?.total || 0) },
    { id: "DRAFTS", label: "Drafts", iconType: "drafts", count: Number(folderMap.get("DRAFTS")?.total || 0) },
    { id: "ARCHIVE", label: "Archive", iconType: "archive", count: Number(folderMap.get("ARCHIVE")?.total || 0) },
    { id: "JUNK", label: "Junk Email", iconType: "junk", count: Number(folderMap.get("JUNK")?.total || 0) },
    { id: "BLOCKED", label: "Blocked Senders", iconType: "blocked", count: 0 },
    { id: "STARRED", label: "Starred", iconType: "starred", count: starredMessageCount },
    { id: "MANAGE_BLOCKED", label: "Manage Blocked Senders", iconType: "manageBlocked", count: 0 }
  ];

  const getMessageRowTone = (message, index) => {
    const isUnread = !Boolean(message.is_read);
    const isSelected = selectedMessage?.provider_message_id === message.provider_message_id;
    const trust = String(message?.trust_verdict || "");
    const trustTone =
      trust === "verified"
        ? "ring-1 ring-success-600/35"
        : trust === "suspicious"
          ? "grayscale opacity-[0.92]"
          : "";
    if (isSelected && isUnread) return "bg-[#deecf9] border-l-[3px] border-l-[#0078d4]";
    if (isSelected) return "bg-[#c7e0f4]/40 border-l-[3px] border-l-[#0078d4]";
    if (isUnread) return `${index % 2 === 0 ? "bg-[#f3f2f1]" : "bg-white"} ${trustTone}`.trim();
    return `${index % 2 === 0 ? "bg-white" : "bg-[#faf9f8]"} ${trustTone}`.trim();
  };

  const getMessageHoverTone = (message) => {
    if (selectedMessage?.provider_message_id === message.provider_message_id) {
      return "hover:bg-[#deecf9]";
    }
    if (String(message?.trust_verdict || "") === "verified") {
      return "hover:bg-emerald-50";
    }
    return "hover:bg-[#edebe9]";
  };

  const domainFromEmail = (email) => {
    const s = String(email || "");
    const at = s.lastIndexOf("@");
    if (at === -1) return "";
    return s.slice(at + 1).trim().toLowerCase();
  };

  const messageRowHeight = inboxDensityMode === "compact" ? 82 : 112;
  const shouldVirtualizeMessages = listMessages.length > 120 && mailView !== "future";
  const groupedVirtualItems =
    inboxViewMode !== "grouped" || mailView === "future"
      ? []
      : (() => {
          const out = [];
          for (let i = 0; i < listMessages.length; i++) {
            const m = listMessages[i];
            const prev = listMessages[i - 1];
            const boundary = i === 0 || prev?.relationship_group_id !== m.relationship_group_id;
            if (boundary && m.relationship_group_id) {
              out.push({
                type: "header",
                id: `hdr-${m.relationship_group_id}`,
                label: m.relationship_group_label || "Grouped",
                size: Number(m.relationship_group_size || 1),
                groupType: m.relationship_group_type || ""
              });
            }
            out.push({
              type: "message",
              id: `${m.account_id || ""}-${m.provider_message_id || i}`,
              message: m,
              index: i
            });
          }
          return out;
        })();

  // (moved) command bar hooks are declared earlier in component body

  return (
    <main className="min-h-screen bg-[#f3f2f1] text-slateLux-900 antialiased" style={appFont}>
      <InjectSkeletonKeyframes />
      <header className="h-[48px] bg-[#0078d4] text-white grid grid-cols-[1fr_auto_1fr] items-center px-4 border-b border-[#106ebe]">
        <div className="text-[20px] font-semibold tracking-tight leading-none">NetluQ</div>
        <input
          className="w-[380px] md:w-[460px] lg:w-[560px] h-8 rounded-[2px] px-3 text-sm text-[#323130] outline-none ring-0 border border-white/50 bg-white placeholder:text-[#605e5c] focus-visible:border-white focus-visible:ring-2 focus-visible:ring-white/80"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="justify-self-end text-xs opacity-90 flex items-center gap-1">
          <div className="relative" ref={settingsMenuRef}>
            <button
              className="w-7 h-7 inline-flex items-center justify-center hover:bg-white/15 rounded-xl transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/35"
              title="Account settings"
              onClick={() => setShowSettingsMenu((prev) => !prev)}
            >
              ⚙
            </button>
            {showSettingsMenu ? (
              <div className="absolute right-0 top-10 z-30 min-w-[280px] rounded-2xl border border-pearl-200 bg-white text-slateLux-900 shadow-lux overflow-hidden">
                {String(currentUser?.role || "").toLowerCase() === "super_admin" ? (
                  <button
                    className="w-full text-left px-6 py-4 text-[16px] leading-none hover:bg-pearl-50 border-b border-pearl-200 transition font-semibold text-[#0078d4]"
                    onClick={() => handleSettingsMenuAction("platform")}
                  >
                    Platform admin
                  </button>
                ) : null}
                <button
                  className="w-full text-left px-6 py-4 text-[16px] leading-none hover:bg-pearl-50 border-b border-pearl-200 transition"
                  onClick={() => handleSettingsMenuAction("blocked")}
                >
                  Manage Blocked Senders
                </button>
                <button
                  className="w-full text-left px-6 py-4 text-[16px] leading-none hover:bg-pearl-50 border-b border-pearl-200 flex items-center gap-3 transition"
                  onClick={() => handleSettingsMenuAction("upgrade")}
                >
                  <span className="text-[16px] leading-none text-slate-500">⬆</span>
                  <span>Upgrade</span>
                </button>
                <button
                  className="w-full text-left px-6 py-4 text-[16px] leading-none hover:bg-pearl-50 flex items-center gap-3 transition"
                  onClick={() => handleSettingsMenuAction("settings")}
                >
                  <span className="text-[16px] leading-none text-slate-500">⚙</span>
                  <span>Settings</span>
                </button>
                <button
                  className="w-full text-left px-6 py-4 text-[16px] leading-none hover:bg-pearl-50 border-t border-pearl-200 text-danger-600 transition"
                  onClick={signOut}
                >
                  Sign out
                </button>
              </div>
            ) : null}
          </div>
          <button className="w-7 h-7 inline-flex items-center justify-center hover:bg-white/20 rounded" title="Profile">
            👤
          </button>
        </div>
      </header>

      <div className="h-[48px] bg-white border-b border-[#edebe9] flex items-center px-3 gap-2">
        <div className="relative" ref={newMenuRef}>
          <button
            className="appearance-none border-0 h-8 min-w-[180px] rounded-[2px] overflow-hidden p-0 leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/50 select-none inline-flex items-stretch bg-[#0078d4] hover:bg-[#106ebe] transition active:scale-[0.99]"
            onClick={() => beginCompose()}
          >
            <span className="h-full inline-flex items-center gap-3 pl-5 pr-5 text-white">
              <span className="shrink-0"><NewMailIcon /></span>
              <span className="text-[14px] font-semibold whitespace-nowrap">New mail</span>
            </span>
            <span className="h-full w-px bg-white/20" />
            <span
              className="h-full w-[42px] inline-flex items-center justify-center text-white bg-transparent"
              onClick={(event) => {
                event.stopPropagation();
                setShowNewMenu((prev) => !prev);
              }}
            >
              <ChevronDownIcon />
            </span>
          </button>
          {showNewMenu ? (
            <div className="absolute top-[40px] left-0 z-20 min-w-[260px] rounded-2xl border border-pearl-200 bg-white shadow-lux p-2">
              <button className="w-full rounded-xl px-3 py-2 text-left flex items-center gap-3 hover:bg-pearl-100 transition" onClick={() => handleNewMenuAction("mail")}>
                <span className="text-xl text-slate-500">✉</span>
                <span className="text-[17px]">New mail</span>
              </button>
              <button className="w-full rounded-xl px-3 py-2 text-left flex items-center gap-3 hover:bg-pearl-100 transition" onClick={() => handleNewMenuAction("calendar")}>
                <span className="text-xl text-slate-500">📅</span>
                <span className="text-[17px]">Calendar event</span>
              </button>
              <button className="w-full rounded-xl px-3 py-2 text-left flex items-center gap-3 hover:bg-pearl-100 transition" onClick={() => handleNewMenuAction("contact")}>
                <span className="text-xl text-slate-500">👥</span>
                <span className="text-[17px]">Contact</span>
              </button>
            </div>
          ) : null}
        </div>

        <div className="mx-1 h-6 w-px bg-[#edebe9]" />

        {hasSelectedMailMessage ? (
          <div className="flex items-center gap-0.5 text-[#323130]">
            <button title="Delete" disabled={Boolean(busyAction)} className="flex h-8 w-8 items-center justify-center rounded-[2px] transition hover:bg-[#edebe9] disabled:opacity-40 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35" onClick={() => runAction("delete", actionDelete)}><ToolbarIcon name="trash" /></button>
            <button title="Archive" disabled={Boolean(busyAction)} className="flex h-8 w-8 items-center justify-center rounded-[2px] transition hover:bg-[#edebe9] disabled:opacity-40 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35" onClick={() => runAction("archive", actionArchive)}><ToolbarIcon name="archive" /></button>
            <button
              title="Snooze"
              disabled={Boolean(busyAction)}
              className="flex h-8 w-8 items-center justify-center rounded-[2px] transition hover:bg-[#edebe9] disabled:opacity-40 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35"
              onClick={() =>
                runAction("snooze", async () => {
                  await snoozeSelectedMessage("tomorrowMorning");
                })
              }
            >
              <span className="text-[14px] leading-none">⏰</span>
            </button>
            <button title="Mark read/unread" disabled={Boolean(busyAction)} className="flex h-8 w-8 items-center justify-center rounded-[2px] transition hover:bg-[#edebe9] disabled:opacity-40 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35" onClick={() => runAction("toggleRead", actionToggleRead)}><ToolbarIcon name="warning" /></button>
            <button title="Move to Inbox" disabled={Boolean(busyAction)} className="flex h-8 w-8 items-center justify-center rounded-[2px] transition hover:bg-[#edebe9] disabled:opacity-40 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35" onClick={() => runAction("moveInbox", async () => optimisticMove(selectedAccountForMessage, selectedProviderMessageId, "INBOX"))}><ToolbarIcon name="folder" /></button>
            <button title="Star" disabled={Boolean(busyAction)} className="flex h-8 w-8 items-center justify-center rounded-[2px] transition hover:bg-[#edebe9] disabled:opacity-40 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35" onClick={() => runAction("toggleStar", actionToggleStar)}><ToolbarIcon name="tag" /></button>
          </div>
        ) : (
          <div className="text-[12px] text-slateLux-500 hidden md:block">Actions appear when you select a message.</div>
        )}

        <div className="mx-1 h-6 w-px bg-[#edebe9]" />

        {hasSelectedMailMessage ? (
          <div className="flex items-center gap-0.5 text-[#323130]">
            <button title="Reply" disabled={Boolean(busyAction)} className="flex h-8 w-8 items-center justify-center rounded-[2px] transition hover:bg-[#edebe9] disabled:opacity-40 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35" onClick={() => runAction("reply", async () => actionReply())}><ToolbarIcon name="replyLeft" /></button>
            <button title="Reply all" disabled={Boolean(busyAction)} className="flex h-8 w-8 items-center justify-center rounded-[2px] transition hover:bg-[#edebe9] disabled:opacity-40 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35" onClick={() => runAction("replyAll", async () => actionReplyAll())}><ToolbarIcon name="replyRight" /></button>
            <button title="Forward" disabled={Boolean(busyAction)} className="flex h-8 w-8 items-center justify-center rounded-[2px] transition hover:bg-[#edebe9] disabled:opacity-40 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35" onClick={() => runAction("forward", async () => actionForward())}><ToolbarIcon name="replyRight" /></button>
          </div>
        ) : null}

        <div className="mx-1 h-6 w-px bg-[#edebe9]" />

        <div className="flex items-center gap-0.5 text-[#323130]">
          <button title="Refresh" className="flex h-8 w-8 items-center justify-center rounded-[2px] transition hover:bg-[#edebe9] disabled:opacity-40 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35" disabled={Boolean(busyAction)} onClick={() => runAction("refresh", refreshAll)}><ToolbarIcon name="refresh" /></button>
          {hasSelectedMailMessage ? (
            <>
              <button title="Undo archive" disabled={Boolean(busyAction)} className="flex h-8 w-8 items-center justify-center rounded-[2px] transition hover:bg-[#edebe9] disabled:opacity-40 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35" onClick={() => runAction("unarchive", async () => optimisticFlagUpdate(selectedAccountForMessage, selectedProviderMessageId, { archived: false }))}><ToolbarIcon name="undo" /></button>
              <button title="Pin" disabled={Boolean(busyAction)} className="flex h-8 w-8 items-center justify-center rounded-[2px] transition hover:bg-[#edebe9] disabled:opacity-40 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35" onClick={() => runAction("pin", actionToggleStar)}><ToolbarIcon name="pin" /></button>
            </>
          ) : null}
          <button title="Print" className="flex h-8 w-8 items-center justify-center rounded-[2px] transition hover:bg-[#edebe9] active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35" onClick={() => window.print()}><ToolbarIcon name="print" /></button>
        </div>

        <button className="ml-auto flex h-8 w-8 items-center justify-center rounded-[2px] text-[#323130] transition hover:bg-[#edebe9] active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35" title="More actions">⋯</button>
      </div>

      <section
        className={`grid gap-0 min-h-0 h-[calc(100vh-96px)] ${
          isMobile ? "grid-cols-1" : "grid-cols-[228px_330px_minmax(0,1fr)_64px]"
        }`}
      >
        <aside className={`border-r border-[#edebe9] bg-[#f3f2f1] px-1.5 py-2 overflow-y-auto ${isMobile ? "hidden" : ""}`}>
          {quickFolders.map((folder) => (
            <button
              key={folder.id}
              className={`mb-0.5 flex w-full items-center border-0 border-l-[3px] py-2 pl-2.5 pr-2 text-left text-[13px] leading-none transition ${
                activeFolderId === folder.id
                  ? "border-l-[#0078d4] bg-[#edebe9] font-semibold text-[#201f1e]"
                  : "border-l-transparent text-[#323130] hover:bg-[#edebe9]/80"
              }`}
              onClick={() => {
                if (folder.id === "BLOCKED" || folder.id === "MANAGE_BLOCKED") {
                  setActivePage("blocked");
                  return;
                }
                setFolderView(folder.id);
              }}
            >
              <span className="mr-3 shrink-0"><FolderIcon type={folder.iconType} /></span>
              <span className="text-[14px] leading-none tracking-tight">{folder.label}</span>
              {folder.count > 0 ? (
                <span className="ml-auto min-w-[22px] rounded-full bg-white px-1.5 py-0.5 text-center text-[10px] font-semibold tabular-nums leading-none text-[#605e5c] shadow-sm">
                  {folder.count}
                </span>
              ) : null}
            </button>
          ))}
          <div className="mt-2 border-t border-[#edebe9] pt-2">
            <button
              className={`mb-0.5 flex w-full items-center border-0 border-l-[3px] py-2 pl-2.5 pr-2 text-left text-[13px] leading-none transition ${
                activePage === "templates"
                  ? "border-l-[#0078d4] bg-[#edebe9] font-semibold text-[#201f1e]"
                  : "border-l-transparent text-[#323130] hover:bg-[#edebe9]/80"
              }`}
              onClick={() => {
                setTemplateSearch("");
                setActivePage("templates");
              }}
            >
              <span className="mr-3 shrink-0"><FolderIcon type="templates" /></span>
              <span className="text-[14px] leading-none tracking-tight">Templates</span>
            </button>
            {String(currentUser?.role || "").toLowerCase() === "super_admin" ? (
              <button
                className={`mb-0.5 mt-1 flex w-full items-center border-0 border-l-[3px] py-2 pl-2.5 pr-2 text-left text-[13px] leading-none transition ${
                  activePage === "platform"
                    ? "border-l-[#0078d4] bg-[#edebe9] font-semibold text-[#201f1e]"
                    : "border-l-transparent text-[#323130] hover:bg-[#edebe9]/80"
                }`}
                onClick={() => setActivePage("platform")}
              >
                <span className="mr-3 shrink-0"><FolderIcon type="platform" /></span>
                <span className="text-[14px] leading-none tracking-tight">Platform</span>
              </button>
            ) : null}
          </div>
          <div className="border-t mt-4 pt-3 hidden">
            <h4 className="text-xs font-semibold text-slate-500 mb-2">Accounts</h4>
            {accounts.map((account) => (
              <button
                key={account.id}
                className={`w-full text-left text-xs px-2 py-1 rounded mb-1 ${activeAccountId === account.id ? "bg-accent-600/10" : "hover:bg-pearl-50"}`}
                onClick={() => {
                  setActiveAccountId(account.id);
                  setUnifiedMode(false);
                  setActivePage("mail");
                }}
              >
                {account.emailAddress}
              </button>
            ))}
          </div>
          <div className="border-t mt-3 pt-3 hidden">
            <h4 className="text-xs font-semibold text-slate-500 mb-2">Social</h4>
            <div className="grid grid-cols-3 gap-1">
              {socialSummary.map((platform) => (
                <button
                  key={platform.id}
                  title={platform.label}
                  className={`rounded p-1 border border-pearl-200 text-[10px] transition ${socialConnectForm.platform === platform.id ? "border-accent-600 bg-accent-600/10" : "hover:bg-pearl-50"}`}
                  onClick={() => {
                    setSocialConnectForm((prev) => ({ ...prev, platform: platform.id }));
                    setActivePage("social");
                  }}
                >
                  <span className="inline-flex w-5 h-5 rounded-full bg-white border border-pearl-200 items-center justify-center">
                    <SocialProviderIcon platform={platform.id} className="w-3.5 h-3.5" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </aside>

        <section
          className={`min-h-0 flex flex-1 flex-col border-[#edebe9] bg-white ${isMobile ? "" : "border-r"} ${
            templateEditorFullBleed ? "hidden" : ""
          }`}
        >
          <div className="shrink-0 border-b border-[#edebe9] bg-white">
            <div className="flex flex-col gap-2 px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                <div className="flex min-w-0 flex-1 items-center gap-2 sm:flex-none sm:max-w-[min(100%,280px)]">
                  <label htmlFor="inbox-scope-select" className="sr-only">
                    Inbox scope
                  </label>
                  <select
                    id="inbox-scope-select"
                    className="text-xs border border-pearl-200 rounded-lg px-2.5 py-1.5 bg-white text-slateLux-700 shadow-sm w-full min-w-0 sm:w-auto sm:min-w-[160px] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30"
                    value={unifiedMode ? "all" : activeAccountId}
                    onChange={(e) => handleInboxScopeChange(e.target.value)}
                    disabled={loadingAccounts}
                  >
                    <option value="all">All Inbox</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.emailAddress}
                      </option>
                    ))}
                  </select>
                  {loadingFolders ? (
                    <span className="inline-flex items-center gap-2 text-[11px] text-slate-400 shrink-0">
                      <span className="inline-block w-2 h-2 rounded-full bg-pearl-200 animate-pulse" />
                      <span className="hidden sm:inline">Folders</span>
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isMobile ? (
                    <button
                      type="button"
                      className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-pearl-200 bg-white text-slateLux-700 shadow-sm hover:bg-pearl-50 whitespace-nowrap transition-colors"
                      onClick={() => setBriefingSheetOpen(true)}
                    >
                      Briefing
                    </button>
                  ) : null}
                  <span
                    className="inline-flex items-center justify-center text-[11px] font-medium tabular-nums rounded-full border border-pearl-200/90 bg-pearl-100/90 text-slateLux-800 px-2.5 py-1 whitespace-nowrap shadow-sm"
                    title="Messages in the current list"
                    aria-live="polite"
                  >
                    {listMessages.length === 1 ? "1 message" : `${listMessages.length} messages`}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-t border-pearl-200/80 pt-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="w-11 shrink-0 text-right text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Sort
                  </span>
                  <div className="inline-flex shrink-0 items-center rounded-lg border border-pearl-200 bg-white p-0.5 shadow-sm">
                    <button
                      type="button"
                      className={`px-2 h-7 text-[11px] font-semibold rounded-md transition-colors ${
                        inboxViewMode === "grouped" ? "bg-pearl-200 text-slateLux-900" : "bg-transparent text-slateLux-500 hover:bg-pearl-50"
                      }`}
                      onClick={() => setInboxViewMode("grouped")}
                      title="Group related messages"
                    >
                      By Group
                    </button>
                    <button
                      type="button"
                      className={`px-2 h-7 text-[11px] font-semibold rounded-md transition-colors border-l border-pearl-200 ${
                        inboxViewMode === "chronological" ? "bg-pearl-200 text-slateLux-900" : "bg-transparent text-slateLux-500 hover:bg-pearl-50"
                      }`}
                      onClick={() => setInboxViewMode("chronological")}
                      title="Sort by latest date/time"
                    >
                      By Time
                    </button>
                  </div>
                </div>
                <div className="flex min-w-0 items-center gap-2">
                  <span className="w-11 shrink-0 text-right text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Density
                  </span>
                  <div className="inline-flex shrink-0 items-center rounded-lg border border-pearl-200 bg-white p-0.5 shadow-sm">
                    <button
                      type="button"
                      className={`px-2 h-7 text-[11px] font-semibold rounded-md transition-colors ${
                        inboxDensityMode === "comfortable" ? "bg-pearl-200 text-slateLux-900" : "bg-transparent text-slateLux-500 hover:bg-pearl-50"
                      }`}
                      onClick={() => setInboxDensityMode("comfortable")}
                      title="Comfortable density"
                    >
                      Comfort
                    </button>
                    <button
                      type="button"
                      className={`px-2 h-7 text-[11px] font-semibold rounded-md transition-colors border-l border-pearl-200 ${
                        inboxDensityMode === "compact" ? "bg-pearl-200 text-slateLux-900" : "bg-transparent text-slateLux-500 hover:bg-pearl-50"
                      }`}
                      onClick={() => setInboxDensityMode("compact")}
                      title="Compact density"
                    >
                      Compact
                    </button>
                  </div>
                </div>
                <div className="hidden min-w-0 flex-1 xl:flex xl:justify-end">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className={`w-7 h-7 shrink-0 rounded-lg border text-[11px] font-semibold shadow-sm transition-colors ${
                        showShortcutLegend
                          ? "bg-pearl-200 border-pearl-200 text-slateLux-900"
                          : "bg-white border-pearl-200 text-slateLux-500 hover:bg-pearl-50"
                      }`}
                      title="Toggle shortcut legend"
                      onClick={() => setShowShortcutLegend((prev) => !prev)}
                    >
                      ?
                    </button>
                    {showShortcutLegend ? (
                      <div className="flex max-w-[min(100%,420px)] items-center gap-1 overflow-x-auto rounded-lg border border-pearl-200 bg-pearl-50 px-2 py-1 text-[10px] text-slateLux-500 shadow-sm [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                        <span className="shrink-0 font-semibold text-slateLux-700">Shortcuts</span>
                        <span className="shrink-0 rounded border border-pearl-200 bg-white px-1 py-[1px] font-semibold">G</span>
                        <span className="shrink-0">Grouped</span>
                        <span className="shrink-0 rounded border border-pearl-200 bg-white px-1 py-[1px] font-semibold">C</span>
                        <span className="shrink-0">Chrono</span>
                        <span className="shrink-0 rounded border border-pearl-200 bg-white px-1 py-[1px] font-semibold">D</span>
                        <span className="shrink-0">Density</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 border-t border-pearl-200/80 pt-2">
                <span className="w-11 shrink-0 text-right text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  Filter
                </span>
                <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-1 overflow-x-auto overscroll-x-contain pb-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                  <button
                    type="button"
                    className={`shrink-0 text-[11px] px-2 py-1 rounded-full border font-semibold transition-colors ${
                      filters.unread ? "bg-pearl-200 border-pearl-200 text-slateLux-900" : "bg-white border-pearl-200 text-slateLux-500 hover:bg-pearl-50"
                    }`}
                    onClick={() => setFilters((prev) => ({ ...prev, unread: !prev.unread }))}
                  >
                    Unread
                  </button>
                  <button
                    type="button"
                    className={`shrink-0 text-[11px] px-2 py-1 rounded-full border font-semibold transition-colors ${
                      filters.starred ? "bg-pearl-200 border-pearl-200 text-slateLux-900" : "bg-white border-pearl-200 text-slateLux-500 hover:bg-pearl-50"
                    }`}
                    onClick={() => setFilters((prev) => ({ ...prev, starred: !prev.starred }))}
                  >
                    Starred
                  </button>
                  <button
                    type="button"
                    className={`shrink-0 text-[11px] px-2 py-1 rounded-full border font-semibold transition-colors ${
                      filters.archived ? "bg-pearl-200 border-pearl-200 text-slateLux-900" : "bg-white border-pearl-200 text-slateLux-500 hover:bg-pearl-50"
                    }`}
                    onClick={() => setFilters((prev) => ({ ...prev, archived: !prev.archived }))}
                  >
                    Archived
                  </button>
                  <button
                    type="button"
                    className={`shrink-0 text-[11px] px-2 py-1 rounded-full border font-semibold transition-colors ${
                      mailView === "future"
                        ? "bg-ink-900 border-ink-900 text-white"
                        : "bg-white border-pearl-200 text-slateLux-500 hover:bg-pearl-50"
                    }`}
                    onClick={() => setMailView((prev) => (prev === "future" ? "inbox" : "future"))}
                  >
                    Future
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div
            ref={inboxListScrollRef}
            className={`min-h-0 flex-1 overflow-y-auto bg-[#faf9f8] ${isMobile ? "pb-24" : ""}`}
          >
            {(mailView === "future" ? loadingFuture : loadingMessages) ? (
              <div className="p-3">
                <SkeletonList rows={10} />
              </div>
            ) : listMessages.length === 0 ? (
              <div className="p-6 text-sm text-slate-500">{mailView === "future" ? "No snoozed messages." : "No messages found."}</div>
            ) : shouldVirtualizeMessages && inboxViewMode === "grouped" ? (
              <VirtualVarList
                height={inboxListViewportHeight}
                itemCount={groupedVirtualItems.length}
                itemSize={(idx) => (groupedVirtualItems[idx]?.type === "header" ? 28 : messageRowHeight)}
                width={"100%"}
                itemKey={(idx) => groupedVirtualItems[idx]?.id || idx}
              >
                {({ index, style }) => {
                  const item = groupedVirtualItems[index];
                  if (!item) return <div style={style} />;
                  if (item.type === "header") {
                    return (
                      <div style={style} className="flex items-center gap-2 border-b border-[#edebe9] bg-[#edebe9] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#605e5c]">
                        <span>{item.label}</span>
                        <span className="rounded-full bg-pearl-200 px-1.5 py-[1px] text-[10px] text-slateLux-700">
                          {Number(item.size || 1)}
                        </span>
                      </div>
                    );
                  }
                  const message = item.message;
                  let touchStartX = 0;
                  return (
                    <div style={style}>
                      <button
                        className={`w-full text-left px-3 ${
                          inboxDensityMode === "compact" ? "py-[8px]" : "py-[12px]"
                        } border-b border-pearl-200 transition-colors duration-150 ${getMessageRowTone(message, item.index)} ${getMessageHoverTone(message)}`}
                        onClick={() => selectMessage(message)}
                        onTouchStart={(e) => {
                          touchStartX = e.touches?.[0]?.clientX ?? 0;
                        }}
                        onTouchEnd={(e) => {
                          const endX = e.changedTouches?.[0]?.clientX ?? 0;
                          const delta = endX - touchStartX;
                          if (Math.abs(delta) < 70) return;
                          const accountId = message.account_id;
                          const providerId = message.provider_message_id;
                          if (!accountId || !providerId) return;
                          if (delta < -70) {
                            haptic(12);
                            pushNotification("Archived");
                            optimisticFlagUpdate(accountId, providerId, { archived: true });
                            return;
                          }
                          if (delta > 140) {
                            haptic(12);
                            pushNotification(Boolean(message.is_starred) ? "Unstarred" : "Starred");
                            optimisticFlagUpdate(accountId, providerId, { starred: !Boolean(message.is_starred) });
                            return;
                          }
                          haptic(10);
                          pushNotification(Boolean(message.is_read) ? "Marked unread" : "Marked read");
                          optimisticFlagUpdate(accountId, providerId, { read: !Boolean(message.is_read) });
                        }}
                      >
                        <div className={`flex items-center gap-2 ${inboxDensityMode === "compact" ? "text-[12px] mb-0.5" : "text-sm mb-1"}`}>
                          <span className="inline-flex w-6 h-6 rounded-full overflow-hidden border border-white/60 bg-white items-center justify-center">
                            {String(message?.trust_verdict || "") === "verified" ? (
                              <img
                                alt=""
                                src={`/api/security/brand-logo/${encodeURIComponent(domainFromEmail(message.from_email || ""))}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  try { e.currentTarget.style.display = "none"; } catch (_e2) {}
                                }}
                              />
                            ) : (
                              <span className="text-[10px] font-semibold text-slate-600">
                                {String(message.from_name || message.from_email || "?").slice(0, 1).toUpperCase()}
                              </span>
                            )}
                          </span>
                          <span className="text-[11px] text-accent-600">{Boolean(message.is_archived) ? "⚑" : "⎆"}</span>
                          <span className={`text-[12px] ${Boolean(message.is_starred) ? "text-gold-500" : "text-slateLux-500"}`}>
                            {Boolean(message.is_starred) ? "★" : "☆"}
                          </span>
                          <span className={`font-semibold ${inboxDensityMode === "compact" ? "text-[13px]" : "text-[14px]"} ${Boolean(message.is_read) ? "text-slateLux-700" : "text-slateLux-900"}`}>
                            {message.from_name || message.from_email}
                          </span>
                          <span className="ml-auto text-[12px] text-slateLux-500 flex items-center gap-1">
                            <span className={Boolean(message.has_attachments) ? "text-accent-600" : "text-transparent"}>📎</span>
                            <span>{message.received_at ? new Date(message.received_at).toLocaleDateString("en-GB") : ""}</span>
                          </span>
                        </div>
                        <div className={`font-semibold ${inboxDensityMode === "compact" ? "text-[14px] mt-0.5" : "text-[16px] mt-1"} leading-tight pr-2 ${Boolean(message.is_read) ? "text-slateLux-700" : "text-slateLux-900"}`}>
                          {message.subject || "(No subject)"}
                        </div>
                        <div className={`${inboxDensityMode === "compact" ? "text-[11px] mt-0.5 line-clamp-1" : "text-[12px] mt-1 line-clamp-2"} text-slateLux-700 text-left`}>
                          {mailView === "future" && message.__wakeAt
                            ? `Wakes ${new Date(message.__wakeAt).toLocaleString()} • ${message.snippet || ""}`.trim()
                            : message.snippet}
                        </div>
                      </button>
                    </div>
                  );
                }}
              </VirtualVarList>
            ) : shouldVirtualizeMessages ? (
              <VirtualList
                height={inboxListViewportHeight}
                itemCount={listMessages.length}
                itemSize={messageRowHeight}
                width={"100%"}
                itemKey={(index) => `${listMessages[index]?.account_id || ""}-${listMessages[index]?.provider_message_id || index}`}
              >
                {({ index, style }) => {
                  const message = listMessages[index];
                  const isSelected = selectedMessage?.provider_message_id === message.provider_message_id;
                  let touchStartX = 0;
                  return (
                    <div style={style}>
                      <button
                        className={`w-full text-left px-3 ${
                          inboxDensityMode === "compact" ? "py-[8px]" : "py-[12px]"
                        } border-b border-pearl-200 transition-colors duration-150 ${getMessageRowTone(message, index)} ${getMessageHoverTone(message)}`}
                        onClick={() => selectMessage(message)}
                        onTouchStart={(e) => {
                          touchStartX = e.touches?.[0]?.clientX ?? 0;
                        }}
                        onTouchEnd={(e) => {
                          const endX = e.changedTouches?.[0]?.clientX ?? 0;
                          const delta = endX - touchStartX;
                          if (Math.abs(delta) < 70) return;
                          const accountId = message.account_id;
                          const providerId = message.provider_message_id;
                          if (!accountId || !providerId) return;
                          if (delta < -70) {
                            haptic(12);
                            pushNotification("Archived");
                            optimisticFlagUpdate(accountId, providerId, { archived: true });
                            return;
                          }
                          if (delta > 140) {
                            haptic(12);
                            pushNotification(Boolean(message.is_starred) ? "Unstarred" : "Starred");
                            optimisticFlagUpdate(accountId, providerId, { starred: !Boolean(message.is_starred) });
                            return;
                          }
                          haptic(10);
                          pushNotification(Boolean(message.is_read) ? "Marked unread" : "Marked read");
                          optimisticFlagUpdate(accountId, providerId, { read: !Boolean(message.is_read) });
                        }}
                      >
                        <div className={`flex items-center gap-2 ${inboxDensityMode === "compact" ? "text-[12px] mb-0.5" : "text-sm mb-1"}`}>
                          <span className="text-[11px] text-accent-600">{Boolean(message.is_archived) ? "⚑" : "⎆"}</span>
                          <span className={`text-[12px] ${Boolean(message.is_starred) ? "text-gold-500" : "text-slateLux-500"}`}>
                            {Boolean(message.is_starred) ? "★" : "☆"}
                          </span>
                          <span className={`font-semibold ${inboxDensityMode === "compact" ? "text-[13px]" : "text-[14px]"} ${Boolean(message.is_read) ? "text-slateLux-700" : "text-slateLux-900"}`}>
                            {message.from_name || message.from_email}
                          </span>
                          <span className="ml-auto text-[12px] text-slateLux-500 flex items-center gap-1">
                            <span className={Boolean(message.has_attachments) ? "text-accent-600" : "text-transparent"}>📎</span>
                            <span>{message.received_at ? new Date(message.received_at).toLocaleDateString("en-GB") : ""}</span>
                          </span>
                        </div>
                        <div className={`font-semibold ${inboxDensityMode === "compact" ? "text-[14px] mt-0.5" : "text-[16px] mt-1"} leading-tight pr-2 ${Boolean(message.is_read) ? "text-slateLux-700" : "text-slateLux-900"}`}>
                          {message.subject || "(No subject)"}
                        </div>
                        {message.ai_category || message.relationship_group_type ? (
                          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                            {message.relationship_group_type && message.relationship_group_type !== "uncategorized" ? (
                              <span className="inline-flex items-center rounded-full px-2 py-[3px] text-[10px] font-semibold bg-accent-600/10 text-accent-700">
                                {String(message.relationship_group_type).toUpperCase()}
                              </span>
                            ) : null}
                            {message.ai_category ? (
                              <span className={`inline-flex items-center rounded-full px-2 py-[3px] text-[10px] font-semibold ${aiCategoryTone(message.ai_category)}`}>
                                {formatAiCategory(message.ai_category)}
                              </span>
                            ) : null}
                            {Number(message.ai_confidence || 0) > 0 ? <span className="text-[10px] text-slateLux-500">{Math.round(Number(message.ai_confidence) * 100)}%</span> : null}
                          </div>
                        ) : null}
                        <div className={`${inboxDensityMode === "compact" ? "text-[11px] mt-0.5 line-clamp-1" : "text-[12px] mt-1 line-clamp-2"} text-slateLux-700 text-left`}>
                          {message.snippet}
                        </div>
                        {isSelected ? <span className="sr-only">Selected</span> : null}
                      </button>
                    </div>
                  );
                }}
              </VirtualList>
            ) : (
              listMessages.map((message, index) => (
              <React.Fragment key={`${message.account_id || ""}-${message.provider_message_id}`}>
                {mailView !== "future" && inboxViewMode === "grouped" &&
                (index === 0 || listMessages[index - 1]?.relationship_group_id !== message.relationship_group_id) ? (
                  message.relationship_group_id ? (
                    <div className="flex items-center gap-2 border-b border-[#edebe9] bg-[#edebe9] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#605e5c]">
                      <span>{message.relationship_group_label || "Grouped"}</span>
                      <span className="rounded-full bg-pearl-200 px-1.5 py-[1px] text-[10px] text-slateLux-700">
                        {Number(message.relationship_group_size || 1)}
                      </span>
                    </div>
                  ) : null
                ) : null}
                {(() => {
                  let touchStartX = 0;
                  return (
                    <button
                  className={`w-full text-left px-3 ${
                    inboxDensityMode === "compact" ? "py-[8px]" : "py-[12px]"
                  } border-b border-pearl-200 transition-colors duration-150 ${getMessageRowTone(message, index)} ${getMessageHoverTone(message)}`}
                  onClick={() => selectMessage(message)}
                  onTouchStart={(e) => {
                    touchStartX = e.touches?.[0]?.clientX ?? 0;
                  }}
                  onTouchEnd={(e) => {
                    const endX = e.changedTouches?.[0]?.clientX ?? 0;
                    const delta = endX - touchStartX;
                    if (Math.abs(delta) < 70) return;
                    const accountId = message.account_id;
                    const providerId = message.provider_message_id;
                    if (!accountId || !providerId) return;
                    if (delta < -70) {
                      haptic(12);
                      pushNotification("Archived");
                      optimisticFlagUpdate(accountId, providerId, { archived: true });
                      return;
                    }
                    if (delta > 140) {
                      haptic(12);
                      pushNotification(Boolean(message.is_starred) ? "Unstarred" : "Starred");
                      optimisticFlagUpdate(accountId, providerId, { starred: !Boolean(message.is_starred) });
                      return;
                    }
                    haptic(10);
                    pushNotification(Boolean(message.is_read) ? "Marked unread" : "Marked read");
                    optimisticFlagUpdate(accountId, providerId, { read: !Boolean(message.is_read) });
                  }}
                >
                <div className={`flex items-center gap-2 ${inboxDensityMode === "compact" ? "text-[12px] mb-0.5" : "text-sm mb-1"}`}>
                  <span className="inline-flex w-6 h-6 rounded-full overflow-hidden border border-white/60 bg-white items-center justify-center">
                    {String(message?.trust_verdict || "") === "verified" ? (
                      <img
                        alt=""
                        src={`/api/security/brand-logo/${encodeURIComponent(domainFromEmail(message.from_email || ""))}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          try { e.currentTarget.style.display = "none"; } catch (_e2) {}
                        }}
                      />
                    ) : (
                      <span className="text-[10px] font-semibold text-slate-600">
                        {String(message.from_name || message.from_email || "?").slice(0, 1).toUpperCase()}
                      </span>
                    )}
                  </span>
                  <span className="text-[11px] text-accent-600">{Boolean(message.is_archived) ? "⚑" : "⎆"}</span>
                  <span className={`text-[12px] ${Boolean(message.is_starred) ? "text-gold-500" : "text-slateLux-500"}`}>
                    {Boolean(message.is_starred) ? "★" : "☆"}
                  </span>
                  <span className={`font-semibold ${inboxDensityMode === "compact" ? "text-[13px]" : "text-[14px]"} ${Boolean(message.is_read) ? "text-slateLux-700" : "text-slateLux-900"}`}>
                    {message.from_name || message.from_email}
                  </span>
                  <span className="ml-auto text-[12px] text-slateLux-500 flex items-center gap-1">
                    <span className={Boolean(message.has_attachments) ? "text-accent-600" : "text-transparent"}>📎</span>
                    <span>{message.received_at ? new Date(message.received_at).toLocaleDateString("en-GB") : ""}</span>
                  </span>
                </div>
                <div className={`font-semibold ${inboxDensityMode === "compact" ? "text-[14px] mt-0.5" : "text-[16px] mt-1"} leading-tight pr-2 ${Boolean(message.is_read) ? "text-slateLux-700" : "text-slateLux-900"}`}>
                  {message.subject || "(No subject)"}
                </div>
                {message.ai_category || message.relationship_group_type ? (
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    {message.relationship_group_type && message.relationship_group_type !== "uncategorized" ? (
                      <span className="inline-flex items-center rounded-full px-2 py-[3px] text-[10px] font-semibold bg-accent-600/10 text-accent-700">
                        {String(message.relationship_group_type).toUpperCase()}
                      </span>
                    ) : null}
                    {message.ai_category ? (
                      <span className={`inline-flex items-center rounded-full px-2 py-[3px] text-[10px] font-semibold ${aiCategoryTone(message.ai_category)}`}>
                        {formatAiCategory(message.ai_category)}
                      </span>
                    ) : null}
                    {Number(message.ai_confidence || 0) > 0 ? <span className="text-[10px] text-slateLux-500">{Math.round(Number(message.ai_confidence) * 100)}%</span> : null}
                  </div>
                ) : null}
                <div className={`${inboxDensityMode === "compact" ? "text-[11px] mt-0.5 line-clamp-1" : "text-[12px] mt-1 line-clamp-2"} text-slateLux-700 text-left`}>
                  {mailView === "future" && message.__wakeAt
                    ? `Wakes ${new Date(message.__wakeAt).toLocaleString()} • ${message.snippet || ""}`.trim()
                    : message.snippet}
                </div>
                    </button>
                  );
                })()}
              </React.Fragment>
            ))
            )}
          </div>
          </div>
        </section>

        <section
          className={`flex min-h-0 flex-col bg-white ${
            isMobile
              ? mobileDetailOpen
                ? "fixed inset-0 z-[60] bg-white p-3 pt-4 pb-24 overflow-y-auto"
                : "hidden"
              : activePage === "templates" && templateEditorOpen
                ? "overflow-hidden bg-white p-2 min-h-0 h-full"
                : "overflow-y-auto bg-white p-3"
          } ${templateEditorFullBleed ? "col-start-2 col-span-2 min-w-0" : ""}`}
        >
          {isMobile ? (
            <div className="sticky top-0 -mt-4 pt-4 pb-2 bg-white z-10 border-b border-pearl-200 flex items-center gap-2">
              <button
                className="h-9 px-3 rounded-full border border-pearl-200 bg-white text-xs font-semibold hover:bg-pearl-50 transition"
                onClick={() => setMobileDetailOpen(false)}
              >
                ← Inbox
              </button>
              <div className="text-xs text-slate-500 truncate">
                {selectedMessage?.subject || selectedMessage?.from_email || "Message"}
              </div>
            </div>
          ) : null}
          {error ? <div className="mb-3 text-xs text-danger-600 border border-red-200 rounded px-2 py-1 bg-red-50">{error}</div> : null}
          {notifications.length > 0 ? (
            <div className="mb-3 text-xs text-slateLux-700 border border-pearl-200 rounded px-2 py-1 bg-pearl-50">
              {notifications[0]?.message}
            </div>
          ) : null}
          {activePage === "compose" ? (
            <div className="h-full bg-pearl-100 -m-4 p-4 flex items-start justify-center overflow-y-auto">
              <div className="w-full max-w-[920px] bg-white border border-pearl-200 rounded-2xl shadow-luxSoft overflow-hidden">
                <div className="px-6 py-5 border-b border-pearl-200 flex items-start gap-3">
                  <div>
                    <div className="text-[24px] leading-none font-semibold text-slateLux-900 tracking-tight">New Message</div>
                    <div className="text-[13px] text-slate-500 mt-1.5">Compose and send a new email message</div>
                  </div>
                  <div className="ml-auto flex items-center gap-3">
                    <button className="w-9 h-9 rounded-full border border-pearl-200 text-slate-500 text-sm hover:bg-pearl-50 transition" title="Help">?</button>
                    <button
                      className="h-11 px-5 rounded-xl bg-ink-900 text-white text-[16px] leading-none font-semibold hover:bg-black disabled:opacity-50 transition"
                      title="AI Draft"
                      disabled={aiDraftLoading}
                      onClick={() => runAction("aiDraft", generateAiDraft)}
                    >
                      {aiDraftLoading ? "Drafting…" : "AI Draft"}
                    </button>
                    <button
                      className="h-11 px-5 rounded-xl border border-pearl-200 text-[16px] leading-none font-semibold text-slateLux-900 hover:bg-pearl-50 transition"
                      title="Templates"
                      onClick={() => {
                        setTemplateSearch("");
                        setActivePage("templates");
                      }}
                    >
                      Templates
                    </button>
                    <button className="w-9 h-9 rounded-full text-2xl leading-none text-slate-500 hover:bg-pearl-100 transition" onClick={() => setActivePage("mail")} title="Close composer">
                      ×
                    </button>
                  </div>
                </div>

                <div className="px-6 py-4 border-b border-pearl-200 space-y-3">
                  <div className="grid grid-cols-[84px_minmax(0,1fr)_54px_54px] items-center gap-3">
                    <label className="text-[16px] text-slate-700">To:</label>
                    <input
                      className="h-12 bg-pearl-50 rounded-md px-4 text-[18px] leading-none outline-none border border-transparent focus:border-accent-600/40"
                      placeholder="Enter recipients"
                      value={compose.to.join(",")}
                      onChange={(e) => setCompose((prev) => ({ ...prev, to: toAddressArray(e.target.value) }))}
                    />
                    <button
                      className="text-[16px] text-slateLux-700 hover:text-accent-600 transition"
                      onClick={() => {
                        setShowCcField(true);
                        setTimeout(() => document.getElementById("compose-cc-input")?.focus(), 0);
                      }}
                    >
                      Cc
                    </button>
                    <button
                      className="text-[16px] text-slateLux-700 hover:text-accent-600 transition"
                      onClick={() => {
                        setShowBccField(true);
                        setTimeout(() => document.getElementById("compose-bcc-input")?.focus(), 0);
                      }}
                    >
                      Bcc
                    </button>
                  </div>
                  {showCcField ? (
                    <div className="grid grid-cols-[84px_minmax(0,1fr)] items-center gap-3">
                      <label className="text-[16px] text-slate-700">Cc:</label>
                      <input
                        id="compose-cc-input"
                        className="h-12 bg-pearl-100 rounded-md px-4 text-[18px] leading-none outline-none border border-transparent focus:border-accent-600/40 focus-visible:ring-2 focus-visible:ring-accent-600/25"
                        placeholder="Cc recipients"
                        value={(compose.cc || []).join(",")}
                        onChange={(e) => setCompose((prev) => ({ ...prev, cc: toAddressArray(e.target.value) }))}
                      />
                    </div>
                  ) : null}
                  {showBccField ? (
                    <div className="grid grid-cols-[84px_minmax(0,1fr)] items-center gap-3">
                      <label className="text-[16px] text-slate-700">Bcc:</label>
                      <input
                        id="compose-bcc-input"
                        className="h-12 bg-pearl-100 rounded-md px-4 text-[18px] leading-none outline-none border border-transparent focus:border-accent-600/40 focus-visible:ring-2 focus-visible:ring-accent-600/25"
                        placeholder="Bcc recipients"
                        value={(compose.bcc || []).join(",")}
                        onChange={(e) => setCompose((prev) => ({ ...prev, bcc: toAddressArray(e.target.value) }))}
                      />
                    </div>
                  ) : null}
                  <div className="grid grid-cols-[84px_minmax(0,1fr)] items-center gap-3">
                    <label className="text-[16px] text-slate-700">Subject:</label>
                    <input
                      className="h-12 bg-pearl-100 rounded-md px-4 text-[18px] leading-none outline-none border border-transparent focus:border-accent-600/40 focus-visible:ring-2 focus-visible:ring-accent-600/25"
                      placeholder="Question regarding [Course/Topic]"
                      value={compose.subject}
                      onChange={(e) => setCompose((prev) => ({ ...prev, subject: e.target.value }))}
                    />
                  </div>
                </div>

                <div ref={composeEmojiPickerRef} className="px-6 py-3 border-b border-pearl-200 flex items-center gap-2 text-slateLux-900 relative flex-wrap">
                  <select
                    className="h-8 rounded-md border border-pearl-200 px-2 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30"
                    value={composeFontFamily}
                    onChange={(e) => {
                      setComposeFontFamily(e.target.value);
                      runComposeCommand("fontName", e.target.value);
                    }}
                    title="Font family"
                  >
                    {composeFontOptions.map((font) => (
                      <option key={font} value={font}>
                        {font}
                      </option>
                    ))}
                  </select>
                  <select
                    className="h-8 rounded-md border border-pearl-200 px-2 text-xs bg-white w-[68px] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30"
                    value={composeFontSize}
                    onChange={(e) => {
                      setComposeFontSize(e.target.value);
                      runComposeCommand("fontSize", e.target.value);
                    }}
                    title="Font size"
                  >
                    {composeFontSizeOptions.map((size) => (
                      <option key={size.value} value={size.value}>
                        {size.label}
                      </option>
                    ))}
                  </select>
                  <button className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 text-[20px] leading-none font-medium" title="Bold" onClick={() => runComposeCommand("bold")}>B</button>
                  <button className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 text-[20px] leading-none italic" title="Italic" onClick={() => runComposeCommand("italic")}>I</button>
                  <button className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 text-[20px] leading-none underline" title="Underline" onClick={() => runComposeCommand("underline")}>U</button>
                  <button className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 text-[18px] leading-none line-through" title="Strikethrough" onClick={() => runComposeCommand("strikeThrough")}>S</button>
                  <button className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 text-[17px] leading-none" title="Link" onClick={insertComposeLink}>🔗</button>
                  <button className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 text-[16px] leading-none" title="Numbered list" onClick={() => runComposeCommand("insertOrderedList")}>1.</button>
                  <button className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 text-[17px] leading-none" title="List" onClick={() => runComposeCommand("insertUnorderedList")}>☰</button>
                  <button className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 text-[15px] leading-none" title="Align left" onClick={() => runComposeCommand("justifyLeft")}>⇤</button>
                  <button className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 text-[15px] leading-none" title="Align center" onClick={() => runComposeCommand("justifyCenter")}>≡</button>
                  <button className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 text-[15px] leading-none" title="Align right" onClick={() => runComposeCommand("justifyRight")}>⇥</button>
                  <label className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus-within:ring-2 focus-within:ring-accent-600/30 text-[13px] leading-none flex items-center justify-center cursor-pointer" title="Text color">
                    A
                    <input
                      type="color"
                      className="sr-only"
                      value={composeTextColor}
                      onChange={(e) => {
                        setComposeTextColor(e.target.value);
                        runComposeCommand("foreColor", e.target.value);
                      }}
                    />
                  </label>
                  <label className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus-within:ring-2 focus-within:ring-accent-600/30 text-[13px] leading-none flex items-center justify-center cursor-pointer" title="Highlight color">
                    🖍
                    <input
                      type="color"
                      className="sr-only"
                      value={composeHighlightColor}
                      onChange={(e) => {
                        setComposeHighlightColor(e.target.value);
                        runComposeCommand("hiliteColor", e.target.value);
                      }}
                    />
                  </label>
                  <button
                    className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 text-[17px] leading-none"
                    title="Emoji"
                    onClick={() => setShowComposeEmojiPicker((prev) => !prev)}
                  >
                    ☺
                  </button>
                  <button className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 text-[16px] leading-none" title="Clear formatting" onClick={() => runComposeCommand("removeFormat")}>Tx</button>
                    <button
                    className="h-9 rounded-md border border-pearl-200 px-2 text-[11px] font-semibold hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30"
                    title="AI reply suggestions"
                    disabled={!hasSelectedMailMessage || Boolean(busyAction)}
                    onClick={() => runAction("replySuggestions", loadReplySuggestions)}
                  >
                      {busyAction === "replySuggestions" ? "AI Replies…" : "AI Replies"}
                  </button>
                  <button
                    className="h-9 rounded-md border border-pearl-200 px-2 text-[11px] font-semibold hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30"
                    title="Grammar and spelling check"
                    disabled={!String(compose.text || "").trim() || Boolean(busyAction)}
                    onClick={() => runAction("grammarCheck", runComposeGrammarCheck)}
                  >
                      {busyAction === "grammarCheck" ? "Grammar…" : "Grammar Check"}
                  </button>
                  <span className="w-px h-8 bg-pearl-200" />
                  <button className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 text-[17px] leading-none" title="Attach file" onClick={() => composeFileInputRef.current?.click()}>📎</button>
                  <button className="w-9 h-9 rounded-md hover:bg-pearl-100 transition active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 text-[17px] leading-none" title="Insert image" onClick={() => composeFileInputRef.current?.click()}>🖼</button>
                  {showComposeEmojiPicker ? (
                    <div className="absolute left-[250px] top-12 z-20 w-[420px] max-h-[260px] overflow-y-auto rounded-2xl border border-pearl-200 bg-white p-3 shadow-lux">
                      <div className="grid grid-cols-10 gap-1.5">
                        {composeEmojiOptions.map((emoji) => (
                          <button
                            key={emoji}
                            className="h-8 w-8 rounded-md text-[20px] leading-none hover:bg-pearl-100 transition"
                            onClick={() => {
                              insertComposeTextAtCaret(`${emoji} `);
                              setShowComposeEmojiPicker(false);
                            }}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>

                {replySuggestions.length > 0 ? (
                  <div className="px-6 py-3 border-b border-pearl-200 bg-pearl-50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500">
                        Suggested replies {replySuggestionsSource ? `(${replySuggestionsSource})` : ""}
                      </div>
                      {replySuggestionStatus ? (
                        <span className="text-[10px] px-2 py-[2px] rounded-full bg-sky-100 text-sky-800">
                          {replySuggestionStatus}
                        </span>
                      ) : null}
                      <div className="ml-auto inline-flex border border-pearl-200 rounded-md overflow-hidden text-[11px]">
                        <button
                          className={`px-2 py-1 ${replySuggestionInsertMode === "replace" ? "bg-accent-600 text-white" : "bg-white text-slateLux-500 hover:bg-pearl-50"}`}
                          onClick={() => setReplySuggestionInsertMode("replace")}
                        >
                          Replace
                        </button>
                        <button
                          className={`px-2 py-1 border-l border-pearl-200 ${replySuggestionInsertMode === "append" ? "bg-accent-600 text-white" : "bg-white text-slateLux-500 hover:bg-pearl-50"}`}
                          onClick={() => setReplySuggestionInsertMode("append")}
                        >
                          Append
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {replySuggestions.map((suggestion, index) => (
                        <div
                          key={`reply-suggestion-${index}`}
                          className="text-left text-[12px] leading-snug px-3 py-2 rounded-lg border border-pearl-200 bg-white max-w-[calc(50%-8px)]"
                        >
                          <div className="text-slateLux-700">{suggestion.slice(0, 180)}</div>
                          <div className="mt-2 flex items-center gap-2">
                            <button
                              className="px-2 py-1 rounded border border-pearl-200 text-[10px] font-semibold hover:bg-pearl-50 transition"
                              onClick={() => applyReplySuggestion(suggestion)}
                            >
                              Use
                            </button>
                            <button
                              className="px-2 py-1 rounded border border-pearl-200 text-[10px] font-semibold hover:bg-pearl-50 transition"
                              onClick={() => copyReplySuggestion(suggestion)}
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="px-6 py-4 border-b border-pearl-200">
                  <div className="relative">
                    {!(compose.text || "").trim() ? <div className="absolute left-5 top-4 text-slate-400 text-[16px] pointer-events-none">Write your message...</div> : null}
                    <div
                      ref={composeTextRef}
                      contentEditable
                      spellCheck
                      suppressContentEditableWarning
                      className="w-full min-h-[340px] rounded-xl3 bg-pearl-100 px-5 py-4 text-[16px] leading-[1.7] outline-none border border-transparent focus:border-accent-600/40 focus-visible:ring-2 focus-visible:ring-accent-600/25"
                      onInput={syncComposeFromEditor}
                      onKeyUp={saveComposeSelection}
                      onMouseUp={saveComposeSelection}
                      onFocus={saveComposeSelection}
                    />
                  </div>
                  <div
                    className={`mt-3 border-2 border-dashed rounded-xl3 p-3 text-xs transition ${dragActive ? "border-accent-600 bg-accent-600/10" : "border-pearl-200 bg-pearl-50"}`}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      setDragActive(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragActive(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                      runAction("addAttachments", async () => addComposeAttachments(e.dataTransfer?.files));
                    }}
                  >
                    Drag and drop attachments or choose files
                    <input ref={composeFileInputRef} type="file" multiple className="block mt-2" onChange={(e) => runAction("addAttachments", async () => addComposeAttachments(e.target.files))} />
                    {(compose.attachments || []).length > 0 ? (
                      <div className="mt-2 border rounded bg-white">
                        {(compose.attachments || []).map((attachment, index) => (
                          <div key={`${attachment.fileName}-${index}`} className="flex justify-between px-2 py-1 text-xs border-b last:border-b-0">
                            <button className="text-left truncate pr-2 hover:underline" onClick={() => openQuickLook(attachment)} title="Preview">
                              {attachment.fileName}
                            </button>
                            <div className="flex items-center gap-2 shrink-0">
                              <button className="text-slate-600 hover:underline" onClick={() => openQuickLook(attachment)}>
                                Preview
                              </button>
                              <button
                                className="text-red-600"
                                onClick={() =>
                                  setCompose((prev) => ({
                                    ...prev,
                                    attachments: (prev.attachments || []).filter((_, i) => i !== index)
                                  }))
                                }
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="px-6 py-4 flex items-center gap-3 bg-pearl-50">
                  <span className="text-[14px] text-slateLux-500">Ctrl+Enter to send</span>
                  <button
                    className="ml-auto border border-pearl-200 rounded-xl3 px-5 py-2 text-[16px] font-medium text-slateLux-900 hover:bg-white transition"
                    onClick={() => {
                      setCompose(defaultCompose);
                      setReplySuggestions([]);
                      setReplySuggestionsSource("");
                      setReplySuggestionStatus("");
                    }}
                  >
                    Discard
                  </button>
                  <button className="bg-ink-800 text-white rounded-xl3 px-5 py-2 text-[16px] font-semibold hover:bg-ink-900 transition disabled:opacity-60" disabled={Boolean(busyAction)} onClick={() => runAction("sendCompose", sendCompose)}>
                    Send
                  </button>
                  <button className="border border-pearl-200 rounded-xl3 px-4 py-2 text-sm text-slateLux-700 hover:bg-pearl-50 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25" disabled={Boolean(busyAction)} onClick={() => runAction("saveDraft", saveDraft)}>
                    Autosave Draft
                  </button>
                </div>

                {outboundStatus ? <div className="mx-6 mb-4 text-xs border rounded px-2 py-1">Outbound status: {outboundStatus.status}</div> : null}
              </div>
            </div>
          ) : activePage === "upgrade" ? (
            <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden border border-[#edebe9] bg-white">
              <div className="flex h-12 shrink-0 items-center gap-3 border-b border-[#edebe9] bg-white px-4">
                <button
                  type="button"
                  className="text-sm font-semibold text-[#323130] transition hover:text-[#201f1e]"
                  onClick={() => setActivePage("settings")}
                >
                  ← Back
                </button>
                <div className="text-sm font-semibold text-[#201f1e]">Upgrade</div>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    className="h-8 rounded-[2px] border border-[#edebe9] bg-white px-3 text-xs font-semibold text-[#323130] shadow-sm transition hover:bg-[#edebe9]"
                    onClick={() => loadBilling()}
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-hidden lg:grid-cols-12">
                <section className="col-span-12 overflow-y-auto border-b border-[#edebe9] p-4 lg:col-span-5 lg:border-b-0 lg:border-r">
                  <div className="rounded-[2px] border border-[#edebe9] bg-[#faf9f8] p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-[#605e5c]">Notice</div>
                    <div className="mt-1 text-sm font-semibold text-[#201f1e]">Why you’re seeing this</div>
                    <div className="mt-2 text-sm leading-relaxed text-[#605e5c]">
                      {upgradeNudge
                        ? upgradeNudge
                        : "Upgrade to increase your storage, email accounts, and social DM limits. You can pay with Stripe, Paystack, Fincra, or OPay."}
                    </div>
                    <button
                      type="button"
                      className="mt-4 h-8 rounded-[2px] border border-[#edebe9] bg-white px-3 text-xs font-semibold text-[#323130] transition hover:bg-[#edebe9]"
                      onClick={() => setUpgradeNudge("")}
                    >
                      Dismiss message
                    </button>
                  </div>

                  {billingMe ? (
                    <div className="mt-3 rounded-[2px] border border-[#edebe9] bg-white p-4 shadow-sm">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-[#605e5c]">Current plan</div>
                      <div className="mt-0.5 text-xl font-semibold text-[#201f1e]">
                        {billingMe.plan?.name || billingMe.planKey || "Starter"}
                      </div>
                      <div className="mt-1 text-xs text-[#605e5c]">
                        {billingMe.subscription?.provider ? `Provider: ${billingMe.subscription.provider}` : "Provider: —"}
                      </div>
                      <div className="mt-4 border-t border-[#edebe9] pt-4">
                        <div className="flex items-center justify-between text-xs font-medium text-[#605e5c]">
                          <span>Storage usage</span>
                          <span className="tabular-nums text-[#323130]">
                            {(billingMe.usage?.storageGb ?? 0).toFixed?.(2)
                              ? billingMe.usage.storageGb.toFixed(2)
                              : billingMe.usage?.storageGb ?? 0}
                            GB /
                            {(billingMe.usage?.storageLimitGb ?? 0).toFixed?.(2)
                              ? billingMe.usage.storageLimitGb.toFixed(2)
                              : billingMe.usage?.storageLimitGb ?? 0}
                            GB
                          </span>
                        </div>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-[1px] bg-[#edebe9]">
                          <div
                            className="h-full bg-[#0078d4]"
                            style={{
                              width: `${Math.min(
                                100,
                                Math.round(
                                  ((Number(billingMe.usage?.storageBytes || 0) || 0) /
                                    Math.max(1, Number(billingMe.usage?.storageLimitBytes || 0) || 1)) *
                                    100
                                )
                              )}%`
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </section>

                <section className="col-span-12 min-h-0 overflow-y-auto bg-[#faf9f8] p-4 lg:col-span-7">
                  <div className="mb-4 border-b border-[#edebe9] pb-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-[#605e5c]">Plans</div>
                    <h3 className="text-lg font-semibold text-[#201f1e] sm:text-xl">Choose a plan</h3>
                    <div className="mt-0.5 text-xs text-[#605e5c]">Select a tier and payment provider.</div>
                  </div>
                  {billingLoading ? <div className="text-sm text-[#605e5c]">Loading plans…</div> : null}
                  {billingError ? <div className="text-sm text-danger-600">{billingError}</div> : null}
                  <div className="grid gap-3">
                    {billingPlans.map((plan) => {
                      const key = String(plan.key || "");
                      const isCurrent = String(billingMe?.planKey || "") === key;
                      const busyStripe = billingCheckoutBusy === `stripe:${key}`;
                      const busyPaystack = billingCheckoutBusy === `paystack:${key}`;
                      const busyFincra = billingCheckoutBusy === `fincra:${key}`;
                      const busyOpay = billingCheckoutBusy === `opay:${key}`;
                      const busyAny = !!billingCheckoutBusy;
                      return (
                        <div
                          key={key}
                          className={`rounded-[2px] border bg-white p-4 shadow-sm ${
                            isCurrent ? "border-[#0078d4] ring-1 ring-[#0078d4]/20" : "border-[#edebe9]"
                          }`}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-lg font-semibold text-[#201f1e]">{plan.name || key}</div>
                              <div className="mt-1 text-xs leading-relaxed text-[#605e5c]">
                                {plan.maxEmailAccounts ?? "—"} emails · {plan.maxSocialAccountsPerPlatform ?? "—"} socials/platform · {plan.storageLimitGb ?? "—"}{" "}
                                GB storage
                              </div>
                            </div>
                            {isCurrent ? (
                              <span className="inline-flex shrink-0 items-center rounded-[2px] border border-[#0078d4]/40 bg-[#deecf9] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#0078d4]">
                                Current
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-4 grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              className="h-9 rounded-[2px] bg-[#0078d4] px-2 text-center text-[11px] font-semibold text-white transition hover:bg-[#106ebe] disabled:opacity-50"
                              disabled={busyStripe || busyAny}
                              onClick={() => startCheckout("stripe", key)}
                            >
                              {busyStripe ? "Stripe…" : "Pay with Stripe"}
                            </button>
                            <button
                              type="button"
                              className="h-9 rounded-[2px] border border-[#edebe9] bg-white px-2 text-center text-[11px] font-semibold text-[#323130] transition hover:bg-[#f3f2f1] disabled:opacity-50"
                              disabled={busyPaystack || busyAny}
                              onClick={() => startCheckout("paystack", key)}
                            >
                              {busyPaystack ? "Paystack…" : "Pay with Paystack"}
                            </button>
                            <button
                              type="button"
                              className="h-9 rounded-[2px] border border-[#edebe9] bg-white px-2 text-center text-[11px] font-semibold text-[#323130] transition hover:bg-[#f3f2f1] disabled:opacity-50"
                              disabled={busyFincra || busyAny}
                              onClick={() => startCheckout("fincra", key)}
                            >
                              {busyFincra ? "Fincra…" : "Pay with Fincra"}
                            </button>
                            <button
                              type="button"
                              className="h-9 rounded-[2px] border border-[#edebe9] bg-white px-2 text-center text-[11px] font-semibold text-[#323130] transition hover:bg-[#f3f2f1] disabled:opacity-50"
                              disabled={busyOpay || busyAny}
                              onClick={() => startCheckout("opay", key)}
                            >
                              {busyOpay ? "OPay…" : "Pay with OPay"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {billingPlans.length === 0 && !billingLoading ? (
                      <div className="text-sm text-[#605e5c]">No plans available yet.</div>
                    ) : null}
                  </div>
                </section>
              </div>
            </div>
          ) : activePage === "health" ? (
            <div className="h-full border border-pearl-200 rounded-xl3 overflow-hidden bg-white">
              <div className="h-[52px] px-4 flex items-center gap-3 border-b border-pearl-200 bg-white/85 backdrop-blur">
                <div className="text-sm font-semibold text-slateLux-900">System Health</div>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    className="px-3 py-2 rounded-xl2 border border-pearl-200 bg-white text-xs font-semibold hover:bg-pearl-50 transition"
                    onClick={() => loadHealthSummary()}
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div className="p-5">
                {healthLoading ? <div className="text-sm text-slateLux-500">Loading health…</div> : null}
                {healthError ? <div className="text-sm text-danger-600">{healthError}</div> : null}

                {healthSummary ? (
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-4 nlq-surface p-4">
                      <div className="text-xs text-slateLux-500">Database</div>
                      <div className="mt-1 text-lg font-semibold text-slateLux-900">
                        {healthSummary.database?.healthy ? "Healthy" : "Degraded"}
                      </div>
                    </div>
                    <div className="col-span-4 nlq-surface p-4">
                      <div className="text-xs text-slateLux-500">Queue</div>
                      <div className="mt-1 text-sm text-slateLux-900">
                        <span className="font-semibold">{Number(healthSummary.queue?.queued || 0)}</span> queued ·{" "}
                        <span className="font-semibold">{Number(healthSummary.queue?.deadLetter || 0)}</span> dead-letter
                      </div>
                    </div>
                    <div className="col-span-4 nlq-surface p-4">
                      <div className="text-xs text-slateLux-500">Overall</div>
                      <div className="mt-1 text-lg font-semibold text-slateLux-900">{healthSummary.ok ? "OK" : "Needs attention"}</div>
                    </div>

                    <div className="col-span-7 nlq-surface p-4">
                      <div className="text-sm font-semibold text-slateLux-900">Last sync</div>
                      {healthSummary.lastSync ? (
                        <div className="mt-2 text-sm text-slateLux-700">
                          <div>
                            <span className="text-slateLux-500">Status:</span> {healthSummary.lastSync.status}
                          </div>
                          <div>
                            <span className="text-slateLux-500">Account:</span> {healthSummary.lastSync.accountId}
                          </div>
                          <div>
                            <span className="text-slateLux-500">Folder:</span> {healthSummary.lastSync.folderId}
                          </div>
                          <div className="mt-1 text-xs text-slateLux-500">
                            Started {healthSummary.lastSync.startedAt || "—"} · Completed {healthSummary.lastSync.completedAt || "—"}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2 text-sm text-slateLux-500">No sync runs yet.</div>
                      )}
                    </div>

                    <div className="col-span-5 nlq-surface p-4">
                      <div className="text-sm font-semibold text-slateLux-900">Last send</div>
                      {healthSummary.lastOutbound ? (
                        <div className="mt-2 text-sm text-slateLux-700">
                          <div>
                            <span className="text-slateLux-500">Status:</span> {healthSummary.lastOutbound.status}
                          </div>
                          <div className="mt-1 text-xs text-slateLux-500">
                            Created {healthSummary.lastOutbound.createdAt || "—"} · Sent {healthSummary.lastOutbound.sentAt || "—"}
                          </div>
                          {healthSummary.lastOutbound.lastError ? (
                            <div className="mt-2 text-xs text-danger-600 break-words">{healthSummary.lastOutbound.lastError}</div>
                          ) : null}
                        </div>
                      ) : (
                        <div className="mt-2 text-sm text-slateLux-500">No outbound messages yet.</div>
                      )}
                    </div>
                  </div>
                ) : (
                  !healthLoading && !healthError ? <div className="text-sm text-slateLux-500">No health data yet.</div> : null
                )}
              </div>
            </div>
          ) : activePage === "templates" ? (
            <div className="h-full min-h-0 flex flex-col border border-pearl-200 rounded-xl3 overflow-hidden bg-white">
              <div className={`grid min-h-0 flex-1 ${templateEditorOpen ? "grid-cols-1" : "grid-cols-[210px_1fr]"} h-full`}>
                {!templateEditorOpen ? (
                  <aside className="border-r border-pearl-200 px-3 py-4 bg-pearl-50 overflow-y-auto">
                    <div className="text-sm font-semibold text-slateLux-500 px-2 mb-3">For you</div>
                    {[
                      "Emails",
                      "Presentations",
                      "Social media",
                      "Photo editor",
                      "Videos",
                      "Docs",
                      "Whiteboards",
                      "Websites",
                      "Upload",
                      "More"
                    ].map((item) => (
                      <button
                        key={item}
                        className={`w-full text-left text-sm rounded-lg px-3 py-2 mb-1 ${
                          templateCategoryFilter === item ? "bg-accent-600/10 text-accent-700 font-semibold" : "text-slateLux-700 hover:bg-pearl-100"
                        }`}
                        onClick={() => setTemplateCategoryFilter(item)}
                      >
                        {item}
                      </button>
                    ))}
                  </aside>
                ) : null}
                <section className={`${templateEditorOpen ? "p-3 h-full min-h-0" : "p-4 h-[calc(100vh-230px)]"} overflow-hidden flex flex-col min-h-0`}>
                  {templateEditorOpen ? (
                    <>
                    <div className="h-full flex flex-col gap-3 min-h-0 flex-1">
                      <div className="min-h-[52px] px-4 py-2 rounded-xl3 border border-pearl-200 bg-white flex flex-wrap items-center gap-2 sm:gap-3 shadow-luxSoft shrink-0">
                        <button className="text-sm text-slateLux-700 hover:text-slateLux-900 transition" onClick={() => setTemplateEditorOpen(false)}>
                          ← Back
                        </button>
                        <input
                          className="h-9 min-w-[180px] max-w-[260px] border border-pearl-200 rounded-xl2 px-3 text-sm font-semibold bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                          value={templateDraft.name}
                          onChange={(e) => setTemplateDraft((prev) => ({ ...prev, name: e.target.value }))}
                          placeholder="Template name"
                        />
                        <div className="text-xs text-slateLux-500">{templateSavedAt ? "Saved" : "Not Saved"}</div>
                        <div className="ml-0 sm:ml-auto flex flex-wrap items-center justify-end gap-1.5 sm:gap-2">
                          <button
                            className="px-2.5 py-1.5 rounded-md border border-pearl-200 text-xs hover:bg-pearl-50 transition disabled:opacity-40"
                            disabled={templateHistoryIndex <= 0}
                            onClick={undoTemplateChange}
                            title="Undo (Cmd/Ctrl+Z)"
                          >
                            Undo
                          </button>
                          <button
                            className="px-2.5 py-1.5 rounded-md border border-pearl-200 text-xs hover:bg-pearl-50 transition disabled:opacity-40"
                            disabled={templateHistoryIndex >= templateHistory.length - 1}
                            onClick={redoTemplateChange}
                            title="Redo (Cmd/Ctrl+Shift+Z)"
                          >
                            Redo
                          </button>
                          {["desktop", "tablet", "mobile"].map((device) => (
                            <button
                              key={device}
                              className={`px-2.5 py-1 rounded-md text-xs border ${
                                templatePreviewDevice === device ? "bg-accent-600 text-white border-accent-600" : "bg-white text-slateLux-700 border-pearl-200 hover:bg-pearl-50 transition"
                              }`}
                              onClick={() => {
                                setTemplatePreviewDevice(device);
                                pushNotification(`Preview: ${device}`);
                              }}
                            >
                              {device}
                            </button>
                          ))}
                          <button
                            className="px-3 py-1.5 rounded-md border border-pearl-200 text-sm hover:bg-pearl-50 transition"
                            onClick={() => {
                              applyTemplateToCompose(templateDraft);
                              pushNotification("Template applied to compose.");
                            }}
                          >
                            Use in Compose
                          </button>
                          <button
                            className="px-3 py-1.5 rounded-md border border-pearl-200 text-sm hover:bg-pearl-50 transition"
                            onClick={() => {
                              setShowAssetsManager(true);
                              pushNotification("Assets Manager opened.");
                            }}
                          >
                            Assets Manager
                          </button>
                          <button
                            className="px-3 py-1.5 rounded-md border border-pearl-200 text-sm hover:bg-pearl-50 transition"
                            type="button"
                            onClick={() => {
                              const mjml = exportTemplateToMjml(templateDraft);
                              if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
                                navigator.clipboard.writeText(mjml).then(
                                  () => pushNotification("MJML copied to clipboard."),
                                  () => pushNotification("Could not copy MJML (clipboard).")
                                );
                              } else {
                                pushNotification("Clipboard not available in this browser.");
                              }
                            }}
                          >
                            Copy MJML
                          </button>
                          <button
                            className="px-3 py-1.5 rounded-md border border-pearl-200 text-sm hover:bg-pearl-50 transition"
                            type="button"
                            onClick={() => {
                              const mjml = exportTemplateToMjml(templateDraft);
                              const base = String(templateDraft.name || "template").replace(/[^\w.-]+/g, "-");
                              const blob = new Blob([mjml], { type: "application/xml;charset=utf-8" });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `${base || "template"}.mjml`;
                              a.click();
                              URL.revokeObjectURL(url);
                              pushNotification("MJML file download started.");
                            }}
                          >
                            Download .mjml
                          </button>
                          {isSuperAdmin ? (
                            <button className="px-3 py-1.5 rounded-md border border-accent-600 text-accent-600 text-sm hover:bg-accent-600/10 transition" onClick={publishTemplateAsPredefined}>
                              Publish Predefined
                            </button>
                          ) : null}
                          <button className="px-3 py-1.5 rounded-md bg-accent-600 text-white text-sm hover:bg-accent-700 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/35" onClick={saveTemplateDraft}>
                            Save Template
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-[minmax(200px,240px)_minmax(0,1.15fr)_minmax(0,1fr)] gap-3 md:gap-4 flex-1 min-h-0 items-stretch">
                        <aside className="border border-pearl-200 rounded-xl3 bg-white overflow-y-auto min-h-0 max-h-full shadow-luxSoft">
                          <div className="px-4 py-3 border-b border-pearl-200 sticky top-0 bg-white/95 backdrop-blur z-10">
                            <div className="text-sm font-semibold">Elements</div>
                            <div className="text-[11px] text-slateLux-500 mt-1 leading-snug">
                              Add blocks below · Reorder on canvas · Rich edit on the right
                            </div>
                          </div>
                          <div className="p-3 grid grid-cols-2 gap-2.5">
                            {templateElementBlocks.map((item) => (
                              <button
                                key={item.id}
                                className="min-h-[4.25rem] rounded-xl2 border border-pearl-200 bg-pearl-50 hover:bg-white hover:border-accent-600/25 transition text-slateLux-700 flex flex-col items-center justify-center px-1"
                                onClick={() => addTemplateElement(item.id)}
                              >
                                <span className="text-lg leading-none">{item.icon}</span>
                                <span className="text-[11px] mt-1.5 text-center leading-tight">{item.label}</span>
                              </button>
                            ))}
                          </div>
                        </aside>

                        <div className="border border-pearl-200 rounded-xl3 bg-gradient-to-b from-pearl-50 to-pearl-100 p-3 sm:p-4 md:p-5 flex flex-col min-h-0 overflow-y-auto shadow-luxSoft">
                          <div
                            className={`bg-white rounded-xl2 border border-pearl-200 overflow-hidden transition-all duration-200 shadow-sm ${templatePreviewWidthClass} ${
                              templatePreviewDevice === "desktop" ? "w-full" : "mx-auto w-full"
                            }`}
                          >
                            <div className="px-5 py-3.5 border-b border-pearl-200 bg-pearl-50/80">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-slateLux-500">Live preview</div>
                              <div className="text-xs text-slateLux-600 mt-0.5">Select a block, then use format & design tools on the right</div>
                            </div>
                            <div className="px-5 py-6 space-y-4">
                              {templateBlocks.map((block) => (
                                <div
                                  key={block.id}
                                  draggable={!lockedTemplateBlockIds.includes(block.id)}
                                  onDragStart={() => {
                                    if (lockedTemplateBlockIds.includes(block.id)) return;
                                    setDraggingTemplateBlockId(block.id);
                                  }}
                                  onDragOver={(event) => event.preventDefault()}
                                  onDrop={() => {
                                    if (lockedTemplateBlockIds.includes(block.id)) return;
                                    reorderTemplateBlock(draggingTemplateBlockId, block.id);
                                    setDraggingTemplateBlockId("");
                                  }}
                                  onClick={() => setSelectedTemplateBlockId(block.id)}
                                  className={`group rounded-md border p-2 transition cursor-pointer relative ${
                                    selectedTemplateBlock?.id === block.id ? "border-accent-600 bg-accent-600/10" : "border-transparent hover:border-pearl-200 hover:bg-pearl-50/60"
                                  }`}
                                >
                                  <div className="absolute top-2 right-2 hidden group-hover:flex items-center gap-1 bg-white/95 border border-pearl-200 rounded-md px-1.5 py-1 shadow-sm">
                                    <button className="text-[10px] text-slateLux-700 hover:text-slateLux-900 transition" title="Move up" onClick={(e) => { e.stopPropagation(); moveTemplateBlock(block.id, "up"); }}>
                                      ↑
                                    </button>
                                    <button className="text-[10px] text-slateLux-700 hover:text-slateLux-900 transition" title="Move down" onClick={(e) => { e.stopPropagation(); moveTemplateBlock(block.id, "down"); }}>
                                      ↓
                                    </button>
                                    <button className="text-[10px] text-slateLux-700 hover:text-slateLux-900 transition" title="Duplicate block" onClick={(e) => { e.stopPropagation(); duplicateTemplateBlock(block.id); }}>
                                      ⧉
                                    </button>
                                    <button
                                      className="text-[10px] text-slateLux-700 hover:text-slateLux-900 transition"
                                      title={lockedTemplateBlockIds.includes(block.id) ? "Unlock block" : "Lock block"}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleLockTemplateBlock(block.id);
                                      }}
                                    >
                                      {lockedTemplateBlockIds.includes(block.id) ? "🔒" : "🔓"}
                                    </button>
                                  </div>
                                  {block.type === "heading" ? (
                                    <div style={blockOuterStyle(block.style, templatePreviewDevice)}>
                                      {block.html && String(block.html).trim() !== "" ? (
                                        <h3
                                          style={blockInnerTypographyStyle(block.style, templatePreviewDevice, true)}
                                          dangerouslySetInnerHTML={{ __html: block.html }}
                                        />
                                      ) : (
                                        <h3 style={blockInnerTypographyStyle(block.style, templatePreviewDevice, true)}>{block.content || "Template heading"}</h3>
                                      )}
                                    </div>
                                  ) : null}
                                  {block.type === "text" ? (
                                    <div style={blockOuterStyle(block.style, templatePreviewDevice)}>
                                      {block.html && String(block.html).trim() !== "" ? (
                                        <div
                                          style={blockInnerTypographyStyle(block.style, templatePreviewDevice, false)}
                                          dangerouslySetInnerHTML={{ __html: block.html }}
                                        />
                                      ) : (
                                        <div style={blockInnerTypographyStyle(block.style, templatePreviewDevice, false)} className="whitespace-pre-wrap">
                                          {block.content || "Write your content..."}
                                        </div>
                                      )}
                                    </div>
                                    ) : null}
                                  {block.type === "columns" ? (
                                    <div
                                      style={{
                                        ...blockOuterStyle(block.style, templatePreviewDevice),
                                        display: "flex",
                                        flexDirection: templatePreviewDevice === "mobile" && block.stackMobile !== false ? "column" : "row",
                                        gap: Number(block.columnGap) || 20,
                                        alignItems: "stretch"
                                      }}
                                    >
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        {block.html && String(block.html).trim() !== "" ? (
                                          <div
                                            style={blockInnerTypographyStyle(mergeDefaultStyle({ type: "text", style: block.style }), templatePreviewDevice, false)}
                                            dangerouslySetInnerHTML={{ __html: block.html }}
                                          />
                                        ) : (
                                          <div
                                            style={blockInnerTypographyStyle(mergeDefaultStyle({ type: "text", style: block.style }), templatePreviewDevice, false)}
                                            className="whitespace-pre-wrap"
                                          >
                                            {block.content || "Left column"}
                                          </div>
                                        )}
                                      </div>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        {block.rightHtml && String(block.rightHtml).trim() !== "" ? (
                                          <div
                                            style={blockInnerTypographyStyle(mergeDefaultStyle({ type: "text", style: block.style }), templatePreviewDevice, false)}
                                            dangerouslySetInnerHTML={{ __html: block.rightHtml }}
                                          />
                                        ) : (
                                          <div
                                            style={blockInnerTypographyStyle(mergeDefaultStyle({ type: "text", style: block.style }), templatePreviewDevice, false)}
                                            className="whitespace-pre-wrap"
                                          >
                                            {block.rightContent || "Right column"}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : null}
                                  {block.type === "image" ? (
                                    block.content ? (
                                      <img src={block.content} alt={templateDraft.name} className={`w-full ${templatePreviewMediaHeightClass} object-cover rounded-md border border-pearl-200`} />
                                    ) : (
                                      <div className={`w-full ${templatePreviewMediaHeightClass} rounded-md border border-dashed border-pearl-200 bg-pearl-50 flex items-center justify-center text-sm text-slateLux-500`}>
                                        Add image
                                      </div>
                                    )
                                  ) : null}
                                  {block.type === "video" ? (
                                    block.content ? (
                                      <video className="w-full rounded-md border border-pearl-200" controls src={block.content} />
                                    ) : (
                                      <div className={`w-full ${templatePreviewMediaHeightClass} rounded-md border border-dashed border-pearl-200 bg-pearl-50 flex items-center justify-center text-sm text-slateLux-500`}>
                                        Add video
                                      </div>
                                    )
                                  ) : null}
                                  {block.type === "audio" ? (
                                    block.content ? (
                                      <audio className="w-full" controls src={block.content} />
                                    ) : (
                                      <div className="w-full h-14 rounded-md border border-dashed border-pearl-200 bg-pearl-50 flex items-center justify-center text-sm text-slateLux-500">
                                        Add audio
                                      </div>
                                    )
                                  ) : null}
                                  {block.type === "button" ? (
                                    <div style={{ textAlign: mergeDefaultStyle(block).textAlign || "left", width: "100%" }}>
                                      <button type="button" style={buttonPreviewStyle(block, templatePreviewDevice)}>
                                        {block.content || "Button"}
                                      </button>
                                    </div>
                                  ) : null}
                                  {block.type === "divider" ? <div className="h-px bg-pearl-200 w-full" /> : null}
                                  {block.type === "social" ? (
                                    <div style={blockOuterStyle(block.style, templatePreviewDevice)}>
                                      {block.html && String(block.html).trim() !== "" ? (
                                        <div style={blockInnerTypographyStyle(block.style, templatePreviewDevice, false)} dangerouslySetInnerHTML={{ __html: block.html }} />
                                      ) : (
                                        <div style={blockInnerTypographyStyle(block.style, templatePreviewDevice, false)}>{block.content || "Instagram • X • LinkedIn"}</div>
                                      )}
                                    </div>
                                  ) : null}
                                  {block.type === "footer" ? (
                                    <div style={blockOuterStyle(block.style, templatePreviewDevice)}>
                                      {block.html && String(block.html).trim() !== "" ? (
                                        <div style={blockInnerTypographyStyle(block.style, templatePreviewDevice, false)} dangerouslySetInnerHTML={{ __html: block.html }} />
                                      ) : (
                                        <div style={blockInnerTypographyStyle(block.style, templatePreviewDevice, false)}>{block.content || "Footer text"}</div>
                                      )}
                                    </div>
                                  ) : null}
                                  {block.type === "logo" ? <div className="h-10 w-10 rounded bg-pearl-200 border border-pearl-200" /> : null}
                                  {block.type === "code" ? <pre className="text-[11px] bg-ink-900 text-emerald-200 p-2 rounded overflow-auto">{block.content || "<p>Code block</p>"}</pre> : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <aside className="border border-pearl-200 rounded-xl3 bg-white p-4 sm:p-5 flex flex-col min-h-0 overflow-y-auto md:max-h-full shadow-luxSoft">
                          <div className="flex items-center border-b border-pearl-200 mb-4 shrink-0">
                            <button
                              className={`px-3 py-2 text-sm ${templateEditorTab === "edit" ? "text-accent-600 border-b-2 border-accent-600 font-semibold" : "text-slateLux-500"}`}
                              onClick={() => setTemplateEditorTab("edit")}
                            >
                              Edit
                            </button>
                            <button
                              className={`px-3 py-2 text-sm ${templateEditorTab === "visibility" ? "text-accent-600 border-b-2 border-accent-600 font-semibold" : "text-slateLux-500"}`}
                              onClick={() => setTemplateEditorTab("visibility")}
                            >
                              Visibility
                            </button>
                          </div>
                          {templateEditorTab === "edit" ? (
                            <div className="space-y-3">
                              <div className="rounded-xl2 border border-pearl-200 p-3 sm:p-4 bg-gradient-to-b from-pearl-50/90 to-white">
                                <div className="text-xs font-semibold text-slateLux-500 mb-1">Selected block</div>
                                {selectedTemplateBlock &&
                                ["heading", "text", "footer", "social"].includes(selectedTemplateBlock.type) ? (
                                  <p className="text-[11px] text-slateLux-600 mb-3 leading-relaxed">
                                    Toolbar: bold, lists, links & merge tags. Below: typography, spacing, and background.
                                  </p>
                                ) : null}
                                {selectedTemplateBlock ? (
                                  <>
                                    <div className="flex items-center justify-between mb-3">
                                      <span className="text-sm font-semibold capitalize">{selectedTemplateBlock.type}</span>
                                      <div className="flex items-center gap-2">
                                        <button
                                          className="text-xs text-slateLux-700 hover:underline"
                                          onClick={() => toggleLockTemplateBlock(selectedTemplateBlock.id)}
                                        >
                                          {lockedTemplateBlockIds.includes(selectedTemplateBlock.id) ? "Unlock" : "Lock"}
                                        </button>
                                        <button className="text-xs text-red-600 hover:underline" onClick={() => deleteTemplateBlock(selectedTemplateBlock.id)}>
                                          Delete
                                        </button>
                                      </div>
                                    </div>
                                    {selectedTemplateBlock.type === "columns" ? (
                                      <>
                                        <div className="text-[11px] font-semibold text-slateLux-600 mb-1">Left column</div>
                                        <RichTextBlockEditor
                                          key={`${selectedTemplateBlock.id}-L`}
                                          html={selectedTemplateBlock.html}
                                          plainFallback={selectedTemplateBlock.content}
                                          disabled={lockedTemplateBlockIds.includes(selectedTemplateBlock.id)}
                                          placeholder="Left column"
                                          onChange={({ html, plain }) =>
                                            updateTemplateBlock(selectedTemplateBlock.id, { html, content: plain })
                                          }
                                        />
                                        <div className="text-[11px] font-semibold text-slateLux-600 mt-3 mb-1">Right column</div>
                                        <RichTextBlockEditor
                                          key={`${selectedTemplateBlock.id}-R`}
                                          html={selectedTemplateBlock.rightHtml}
                                          plainFallback={selectedTemplateBlock.rightContent}
                                          disabled={lockedTemplateBlockIds.includes(selectedTemplateBlock.id)}
                                          placeholder="Right column"
                                          onChange={({ html, plain }) =>
                                            updateTemplateBlock(selectedTemplateBlock.id, { rightHtml: html, rightContent: plain })
                                          }
                                        />
                                        <label className="block text-[10px] text-slateLux-500 mt-3 mb-1">Column gap (px)</label>
                                        <input
                                          type="number"
                                          min={8}
                                          max={64}
                                          className="w-full border border-pearl-200 rounded-xl2 px-3 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                                          value={Number(selectedTemplateBlock.columnGap) || 20}
                                          disabled={lockedTemplateBlockIds.includes(selectedTemplateBlock.id)}
                                          onChange={(e) =>
                                            updateTemplateBlock(selectedTemplateBlock.id, { columnGap: Number(e.target.value) || 20 })
                                          }
                                        />
                                        <label className="flex items-center gap-2 text-xs text-slateLux-700 mt-3">
                                          <input
                                            type="checkbox"
                                            checked={selectedTemplateBlock.stackMobile !== false}
                                            disabled={lockedTemplateBlockIds.includes(selectedTemplateBlock.id)}
                                            onChange={(e) =>
                                              updateTemplateBlock(selectedTemplateBlock.id, { stackMobile: e.target.checked })
                                            }
                                          />
                                          Stack columns on mobile preview
                                        </label>
                                        <div className="mt-3 grid grid-cols-2 gap-2">
                                          <div>
                                            <div className="text-[10px] text-slateLux-500 mb-1">Row padding top</div>
                                            <input
                                              type="number"
                                              min={0}
                                              max={64}
                                              className="w-full border border-pearl-200 rounded-lg px-2 py-1.5 text-xs bg-white"
                                              value={Number(mergeDefaultStyle(selectedTemplateBlock).paddingTop ?? 8)}
                                              disabled={lockedTemplateBlockIds.includes(selectedTemplateBlock.id)}
                                              onChange={(e) =>
                                                patchTemplateBlockStyle(selectedTemplateBlock.id, {
                                                  paddingTop: Math.min(64, Math.max(0, Number(e.target.value) || 0))
                                                })
                                              }
                                            />
                                          </div>
                                          <div>
                                            <div className="text-[10px] text-slateLux-500 mb-1">Row padding bottom</div>
                                            <input
                                              type="number"
                                              min={0}
                                              max={64}
                                              className="w-full border border-pearl-200 rounded-lg px-2 py-1.5 text-xs bg-white"
                                              value={Number(mergeDefaultStyle(selectedTemplateBlock).paddingBottom ?? 8)}
                                              disabled={lockedTemplateBlockIds.includes(selectedTemplateBlock.id)}
                                              onChange={(e) =>
                                                patchTemplateBlockStyle(selectedTemplateBlock.id, {
                                                  paddingBottom: Math.min(64, Math.max(0, Number(e.target.value) || 0))
                                                })
                                              }
                                            />
                                          </div>
                                        </div>
                                      </>
                                    ) : ["heading", "text", "footer", "social"].includes(selectedTemplateBlock.type) ? (
                                      <>
                                        <RichTextBlockEditor
                                          key={selectedTemplateBlock.id}
                                          html={selectedTemplateBlock.html}
                                          plainFallback={selectedTemplateBlock.content}
                                          disabled={lockedTemplateBlockIds.includes(selectedTemplateBlock.id)}
                                          placeholder={
                                            selectedTemplateBlock.type === "heading"
                                              ? "Headline — merge tags & formatting"
                                              : selectedTemplateBlock.type === "footer"
                                                ? "Footer / legal copy"
                                                : "Rich body copy"
                                          }
                                          onChange={({ html, plain }) =>
                                            updateTemplateBlock(selectedTemplateBlock.id, { html, content: plain })
                                          }
                                        />
                                        <div className="mt-3 space-y-2">
                                          <div className="text-[11px] font-bold uppercase tracking-wide text-slateLux-500 px-0.5">
                                            Block design
                                          </div>
                                          <BlockDesignInspector
                                            block={selectedTemplateBlock}
                                            disabled={lockedTemplateBlockIds.includes(selectedTemplateBlock.id)}
                                            onPatchStyle={(partial) => patchTemplateBlockStyle(selectedTemplateBlock.id, partial)}
                                          />
                                        </div>
                                      </>
                                    ) : selectedTemplateBlock.type === "button" ? (
                                      <>
                                        <label className="block text-[10px] text-slateLux-500 mb-1">Label</label>
                                        <input
                                          className="w-full border border-pearl-200 rounded-xl2 px-3 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                                          placeholder="Button label"
                                          value={selectedTemplateBlock.content || ""}
                                          disabled={lockedTemplateBlockIds.includes(selectedTemplateBlock.id)}
                                          onChange={(e) => updateTemplateBlock(selectedTemplateBlock.id, { content: e.target.value })}
                                        />
                                        <label className="block text-[10px] text-slateLux-500 mt-2 mb-1">Destination URL</label>
                                        <input
                                          className="w-full border border-pearl-200 rounded-xl2 px-3 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                                          placeholder="https://"
                                          value={selectedTemplateBlock.href || ""}
                                          disabled={lockedTemplateBlockIds.includes(selectedTemplateBlock.id)}
                                          onChange={(e) => updateTemplateBlock(selectedTemplateBlock.id, { href: e.target.value })}
                                        />
                                        <div className="mt-3 space-y-2">
                                          <div className="text-[11px] font-bold uppercase tracking-wide text-slateLux-500 px-0.5">
                                            Button style
                                          </div>
                                          <BlockDesignInspector
                                            block={selectedTemplateBlock}
                                            disabled={lockedTemplateBlockIds.includes(selectedTemplateBlock.id)}
                                            onPatchStyle={(partial) => patchTemplateBlockStyle(selectedTemplateBlock.id, partial)}
                                          />
                                        </div>
                                      </>
                                    ) : ["image", "audio", "video"].includes(selectedTemplateBlock.type) ? (
                                      <>
                                        <label className="block text-[10px] text-slateLux-500 mb-1">Media URL</label>
                                        <input
                                          className="w-full border border-pearl-200 rounded-xl2 px-3 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                                          placeholder={`${selectedTemplateBlock.type} URL`}
                                          value={selectedTemplateBlock.content || ""}
                                          disabled={lockedTemplateBlockIds.includes(selectedTemplateBlock.id)}
                                          onChange={(e) => updateTemplateBlock(selectedTemplateBlock.id, { content: e.target.value })}
                                        />
                                      </>
                                    ) : (
                                      <textarea
                                        className="w-full min-h-[92px] border border-pearl-200 rounded-xl2 px-3 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                                        placeholder="Block content"
                                        value={selectedTemplateBlock.content || ""}
                                        disabled={lockedTemplateBlockIds.includes(selectedTemplateBlock.id)}
                                        onChange={(e) => updateTemplateBlock(selectedTemplateBlock.id, { content: e.target.value })}
                                      />
                                    )}
                                    {["image", "audio", "video"].includes(selectedTemplateBlock.type) ? (
                                      <div className="mt-3 rounded-xl2 border border-pearl-200 p-2 bg-white">
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="text-xs font-semibold text-slateLux-500">Asset Library ({selectedTemplateBlock.type})</div>
                                          <input
                                            type="file"
                                            multiple
                                            accept="image/*,audio/*,video/*"
                                            className="text-[10px]"
                                            onChange={(e) => addMediaFilesToLibrary(e.target.files, { shared: false })}
                                          />
                                        </div>
                                        {isSuperAdmin ? (
                                          <div className="mb-2">
                                            <label className="text-[10px] text-slateLux-500 mr-2">Admin publish:</label>
                                            <input
                                              type="file"
                                              multiple
                                              accept="image/*,audio/*,video/*"
                                              className="text-[10px]"
                                              onChange={(e) => addMediaFilesToLibrary(e.target.files, { shared: true })}
                                            />
                                          </div>
                                        ) : null}
                                        <div className="max-h-[160px] overflow-y-auto space-y-1">
                                          {mediaAssetsForSelectedBlock.slice(0, 20).map((asset) => (
                                            <div key={asset.id} className="flex items-center gap-2 border border-pearl-200 rounded px-2 py-1">
                                              <span className="text-[10px] uppercase text-slateLux-500">{asset.type}</span>
                                              <span className="text-[11px] truncate flex-1">{asset.name}</span>
                                              <button className="text-[10px] text-accent-600 hover:underline" onClick={() => applyMediaAssetToSelectedBlock(asset)}>
                                                Use
                                              </button>
                                              <button
                                                className="text-[10px] text-red-600 hover:underline"
                                                onClick={() => removeMediaAsset(asset.id, Boolean(asset.shared))}
                                              >
                                                Delete
                                              </button>
                                            </div>
                                          ))}
                                          {mediaAssetsForSelectedBlock.length === 0 ? <div className="text-[11px] text-slateLux-500">No assets. Upload media to start.</div> : null}
                                        </div>
                                      </div>
                                    ) : null}
                                  </>
                                ) : (
                                  <div className="text-xs text-slateLux-500">Select a block in the canvas.</div>
                                )}
                              </div>
                              <details className="rounded-xl2 border border-pearl-200 bg-white overflow-hidden">
                                <summary className="cursor-pointer select-none px-3 py-2.5 text-sm font-semibold text-slateLux-800 hover:bg-pearl-50 flex items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
                                  <span>Template metadata</span>
                                  <span className="text-[11px] font-normal text-slateLux-500">Optional · name, subject, shortcuts</span>
                                </summary>
                                <div className="px-3 pb-3 pt-0 space-y-3 border-t border-pearl-100">
                                  <p className="text-[11px] text-slateLux-500 pt-2">
                                    Optional shortcuts; blocks on the canvas are the source of truth for layout and copy.
                                  </p>
                                  <input className="w-full border border-pearl-200 rounded-xl2 px-3 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25" placeholder="Template name" value={templateDraft.name} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, name: e.target.value }))} />
                                  <input className="w-full border border-pearl-200 rounded-xl2 px-3 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25" placeholder="Category" value={templateDraft.category} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, category: e.target.value }))} />
                                  <input className="w-full border border-pearl-200 rounded-xl2 px-3 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25" placeholder="Subject / heading" value={templateDraft.subject} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, subject: e.target.value }))} />
                                  <input className="w-full border border-pearl-200 rounded-xl2 px-3 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25" placeholder="Image URL" value={templateDraft.heroImageUrl} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, heroImageUrl: e.target.value }))} />
                                  <textarea className="w-full min-h-[120px] border border-pearl-200 rounded-xl2 px-3 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25" placeholder="Body content" value={templateDraft.body} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, body: e.target.value }))} />
                                  <div className="grid grid-cols-2 gap-2">
                                    <input className="w-full border border-pearl-200 rounded-xl2 px-3 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25" placeholder="Button text" value={templateDraft.ctaText} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, ctaText: e.target.value }))} />
                                    <input className="w-full border border-pearl-200 rounded-xl2 px-3 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25" placeholder="Button URL" value={templateDraft.ctaUrl} onChange={(e) => setTemplateDraft((prev) => ({ ...prev, ctaUrl: e.target.value }))} />
                                  </div>
                                  <div className="pt-1 flex gap-2">
                                    <button className="flex-1 rounded-xl2 border border-pearl-200 px-3 py-2 text-sm hover:bg-pearl-50 transition" onClick={() => setTemplateEditorOpen(false)}>
                                      Exit Editor
                                    </button>
                                    <button className="flex-1 rounded-xl2 bg-accent-600 text-white px-3 py-2 text-sm hover:bg-accent-700 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/35" onClick={saveTemplateDraft}>
                                      Save
                                    </button>
                                  </div>
                                </div>
                              </details>
                            </div>
                          ) : (
                            <div className="space-y-3 text-sm">
                              <div className="font-semibold">Visibility</div>
                              <label className="flex items-center gap-2"><input type="radio" name="template-vis" defaultChecked /> All devices</label>
                              <label className="flex items-center gap-2"><input type="radio" name="template-vis" /> Desktop only</label>
                              <label className="flex items-center gap-2"><input type="radio" name="template-vis" /> Mobile only</label>
                              <div className="mt-3 p-3 rounded-xl2 border border-pearl-200 bg-pearl-50 text-xs text-slateLux-500">
                                Pro conditional send rules can be configured per audience segment in a later pass.
                              </div>
                            </div>
                          )}
                        </aside>
                      </div>
                    </div>
                    {showAssetsManager ? (
                      <div className="fixed inset-0 z-40 bg-black/30 flex items-center justify-center p-6">
                        <div className="w-[min(1050px,96vw)] h-[min(760px,92vh)] bg-white rounded-xl3 border border-pearl-200 shadow-lux flex flex-col overflow-hidden">
                          <div className="px-5 py-4 border-b border-pearl-200 flex items-center gap-3">
                            <div className="text-lg font-semibold">Assets Manager</div>
                            <input
                              className="ml-3 flex-1 border border-pearl-200 rounded-xl2 px-3 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                              placeholder="Search assets..."
                              value={assetsManagerSearch}
                              onChange={(e) => setAssetsManagerSearch(e.target.value)}
                            />
                            <select
                              className="border border-pearl-200 rounded-xl2 px-2 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                              value={assetsManagerTypeFilter}
                              onChange={(e) => setAssetsManagerTypeFilter(e.target.value)}
                            >
                              <option value="all">All</option>
                              <option value="image">Image</option>
                              <option value="audio">Audio</option>
                              <option value="video">Video</option>
                            </select>
                            <label className="px-3 py-2 rounded-xl2 border border-pearl-200 text-sm hover:bg-pearl-50 transition cursor-pointer">
                              Upload assets
                              <input
                                type="file"
                                multiple
                                accept="image/*,audio/*,video/*"
                                className="sr-only"
                                onChange={(e) => addMediaFilesToLibrary(e.target.files, { shared: false })}
                              />
                            </label>
                            {isSuperAdmin ? (
                              <label className="px-3 py-2 rounded-xl2 border border-accent-600 text-accent-600 text-sm hover:bg-accent-600/10 transition cursor-pointer">
                                Publish shared
                                <input
                                  type="file"
                                  multiple
                                  accept="image/*,audio/*,video/*"
                                  className="sr-only"
                                  onChange={(e) => addMediaFilesToLibrary(e.target.files, { shared: true })}
                                />
                              </label>
                            ) : null}
                            <button className="px-3 py-2 rounded-xl2 border border-pearl-200 text-sm hover:bg-pearl-50 transition" onClick={() => setShowAssetsManager(false)}>
                              Close
                            </button>
                          </div>
                          <div className="px-5 py-3 border-b border-pearl-200 flex items-center gap-4 text-xs text-slateLux-500">
                            <span>{filteredManagerAssets.length} assets</span>
                            <span>{availableMediaAssets.filter((item) => item.shared).length} shared by admin</span>
                            <span>{availableMediaAssets.filter((item) => !item.shared).length} personal</span>
                          </div>
                          <div className="p-5 overflow-y-auto grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                            {filteredManagerAssets.map((asset) => (
                              <div key={asset.id} className="border border-pearl-200 rounded-xl3 bg-pearl-50 overflow-hidden">
                                <div className="h-28 border-b border-pearl-200 bg-pearl-100 flex items-center justify-center">
                                  {asset.type === "image" ? <img src={asset.url} alt={asset.name} className="w-full h-full object-cover" /> : null}
                                  {asset.type === "audio" ? <audio src={asset.url} controls className="w-[90%]" /> : null}
                                  {asset.type === "video" ? <video src={asset.url} controls className="w-full h-full object-cover" /> : null}
                                </div>
                                <div className="p-2.5">
                                  <div className="text-xs font-semibold truncate">{asset.name}</div>
                                  <div className="text-[11px] text-slateLux-500 mt-1 flex items-center gap-1">
                                    <span className="uppercase">{asset.type}</span>
                                    <span>•</span>
                                    <span>{formatAssetTtl(asset.expiresAt)}</span>
                                    {asset.shared ? <span className="ml-auto px-1.5 py-0.5 rounded bg-accent-600/10 text-accent-600">Shared</span> : null}
                                  </div>
                                  <div className="mt-2 flex items-center gap-2">
                                    <button className="flex-1 text-[11px] border border-pearl-200 rounded px-2 py-1 hover:bg-white transition" onClick={() => applyMediaAssetToSelectedBlock(asset)}>
                                      Use
                                    </button>
                                    <button
                                      className="text-[11px] text-red-600"
                                      onClick={() => removeMediaAsset(asset.id, Boolean(asset.shared))}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {filteredManagerAssets.length === 0 ? <div className="text-sm text-slateLux-500">No assets match your filter.</div> : null}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
                        <div className="min-w-0">
                          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slateLux-500">Templates</p>
                          <h2 className="mt-1 text-[24px] sm:text-[28px] leading-[1.12] font-semibold tracking-[-0.03em] text-slateLux-900 text-balance">
                            <span className="block">Create a</span>
                            <span className="block text-slateLux-800">design</span>
                          </h2>
                        </div>
                        <input
                          className="w-full sm:mt-0 sm:max-w-md shrink-0 border border-pearl-200 rounded-xl px-3.5 py-2.5 text-[13px] text-slateLux-700 placeholder:text-slateLux-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30"
                          placeholder="What would you like to create?"
                          value={templateSearch}
                          onChange={(e) => setTemplateSearch(e.target.value)}
                        />
                      </div>

                      <div className="mt-8">
                        <div className="text-[20px] leading-none font-semibold tracking-tight text-slateLux-900 mb-3">Emails</div>
                        <button className="w-[200px] text-left border border-pearl-200 rounded-2xl bg-pearl-50 p-3 hover:bg-white transition" onClick={() => openTemplateEditor(null)}>
                          <div className="h-[108px] rounded-xl bg-pearl-100 border border-pearl-200 relative overflow-hidden">
                            <div className="absolute inset-3 rounded-lg border border-pearl-200 bg-pearl-200/70" />
                          </div>
                          <div className="text-[14px] leading-none font-medium mt-3 text-slateLux-900">Create Email Template</div>
                        </button>
                      </div>

                      <div className="mt-10">
                        <div className="flex items-center">
                          <div className="text-[24px] leading-none font-semibold tracking-[-0.01em] text-slateLux-900">
                            {templateCategoryFilter === "Emails" ? "Email templates" : `${templateCategoryFilter} templates`}
                          </div>
                          <button className="ml-auto text-[16px] text-slate-600 hover:underline">See all</button>
                        </div>
                        {filteredTemplates.length === 0 ? (
                          <div className="text-sm text-slateLux-500 border border-pearl-200 rounded-lg p-4 bg-pearl-50 mt-3">No templates match your search.</div>
                        ) : (
                          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
                            {filteredTemplates.map((template) => (
                              <div key={template.id} className="border border-pearl-200 rounded-2xl overflow-hidden bg-pearl-50">
                                <button className="w-full text-left hover:bg-white/40 transition" onClick={() => applyTemplateToCompose(template)}>
                                  <div className="h-[134px] bg-pearl-200 border-b border-pearl-200 px-4 py-3">
                                    <div className="h-full w-full rounded-lg border border-pearl-200 bg-pearl-50 relative overflow-hidden">
                                      <div className="absolute left-3 right-4 top-3 h-2 rounded bg-pearl-200" />
                                      <div className="absolute left-3 right-8 top-8 h-2 rounded bg-pearl-200" />
                                      <div className="absolute left-3 right-5 top-[43px] h-2 rounded bg-pearl-200" />
                                    </div>
                                  </div>
                                  <div className="p-4 flex flex-col">
                                    <div className="h-[24px] text-[16px] leading-tight font-semibold text-slateLux-900 truncate flex items-center gap-2">
                                      <span className="truncate">{template.name}</span>
                                      {template.isPredefined ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-accent-600">Predefined</span> : null}
                                    </div>
                                    <div className="h-[20px] text-[12px] text-slateLux-500 mt-1.5">{template.category}</div>
                                    <div className="mt-2 min-h-[54px] text-[13px] text-slateLux-700 leading-[1.35] line-clamp-2">{template.subject.replace(/^You are invited:\s*/i, "")}</div>
                                  </div>
                                </button>
                                <div className="px-4 pb-4">
                                  <button className="w-full border border-pearl-200 rounded-lg py-1.5 text-xs hover:bg-white transition" onClick={() => openTemplateEditor(template)}>
                                    Edit Template
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </section>
              </div>
            </div>
          ) : activePage === "platform" ? (
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-pearl-200 bg-white">
              <div className="shrink-0 border-b border-pearl-200 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-[20px] font-semibold leading-none">Platform admin</h3>
                    <p className="mt-2 max-w-[70ch] text-sm text-slate-500">
                      Limits, mail-domain policy, OAuth/billing <strong>presence</strong> (not secret values), and domain lists.
                      Change secrets in <span className="font-mono text-xs">.env</span> on the server, then{" "}
                      <span className="font-mono text-xs">php artisan config:cache</span>.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="h-9 shrink-0 rounded-[2px] border border-[#edebe9] bg-white px-3 text-xs font-semibold text-[#323130] shadow-sm transition hover:bg-[#edebe9]"
                    onClick={() => runAction("platformAdminRefresh", loadPlatformAdmin)}
                  >
                    Refresh
                  </button>
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto space-y-6 p-4">
                {platformAdminLoading ? <div className="text-sm text-slate-500">Loading platform data…</div> : null}
                {platformMigrationWarning ? (
                  <div
                    className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950"
                    role="status"
                  >
                    <strong className="font-semibold">Database update needed.</strong> {platformMigrationWarning}
                  </div>
                ) : null}

                {platformSettings ? (
                  <div className="rounded-xl border border-pearl-200 bg-pearl-50/60 p-4">
                    <h4 className="text-sm font-semibold text-slate-800">Limits &amp; storage defaults</h4>
                    <p className="mt-1 text-xs text-slate-500">Persisted in app_settings (global defaults).</p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <label className="block text-xs font-medium text-slate-600">
                        Max mail accounts / user
                        <input
                          type="number"
                          min={1}
                          className="mt-1 w-full rounded border border-pearl-200 bg-white px-2 py-1.5 text-sm"
                          value={String(platformSettings.maxEmailAccountsPerUser ?? "")}
                          onChange={(e) =>
                            setPlatformSettings((prev) =>
                              prev ? { ...prev, maxEmailAccountsPerUser: e.target.value } : prev
                            )
                          }
                        />
                      </label>
                      <label className="block text-xs font-medium text-slate-600">
                        Max social accounts / platform
                        <input
                          type="number"
                          min={1}
                          className="mt-1 w-full rounded border border-pearl-200 bg-white px-2 py-1.5 text-sm"
                          value={String(platformSettings.maxSocialAccountsPerPlatform ?? "")}
                          onChange={(e) =>
                            setPlatformSettings((prev) =>
                              prev ? { ...prev, maxSocialAccountsPerPlatform: e.target.value } : prev
                            )
                          }
                        />
                      </label>
                      <label className="block text-xs font-medium text-slate-600">
                        Default storage (GB)
                        <input
                          type="number"
                          min={1}
                          className="mt-1 w-full rounded border border-pearl-200 bg-white px-2 py-1.5 text-sm"
                          value={String(platformSettings.defaultStorageLimitGb ?? "")}
                          onChange={(e) =>
                            setPlatformSettings((prev) =>
                              prev ? { ...prev, defaultStorageLimitGb: e.target.value } : prev
                            )
                          }
                        />
                      </label>
                      <label className="block text-xs font-medium text-slate-600">
                        Max storage cap (GB)
                        <input
                          type="number"
                          min={1}
                          className="mt-1 w-full rounded border border-pearl-200 bg-white px-2 py-1.5 text-sm"
                          value={String(platformSettings.maxStorageLimitGb ?? "")}
                          onChange={(e) =>
                            setPlatformSettings((prev) =>
                              prev ? { ...prev, maxStorageLimitGb: e.target.value } : prev
                            )
                          }
                        />
                      </label>
                    </div>
                    <button
                      type="button"
                      className="mt-3 rounded border border-[#0078d4] bg-[#0078d4] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#106ebe]"
                      onClick={() => runAction("platformSaveLimits", savePlatformLimits)}
                    >
                      Save limits
                    </button>
                  </div>
                ) : null}

                {platformSettings ? (
                  <div className="rounded-xl border border-pearl-200 bg-pearl-50/60 p-4">
                    <h4 className="text-sm font-semibold text-slate-800">Mail connection domain policy</h4>
                    <p className="mt-1 text-xs text-slate-500">
                      Effective: <strong className="text-slate-800">{String(platformSettings.emailDomainPolicy || "")}</strong>
                      {" · "}
                      Source:{" "}
                      <strong>{platformSettings.emailDomainPolicySource === "database" ? "database" : ".env / config"}</strong>
                      {platformSettings.emailDomainPolicyEnvFallback ? (
                        <>
                          {" · "}
                          <span className="text-slate-600">
                            .env fallback: <span className="font-mono">{String(platformSettings.emailDomainPolicyEnvFallback)}</span>
                          </span>
                        </>
                      ) : null}
                    </p>
                    <div className="mt-3 flex flex-wrap items-end gap-3">
                      <label className="block text-xs font-medium text-slate-600">
                        Policy (saved to database)
                        <select
                          className="mt-1 block min-w-[12rem] rounded border border-pearl-200 bg-white px-2 py-2 text-sm"
                          value={platformPolicyDraft}
                          onChange={(e) => setPlatformPolicyDraft(e.target.value)}
                        >
                          <option value="allowlist">allowlist — verified domains only</option>
                          <option value="hybrid">hybrid — verified + well-known consumer domains</option>
                          <option value="open">open — any domain except blocked list</option>
                        </select>
                      </label>
                      <button
                        type="button"
                        className="rounded border border-[#0078d4] bg-[#0078d4] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#106ebe]"
                        onClick={() => runAction("platformSaveEmailPolicy", savePlatformEmailPolicy)}
                      >
                        Save policy
                      </button>
                      <button
                        type="button"
                        className="rounded border border-[#edebe9] bg-white px-4 py-2 text-sm font-semibold text-[#323130] shadow-sm transition hover:bg-[#edebe9]"
                        onClick={() => runAction("platformClearEmailPolicy", clearPlatformEmailPolicyOverride)}
                      >
                        Use .env only
                      </button>
                    </div>
                  </div>
                ) : null}

                <div className="rounded-xl border border-pearl-200 bg-white p-4 shadow-sm">
                  <h4 className="text-sm font-semibold text-slate-800">Verify domain before allow / block</h4>
                  <p className="mt-1 text-xs text-slate-500">
                    Checks MX, SPF TXT hint, and public HTTPS with a verified TLS chain (from this server). Signals help
                    you decide — they are not a guarantee. Use <strong>allowlist</strong> for approved orgs and{" "}
                    <strong>blocklist</strong> for rejects. With policy <strong>allowlist</strong>, unknown domains stay
                    blocked until you add them here after review.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <input
                      className="min-w-[14rem] flex-1 rounded border border-pearl-200 px-3 py-2 text-sm"
                      placeholder="acme.com or user@acme.com"
                      value={platformInspectInput}
                      onChange={(e) => setPlatformInspectInput(e.target.value)}
                    />
                    <button
                      type="button"
                      className="rounded border border-[#0078d4] bg-[#0078d4] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#106ebe] disabled:opacity-50"
                      disabled={Boolean(busyAction)}
                      onClick={() => runAction("platformInspectDomain", runPlatformDomainInspect)}
                    >
                      Run checks
                    </button>
                  </div>
                  {platformInspectResult?.ok ? (
                    <div className="mt-4 space-y-3 rounded-lg border border-pearl-100 bg-pearl-50/80 p-3 text-sm">
                      <div className="font-mono text-base font-semibold text-slate-900">{platformInspectResult.domain}</div>
                      {platformInspectResult.recommendation ? (
                        <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-950">
                          <div className="font-semibold capitalize">{String(platformInspectResult.recommendation.summary || "").replace(/_/g, " ")}</div>
                          <div className="mt-1 text-sky-900">{platformInspectResult.recommendation.detail}</div>
                        </div>
                      ) : null}
                      <div className="grid gap-3 sm:grid-cols-3 text-xs">
                        <div>
                          <div className="font-semibold text-slate-700">MX</div>
                          <div className="mt-1 text-slate-600">
                            {platformInspectResult.mx?.lookupOk ? (
                              <>
                                <div>Hint: {String(platformInspectResult.mx?.hostedEmailHint || "—")}</div>
                                <ul className="mt-1 list-inside list-disc font-mono text-[11px]">
                                  {(platformInspectResult.mx?.hosts || []).slice(0, 6).map((h) => (
                                    <li key={h}>{h}</li>
                                  ))}
                                </ul>
                              </>
                            ) : (
                              <span className="text-danger-600">{platformInspectResult.mx?.error || "MX lookup failed"}</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="font-semibold text-slate-700">HTTPS</div>
                          <div className="mt-1 text-slate-600">
                            <div>URL: {String(platformInspectResult.https?.url || "—")}</div>
                            <div>Reachable: {platformInspectResult.https?.reachable ? "yes" : "no"}</div>
                            <div>TLS verified: {platformInspectResult.https?.tlsVerified ? "yes" : "no"}</div>
                            {platformInspectResult.https?.error ? (
                              <div className="mt-1 text-danger-600">{String(platformInspectResult.https.error)}</div>
                            ) : null}
                          </div>
                        </div>
                        <div>
                          <div className="font-semibold text-slate-700">SPF (TXT)</div>
                          <div className="mt-1 break-all font-mono text-[11px] text-slate-600">
                            {platformInspectResult.spf?.record ? String(platformInspectResult.spf.record) : "No SPF TXT found"}
                          </div>
                        </div>
                      </div>
                      {platformInspectResult.note ? <div className="text-xs text-slate-500">{platformInspectResult.note}</div> : null}
                      <div className="flex flex-wrap gap-2 pt-1">
                        <button
                          type="button"
                          className="rounded border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
                          onClick={() => runAction("platformAllowInspect", allowlistInspectedDomain)}
                        >
                          Add to verified (allow)
                        </button>
                        <button
                          type="button"
                          className="rounded border border-red-600 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50"
                          onClick={() => runAction("platformBlockInspect", blockInspectedDomain)}
                        >
                          Add to blocked
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>

                {platformIntegrations ? (
                  <div className="rounded-xl border border-pearl-200 bg-pearl-50/60 p-4">
                    <h4 className="text-sm font-semibold text-slate-800">Integrations &amp; runtime (.env / config)</h4>
                    <p className="mt-1 text-xs text-slate-500">
                      “yes” means a non-empty value is set (IDs/URIs shown; secrets only as configured/not).
                    </p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      {Object.entries(platformIntegrations).map(([group, cfg]) => (
                        <div key={group} className="rounded-lg border border-pearl-200 bg-white p-3 text-xs shadow-sm">
                          <div className="font-semibold capitalize text-slate-800">{group}</div>
                          <dl className="mt-2 space-y-1.5">
                            {Object.entries(cfg || {}).map(([k, v]) => (
                              <div key={k} className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-2">
                                <dt className="shrink-0 text-slate-500">{k}</dt>
                                <dd className="min-w-0 break-all text-right text-slate-800 sm:max-w-[60%]">
                                  {typeof v === "boolean" ? (v ? "yes" : "no") : String(v || "—")}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {platformSettings?.wellKnownEmailDomains?.length ? (
                  <details className="rounded-xl border border-pearl-200 bg-white p-4 text-sm">
                    <summary className="cursor-pointer font-semibold text-slate-800">
                      Well-known consumer domains ({platformSettings.wellKnownEmailDomains.length}) — used in hybrid mode
                    </summary>
                    <p className="mt-2 text-xs text-slate-500">
                      Edit the list in <span className="font-mono">config/netluq.php</span> (requires deploy / config refresh).
                    </p>
                    <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded border border-pearl-100 bg-pearl-50 p-2 text-xs font-mono">
                      {(platformSettings.wellKnownEmailDomains || []).join(", ")}
                    </pre>
                  </details>
                ) : null}

                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="rounded-xl border border-pearl-200 bg-pearl-50/60 p-4">
                    <h4 className="text-sm font-semibold text-slate-800">Verified domains</h4>
                    <p className="mt-1 text-xs text-slate-500">Allowlist / hybrid: users may connect mail @ these domains.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <input
                        className="min-w-[12rem] flex-1 rounded border border-pearl-200 bg-white px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35"
                        placeholder="e.g. company.com"
                        value={platformNewVerified}
                        onChange={(e) => setPlatformNewVerified(e.target.value)}
                      />
                      <button
                        type="button"
                        className="rounded border border-[#0078d4] bg-[#0078d4] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#106ebe]"
                        onClick={() => runAction("platformAddVerified", addPlatformVerifiedDomain)}
                      >
                        Add
                      </button>
                    </div>
                    <div className="mt-4 max-h-[min(320px,40vh)] overflow-y-auto rounded border border-pearl-200 bg-white">
                      {(platformVerifiedDomains || []).length === 0 ? (
                        <div className="p-4 text-sm text-slate-500">No verified domains yet.</div>
                      ) : (
                        (platformVerifiedDomains || []).map((row) => {
                          const domain = typeof row === "string" ? row : String(row?.domain || "");
                          if (!domain) return null;
                          return (
                            <div key={domain} className="flex items-center gap-2 border-b border-pearl-100 px-3 py-2 last:border-b-0">
                              <span className="min-w-0 flex-1 truncate font-mono text-sm">{domain}</span>
                              <button
                                type="button"
                                className="shrink-0 rounded border border-red-200 px-2 py-1 text-xs text-danger-600 transition hover:bg-red-50"
                                onClick={() => runAction(`platformRmVerified-${domain}`, () => removePlatformVerifiedDomain(domain))}
                              >
                                Remove
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                  <div className="rounded-xl border border-pearl-200 bg-pearl-50/60 p-4">
                    <h4 className="text-sm font-semibold text-slate-800">Blocked domains</h4>
                    <p className="mt-1 text-xs text-slate-500">Always rejected when users try to connect a mailbox.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <input
                        className="min-w-[12rem] flex-1 rounded border border-pearl-200 bg-white px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0078d4]/35"
                        placeholder="e.g. disposable.test"
                        value={platformNewBlocked}
                        onChange={(e) => setPlatformNewBlocked(e.target.value)}
                      />
                      <button
                        type="button"
                        className="rounded border border-[#0078d4] bg-[#0078d4] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#106ebe]"
                        onClick={() => runAction("platformAddBlocked", addPlatformBlockedDomain)}
                      >
                        Add
                      </button>
                    </div>
                    <div className="mt-4 max-h-[min(320px,40vh)] overflow-y-auto rounded border border-pearl-200 bg-white">
                      {(platformBlockedDomains || []).length === 0 ? (
                        <div className="p-4 text-sm text-slate-500">No blocked domains.</div>
                      ) : (
                        (platformBlockedDomains || []).map((row) => {
                          const domain = typeof row === "string" ? row : String(row?.domain || "");
                          if (!domain) return null;
                          return (
                            <div key={domain} className="flex items-center gap-2 border-b border-pearl-100 px-3 py-2 last:border-b-0">
                              <span className="min-w-0 flex-1 truncate font-mono text-sm">{domain}</span>
                              <button
                                type="button"
                                className="shrink-0 rounded border border-red-200 px-2 py-1 text-xs text-danger-600 transition hover:bg-red-50"
                                onClick={() => runAction(`platformRmBlocked-${domain}`, () => removePlatformBlockedDomain(domain))}
                              >
                                Remove
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : activePage === "blocked" ? (
            <div className="h-full border border-pearl-200 rounded-xl overflow-hidden bg-white">
              <div className="p-4 border-b border-pearl-200">
                <h3 className="text-[20px] leading-none font-semibold">Manage Blocked Senders</h3>
                <p className="mt-2 text-sm text-slate-500">
                  Block email addresses, handles, or names. Blocked senders are filtered from inbox and social feeds.
                </p>
              </div>
              <div className="p-4 border-b border-pearl-200 bg-pearl-50">
                <div className="grid grid-cols-[1fr_160px_1fr_auto] gap-2 items-center">
                  <input
                    className="border border-pearl-200 rounded px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30"
                    placeholder="Email, handle, or sender name"
                    value={blockedSenderValue}
                    onChange={(e) => setBlockedSenderValue(e.target.value)}
                  />
                  <select
                    className="border border-pearl-200 rounded px-2 py-2 text-sm bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30"
                    value={blockedSenderChannel}
                    onChange={(e) => setBlockedSenderChannel(e.target.value)}
                  >
                    <option value="all">All channels</option>
                    <option value="mail">Mail only</option>
                    <option value="social">Social only</option>
                  </select>
                  <input
                    className="border border-pearl-200 rounded px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30"
                    placeholder="Label (optional)"
                    value={blockedSenderLabel}
                    onChange={(e) => setBlockedSenderLabel(e.target.value)}
                  />
                  <button
                    className="nlq-btn-primary px-4 py-2 rounded text-sm"
                    onClick={() => runAction("addBlockedSender", createBlockedSender)}
                  >
                    Add
                  </button>
                </div>
              </div>
              <div className="max-h-[calc(100vh-290px)] overflow-y-auto">
                {blockedSenders.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">No blocked senders yet.</div>
                ) : (
                  blockedSenders.map((item) => (
                    <div key={item.id} className="px-4 py-3 border-b border-pearl-200 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-800 truncate">{item.value}</div>
                        <div className="text-xs text-slate-500">
                          {item.channel.toUpperCase()} {item.label ? `· ${item.label}` : ""}
                        </div>
                      </div>
                      <button
                        className="text-xs px-2 py-1 rounded border border-red-200 text-danger-600 hover:bg-red-50 transition"
                        onClick={() => runAction(`removeBlocked-${item.id}`, async () => deleteBlockedSender(item.id))}
                      >
                        Unblock
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : activePage === "settings" ? (
            <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden border border-[#edebe9] bg-white">
              <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-12 lg:h-full">
                <section className="flex min-h-0 min-w-0 flex-col overflow-y-auto overflow-x-hidden border-b border-[#edebe9] p-3 sm:p-4 lg:col-span-6 lg:border-b-0 lg:border-r">
                  <div className="mb-4 flex flex-wrap gap-1.5 sm:mb-5 sm:gap-2">
                    {[
                      { id: "messaging", label: "Messaging" },
                      { id: "email", label: "Email" },
                      { id: "calendar", label: "Calendar" },
                      { id: "billing", label: "Billing" }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        className={`border px-3 py-1.5 text-xs font-semibold tracking-normal transition sm:px-3.5 ${
                          settingsCategory === tab.id
                            ? "border-[#0078d4] bg-[#0078d4] text-white"
                            : "border-[#edebe9] bg-[#faf9f8] text-[#323130] hover:border-[#c8c6c4] hover:bg-[#f3f2f1]"
                        } rounded-[2px]`}
                        onClick={() => {
                          setSettingsCategory(tab.id);
                          if (tab.id === "billing") {
                            loadBilling();
                            return;
                          }
                          setSettingsProvider(integrationCatalog[tab.id][0]?.id || "linkedin");
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  {settingsCategory === "billing" ? (
                    <div className="rounded-[2px] border border-[#edebe9] bg-[#faf9f8] p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <div className="text-xs font-semibold uppercase tracking-wide text-[#605e5c]">Plan &amp; usage</div>
                          <div className="mt-1 text-sm font-semibold text-[#201f1e]">Subscription &amp; storage</div>
                          <div className="mt-0.5 text-xs text-[#605e5c]">Upgrade your limits (emails, DMs, storage) per plan.</div>
                        </div>
                        <button
                          className="h-8 shrink-0 rounded-[2px] border border-[#edebe9] bg-white px-3 text-xs font-semibold text-[#323130] shadow-sm transition hover:bg-[#edebe9]"
                          onClick={() => loadBilling()}
                          type="button"
                        >
                          Refresh
                        </button>
                      </div>

                      {billingLoading ? <div className="mt-4 text-sm text-[#605e5c]">Loading billing…</div> : null}
                      {billingError ? <div className="mt-4 text-sm text-danger-600">{billingError}</div> : null}

                      {billingMe ? (
                        <div className="mt-4 grid gap-3">
                          <div className="rounded-[2px] border border-[#edebe9] bg-white p-4 shadow-sm">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <div className="text-[11px] font-medium uppercase tracking-wide text-[#605e5c]">Current plan</div>
                                <div className="mt-0.5 text-xl font-semibold text-[#201f1e]">
                                  {billingMe.plan?.name || billingMe.planKey || "Starter"}
                                </div>
                              </div>
                              <div className="text-right text-xs text-[#605e5c]">
                                {billingMe.subscription?.provider ? `Provider: ${billingMe.subscription.provider}` : "Provider: —"}
                              </div>
                            </div>
                            <div className="mt-4 border-t border-[#edebe9] pt-4">
                              <div className="flex items-center justify-between text-xs font-medium text-[#605e5c]">
                                <span>Storage usage</span>
                                <span className="tabular-nums text-[#323130]">
                                  {(billingMe.usage?.storageGb ?? 0).toFixed?.(2) ? billingMe.usage.storageGb.toFixed(2) : billingMe.usage?.storageGb ?? 0}
                                  GB /
                                  {(billingMe.usage?.storageLimitGb ?? 0).toFixed?.(2)
                                    ? billingMe.usage.storageLimitGb.toFixed(2)
                                    : billingMe.usage?.storageLimitGb ?? 0}
                                  GB
                                </span>
                              </div>
                              <div className="mt-2 h-1.5 overflow-hidden rounded-[1px] bg-[#edebe9]">
                                <div
                                  className="h-full bg-[#0078d4]"
                                  style={{
                                    width: `${Math.min(
                                      100,
                                      Math.round(
                                        ((Number(billingMe.usage?.storageBytes || 0) || 0) /
                                          Math.max(1, Number(billingMe.usage?.storageLimitBytes || 0) || 1)) *
                                          100
                                      )
                                    )}%`
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                      {(integrationCatalog[settingsCategory] || []).map((provider) => (
                        <button
                          key={provider.id}
                          className={`rounded-xl border p-2.5 sm:p-3 text-left shadow-luxSoft transition min-w-0 ${
                            settingsProvider === provider.id
                              ? "bg-pearl-100 border-pearl-200"
                              : "bg-pearl-50 border-pearl-200 hover:bg-white"
                          }`}
                          onClick={() => {
                            setSettingsProvider(provider.id);
                            setError("");
                            if (isSocialProvider(provider.id)) {
                              setSocialConnectForm((prev) => ({ ...prev, platform: provider.id }));
                            }
                          }}
                        >
                          <div className="flex items-start justify-between gap-1">
                            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                              <span className="inline-flex w-6 h-6 sm:w-7 sm:h-7 shrink-0 rounded-md bg-white border border-pearl-200 items-center justify-center text-[10px] font-semibold">
                                <SocialProviderIcon platform={provider.id} />
                              </span>
                              <span className="text-xs sm:text-sm font-semibold truncate">{provider.label}</span>
                            </div>
                            <span className="text-[10px] sm:text-xs text-slate-500 shrink-0">▾</span>
                          </div>
                          <div className="mt-2 sm:mt-3 text-[10px] sm:text-xs text-slate-700 tabular-nums">
                            {provider.featuresCount} Features
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </section>

                <section
                  className={`min-h-0 min-w-0 overflow-y-auto overflow-x-hidden p-3 sm:p-4 lg:col-span-6 ${
                    settingsCategory === "billing" ? "bg-[#faf9f8] lg:border-l lg:border-[#edebe9]" : ""
                  }`}
                >
                  {settingsCategory === "billing" ? (
                    <div>
                      <div className="mb-4 flex flex-col gap-1 border-b border-[#edebe9] pb-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-[#605e5c]">Upgrade</div>
                          <h3 className="text-lg font-semibold leading-tight text-[#201f1e] sm:text-xl">Choose a plan</h3>
                        </div>
                        <div className="text-xs text-[#605e5c]">Select a tier and payment provider.</div>
                      </div>

                      <div className="grid grid-cols-1 gap-3">
                        {billingPlans.length === 0 && !billingLoading ? (
                          <div className="text-sm text-[#605e5c]">No plans available yet.</div>
                        ) : null}
                        {billingPlans.map((plan) => {
                          const key = String(plan.key || "");
                          const isCurrent = String(billingMe?.planKey || "") === key;
                          const busyStripe = billingCheckoutBusy === `stripe:${key}`;
                          const busyPaystack = billingCheckoutBusy === `paystack:${key}`;
                          const busyFincra = billingCheckoutBusy === `fincra:${key}`;
                          const busyOpay = billingCheckoutBusy === `opay:${key}`;
                          const busyAny = !!billingCheckoutBusy;
                          return (
                            <div
                              key={key}
                              className={`rounded-[2px] border bg-white p-4 shadow-sm ${
                                isCurrent ? "border-[#0078d4] ring-1 ring-[#0078d4]/20" : "border-[#edebe9]"
                              }`}
                            >
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="text-lg font-semibold text-[#201f1e]">{plan.name || key}</div>
                                  <div className="mt-1 text-xs leading-relaxed text-[#605e5c]">
                                    {plan.maxEmailAccounts ?? "—"} emails · {plan.maxSocialAccountsPerPlatform ?? "—"} socials/platform ·{" "}
                                    {plan.storageLimitGb ?? "—"} GB storage
                                  </div>
                                </div>
                                {isCurrent ? (
                                  <span className="inline-flex shrink-0 items-center rounded-[2px] border border-[#0078d4]/40 bg-[#deecf9] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#0078d4]">
                                    Current
                                  </span>
                                ) : null}
                              </div>

                              <div className="mt-4 grid grid-cols-2 gap-2">
                                <button
                                  type="button"
                                  className="h-9 rounded-[2px] bg-[#0078d4] px-2 text-center text-[11px] font-semibold text-white transition hover:bg-[#106ebe] disabled:opacity-50"
                                  disabled={busyStripe || busyAny}
                                  onClick={() => startCheckout("stripe", key)}
                                >
                                  {busyStripe ? "Stripe…" : "Pay with Stripe"}
                                </button>
                                <button
                                  type="button"
                                  className="h-9 rounded-[2px] border border-[#edebe9] bg-white px-2 text-center text-[11px] font-semibold text-[#323130] transition hover:bg-[#f3f2f1] disabled:opacity-50"
                                  disabled={busyPaystack || busyAny}
                                  onClick={() => startCheckout("paystack", key)}
                                >
                                  {busyPaystack ? "Paystack…" : "Pay with Paystack"}
                                </button>
                                <button
                                  type="button"
                                  className="h-9 rounded-[2px] border border-[#edebe9] bg-white px-2 text-center text-[11px] font-semibold text-[#323130] transition hover:bg-[#f3f2f1] disabled:opacity-50"
                                  disabled={busyFincra || busyAny}
                                  onClick={() => startCheckout("fincra", key)}
                                >
                                  {busyFincra ? "Fincra…" : "Pay with Fincra"}
                                </button>
                                <button
                                  type="button"
                                  className="h-9 rounded-[2px] border border-[#edebe9] bg-white px-2 text-center text-[11px] font-semibold text-[#323130] transition hover:bg-[#f3f2f1] disabled:opacity-50"
                                  disabled={busyOpay || busyAny}
                                  onClick={() => startCheckout("opay", key)}
                                >
                                  {busyOpay ? "OPay…" : "Pay with OPay"}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4 min-w-0">
                    <span className="inline-flex w-8 h-8 shrink-0 rounded-md bg-white border border-pearl-200 items-center justify-center">
                      <SocialProviderIcon platform={settingsProvider} className="w-5 h-5" />
                    </span>
                    <h3 className="text-lg sm:text-[22px] leading-tight sm:leading-none font-semibold tracking-tight break-words min-w-0">
                      {integrationFeatureMap[settingsProvider]?.title || `${socialPlatformLabel(settingsProvider)} Features`}
                    </h3>
                  </div>
                  )}
                  {isSocialProvider(settingsProvider) ? (
                    <div className="border border-pearl-200 rounded-xl3 p-3 mb-3 bg-pearl-50 space-y-2">
                      <div className="text-xs font-semibold text-slateLux-700">
                        Connection Status: {selectedSocialConnection ? `Connected as @${selectedSocialConnection.handle}` : "Not Connected"}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input
                          className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                          placeholder="Handle (required)"
                          value={socialConnectForm.handle}
                          onChange={(e) =>
                            setSocialConnectForm((prev) => ({
                              ...prev,
                              platform: settingsProvider,
                              handle: e.target.value
                            }))
                          }
                        />
                        <input
                          className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                          placeholder="Display name"
                          value={socialConnectForm.displayName}
                          onChange={(e) =>
                            setSocialConnectForm((prev) => ({
                              ...prev,
                              platform: settingsProvider,
                              displayName: e.target.value
                            }))
                          }
                        />
                      </div>
                      <input
                        className="w-full border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                        placeholder="Access token (optional)"
                        value={socialConnectForm.accessToken}
                        onChange={(e) =>
                          setSocialConnectForm((prev) => ({
                            ...prev,
                            platform: settingsProvider,
                            accessToken: e.target.value
                          }))
                        }
                      />
                      {oauthSupportedSocialProviders.includes(settingsProvider) ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input
                            className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                            placeholder="OAuth code"
                            value={socialOauth.code}
                            onChange={(e) => setSocialOauth((prev) => ({ ...prev, code: e.target.value }))}
                          />
                          <input
                            className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                            placeholder="OAuth state"
                            value={socialOauth.state}
                            onChange={(e) => setSocialOauth((prev) => ({ ...prev, state: e.target.value }))}
                          />
                        </div>
                      ) : null}
                      <div className="flex flex-wrap gap-2">
                        <button
                          className="text-xs border border-pearl-200 rounded px-2 py-1 bg-white hover:bg-pearl-50 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                          onClick={() =>
                            runAction("connectSocial", async () => {
                              setSocialConnectForm((prev) => ({ ...prev, platform: settingsProvider }));
                              await connectSocialAccount();
                            })
                          }
                        >
                          Connect {socialPlatformLabel(settingsProvider)}
                        </button>
                        <button
                          className="text-xs border border-pearl-200 rounded px-2 py-1 bg-white hover:bg-pearl-50 transition disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                          disabled={!selectedSocialConnection || !syncSupportedSocialProviders.includes(settingsProvider)}
                          onClick={() =>
                            runAction("syncSocial", async () => {
                              if (!selectedSocialConnection) return;
                              setActiveSocialAccountId(selectedSocialConnection.id);
                              await syncSocialMessages();
                            })
                          }
                        >
                          Sync Messages
                        </button>
                        {oauthSupportedSocialProviders.includes(settingsProvider) ? (
                          <>
                            <button
                              className="text-xs border border-pearl-200 rounded px-2 py-1 bg-white hover:bg-pearl-50 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                              onClick={() =>
                                runAction("startOauth", async () => {
                                  setSocialOauth((prev) => ({ ...prev, platform: settingsProvider }));
                                  await startSocialOauth();
                                })
                              }
                            >
                              Start OAuth
                            </button>
                            <button
                              className="text-xs border border-pearl-200 rounded px-2 py-1 bg-white hover:bg-pearl-50 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                              onClick={() =>
                                runAction("completeOauth", async () => {
                                  setSocialOauth((prev) => ({ ...prev, platform: settingsProvider }));
                                  await completeSocialOauth();
                                })
                              }
                            >
                              Complete OAuth
                            </button>
                          </>
                        ) : (
                          <span className="text-[11px] text-slateLux-500">OAuth is not available for this provider in the current backend.</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="border border-pearl-200 rounded-xl3 p-3 mb-3 bg-pearl-50 space-y-2">
                      <div className="text-xs font-semibold text-slateLux-700">
                        Connection Status: {emailConnectionConnected ? `${selectedEmailConnections.length} Account(s) Connected` : "Not Connected"}
                      </div>
                      {accounts.some((a) => a.capabilities && a.capabilities.phpImapExtension === false) ? (
                        <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 leading-snug">
                          Inbox sync cannot run on this server until the PHP <strong>imap</strong> extension is enabled (cPanel: Select PHP Version → Extensions, or EasyApache). Sending still uses SMTP if credentials are present.
                        </div>
                      ) : null}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input
                          className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                          placeholder="Email address"
                          value={connectForm.emailAddress}
                          onChange={(e) => setConnectForm((prev) => ({ ...prev, emailAddress: e.target.value }))}
                        />
                        <input
                          className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                          placeholder="Password / App password"
                          type="password"
                          value={connectForm.password}
                          onChange={(e) => setConnectForm((prev) => ({ ...prev, password: e.target.value }))}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <select
                          className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                          value={connectForm.authType}
                          onChange={(e) => setConnectForm((prev) => ({ ...prev, authType: e.target.value }))}
                        >
                          <option value="app_password">App password</option>
                          <option value="password">Password</option>
                        </select>
                        {emailOauthSupportedProviders.includes(normalizedSettingsEmailProvider) ? (
                          <input
                            className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                            placeholder="Email hint (optional)"
                            value={emailOauth.emailAddressHint}
                            onChange={(e) => setEmailOauth((prev) => ({ ...prev, emailAddressHint: e.target.value }))}
                          />
                        ) : (
                          <button
                            className="text-[11px] text-slateLux-700 px-2 py-1.5 border border-pearl-200 rounded bg-white hover:bg-pearl-50 transition text-left"
                            onClick={() => setConnectForm((prev) => ({ ...prev, manualMode: !prev.manualMode }))}
                            title="Toggle full IMAP/SMTP fields"
                          >
                            Manual Credentials Mode: {connectForm.manualMode ? "ON" : "OFF"}
                          </button>
                        )}
                      </div>
                      {!emailOauthSupportedProviders.includes(normalizedSettingsEmailProvider) && connectForm.manualMode ? (
                        <div className="mt-2 border border-pearl-200 rounded-xl3 p-3 bg-white space-y-3">
                          <div className="text-xs font-semibold text-slateLux-700">IMAP (incoming)</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input
                              className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                              placeholder="IMAP host (e.g. imap.gmail.com)"
                              value={connectForm.imap.host}
                              onChange={(e) =>
                                setConnectForm((prev) => ({ ...prev, imap: { ...prev.imap, host: e.target.value } }))
                              }
                            />
                            <input
                              className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                              placeholder="IMAP port (e.g. 993)"
                              inputMode="numeric"
                              value={String(connectForm.imap.port ?? 993)}
                              onChange={(e) =>
                                setConnectForm((prev) => ({
                                  ...prev,
                                  imap: { ...prev.imap, port: Number(e.target.value || 993) }
                                }))
                              }
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input
                              className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                              placeholder="IMAP username (optional)"
                              value={connectForm.imap.username}
                              onChange={(e) =>
                                setConnectForm((prev) => ({ ...prev, imap: { ...prev.imap, username: e.target.value } }))
                              }
                            />
                            <input
                              className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                              placeholder="IMAP password (optional; defaults to above)"
                              type="password"
                              value={connectForm.imap.password}
                              onChange={(e) =>
                                setConnectForm((prev) => ({ ...prev, imap: { ...prev.imap, password: e.target.value } }))
                              }
                            />
                          </div>
                          <label className="flex items-center gap-2 text-[11px] text-slateLux-600">
                            <input
                              type="checkbox"
                              checked={Boolean(connectForm.imap.secure)}
                              onChange={(e) =>
                                setConnectForm((prev) => ({ ...prev, imap: { ...prev.imap, secure: e.target.checked } }))
                              }
                            />
                            Use TLS/SSL
                          </label>

                          <div className="text-xs font-semibold text-slateLux-700 pt-2">SMTP (outgoing)</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input
                              className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                              placeholder="SMTP host (e.g. smtp.gmail.com)"
                              value={connectForm.smtp.host}
                              onChange={(e) =>
                                setConnectForm((prev) => ({ ...prev, smtp: { ...prev.smtp, host: e.target.value } }))
                              }
                            />
                            <input
                              className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                              placeholder="SMTP port (e.g. 587 / 465)"
                              inputMode="numeric"
                              value={String(connectForm.smtp.port ?? 587)}
                              onChange={(e) =>
                                setConnectForm((prev) => ({
                                  ...prev,
                                  smtp: { ...prev.smtp, port: Number(e.target.value || 587) }
                                }))
                              }
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input
                              className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                              placeholder="SMTP username (optional)"
                              value={connectForm.smtp.username}
                              onChange={(e) =>
                                setConnectForm((prev) => ({ ...prev, smtp: { ...prev.smtp, username: e.target.value } }))
                              }
                            />
                            <input
                              className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                              placeholder="SMTP password (optional; defaults to above)"
                              type="password"
                              value={connectForm.smtp.password}
                              onChange={(e) =>
                                setConnectForm((prev) => ({ ...prev, smtp: { ...prev.smtp, password: e.target.value } }))
                              }
                            />
                          </div>
                          <label className="flex items-center gap-2 text-[11px] text-slateLux-600">
                            <input
                              type="checkbox"
                              checked={Boolean(connectForm.smtp.secure)}
                              onChange={(e) =>
                                setConnectForm((prev) => ({ ...prev, smtp: { ...prev.smtp, secure: e.target.checked } }))
                              }
                            />
                            Use TLS/SSL
                          </label>
                        </div>
                      ) : null}
                      {emailOauthSupportedProviders.includes(normalizedSettingsEmailProvider) ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input
                            className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                            placeholder="OAuth code"
                            value={emailOauth.code}
                            onChange={(e) => setEmailOauth((prev) => ({ ...prev, code: e.target.value }))}
                          />
                          <input
                            className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                            placeholder="OAuth state"
                            value={emailOauth.state}
                            onChange={(e) => setEmailOauth((prev) => ({ ...prev, state: e.target.value }))}
                          />
                        </div>
                      ) : null}
                      <div className="flex flex-wrap gap-2">
                        <button
                          className="text-xs border border-pearl-200 rounded px-2 py-1 bg-white hover:bg-pearl-50 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                          onClick={() =>
                            runAction("connectEmail", async () => {
                              if (emailOauthSupportedProviders.includes(normalizedSettingsEmailProvider)) {
                                setEmailOauth((prev) => ({ ...prev, provider: normalizedSettingsEmailProvider }));
                                if (emailOauth.code && emailOauth.state) {
                                  await completeEmailOauth();
                                } else {
                                  await startEmailOauth();
                                }
                                return;
                              }
                              await quickConnectAccount();
                            })
                          }
                        >
                          Connect Account
                        </button>
                        {emailOauthSupportedProviders.includes(normalizedSettingsEmailProvider) ? (
                          <>
                            <button
                              className="text-xs border border-pearl-200 rounded px-2 py-1 bg-white hover:bg-pearl-50 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                              onClick={() =>
                                runAction("startEmailOauth", async () => {
                                  setEmailOauth((prev) => ({ ...prev, provider: normalizedSettingsEmailProvider }));
                                  await startEmailOauth();
                                })
                              }
                            >
                              Start OAuth
                            </button>
                            <button
                              className="text-xs border border-pearl-200 rounded px-2 py-1 bg-white hover:bg-pearl-50 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                              onClick={() =>
                                runAction("completeEmailOauth", async () => {
                                  setEmailOauth((prev) => ({ ...prev, provider: normalizedSettingsEmailProvider }));
                                  await completeEmailOauth();
                                })
                              }
                            >
                              Complete OAuth
                            </button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 border border-pearl-200 rounded-xl3 p-3 bg-white shadow-luxSoft">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-slateLux-700">Pulse (SPF / DMARC / BIMI)</div>
                        <div className="text-[11px] text-slateLux-500 mt-0.5">Check a sender domain’s authentication posture.</div>
                      </div>
                      <button
                        className="text-xs px-3 py-1.5 rounded-full border border-pearl-200 bg-white hover:bg-pearl-50 transition font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25 shrink-0 self-start sm:self-auto"
                        disabled={securityPulseLoading}
                        onClick={() =>
                          runAction("pulse", async () => {
                            const d = String(securityPulseDomain || "").trim();
                            if (!d) return;
                            setSecurityPulseLoading(true);
                            try {
                              const res = await api.get(`/api/security/domain-health?domain=${encodeURIComponent(d)}`);
                              setSecurityPulseResult(res.data || null);
                            } finally {
                              setSecurityPulseLoading(false);
                            }
                          })
                        }
                      >
                        {securityPulseLoading ? "Checking…" : "Check"}
                      </button>
                    </div>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_100px] gap-2">
                      <input
                        className="border border-pearl-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25 min-w-0"
                        placeholder="example.com"
                        value={securityPulseDomain}
                        onChange={(e) => setSecurityPulseDomain(e.target.value)}
                      />
                      <button
                        className="text-xs rounded px-2 py-1.5 bg-ink-900 text-white font-semibold hover:bg-ink-800 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                        onClick={() => {
                          const d = String(selectedMessage?.from_email || "").split("@")[1] || "";
                          if (d) setSecurityPulseDomain(d);
                        }}
                      >
                        Use sender
                      </button>
                    </div>
                    {securityPulseResult ? (
                      <div className="mt-3 text-[12px] text-slateLux-700 space-y-1">
                        <div>
                          <span className="font-semibold">SPF:</span> {securityPulseResult.spf?.present ? "present" : "missing"}
                        </div>
                        <div>
                          <span className="font-semibold">DMARC:</span>{" "}
                          {securityPulseResult.dmarc?.present ? `${securityPulseResult.dmarc.policy || "present"}` : "missing"}
                        </div>
                        <div>
                          <span className="font-semibold">BIMI:</span> {securityPulseResult.bimi?.present ? "present" : "missing"}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          Verdict:{" "}
                          <span className={`font-semibold ${securityPulseResult.strong ? "text-emerald-600" : "text-amber-600"}`}>
                            {securityPulseResult.strong ? "strong" : "weak/unknown"}
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="border border-pearl-200 rounded-xl3 overflow-hidden shadow-luxSoft">
                    {(integrationFeatureMap[settingsProvider]?.sections || [
                      { title: "Actions", items: ["Connect Account", "Sync Account Data", "Send Test Message"] }
                    ]).map((section) => (
                      <div key={section.title} className="border-b last:border-b-0">
                        <div className="bg-pearl-100 px-3 py-2 text-sm font-semibold text-slateLux-900">{section.title}</div>
                        {section.items.map((item) => (
                          <button
                            key={item}
                            className="w-full px-3 py-2.5 text-left text-sm border-t border-pearl-200 first:border-t-0 hover:bg-pearl-50 transition flex items-center justify-between"
                            onClick={() => runAction(`settings-${item}`, async () => executeSettingsAction(item))}
                          >
                            <span>{item}</span>
                            <span>›</span>
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          ) : activePage === "social" ? (
            <div className="h-full border border-pearl-200 rounded-xl3 overflow-hidden bg-white flex flex-col min-h-0 min-w-0">
              <div className="grid grid-cols-1 lg:grid-cols-12 lg:h-full min-h-0 flex-1 overflow-hidden">
                <section className="lg:col-span-6 border-b lg:border-b-0 lg:border-r border-pearl-200 min-h-0 min-w-0 overflow-hidden flex flex-col">
                  <div className="flex border-b border-pearl-200">
                    {[
                      { id: "contacts", label: "CONTACTS", enabled: true },
                      { id: "opportunities", label: "OPPORTUNITIES", enabled: true },
                      { id: "deals", label: "DEALS", enabled: true }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        className={`flex-1 py-3 text-[11px] font-semibold tracking-[0.08em] relative ${
                          socialViewTab === tab.id && tab.enabled
                            ? "bg-accent-600 text-white"
                            : "bg-white text-slateLux-500"
                        }`}
                        onClick={() => {
                          setSocialViewTab(tab.id);
                        }}
                        title={tab.label}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-12 px-3 py-2 border-b border-pearl-200 text-[11px] font-semibold text-slateLux-500 tracking-wide bg-pearl-50">
                    <div className="col-span-5">Contacts</div>
                    <div className="col-span-3">Company</div>
                    <div className="col-span-2">From</div>
                    <div className="col-span-2">Status</div>
                  </div>
                  <div className="max-h-[calc(100vh-220px)] overflow-y-auto bg-pearl-50 p-2">
                    {socialContacts.map((contact, index) => (
                      <button
                        key={contact.id}
                        className={`w-full grid grid-cols-12 text-left px-3 py-2 rounded-xl2 border mb-2 last:mb-0 transition ${
                          (activeSocialContact?.id || socialContacts[0]?.id) === contact.id
                            ? "bg-accent-600/10 border-accent-600/25 shadow-luxSoft"
                            : "bg-white border-pearl-200 hover:bg-pearl-50"
                        }`}
                        onClick={() => {
                          setSelectedSocialContactId(contact.id);
                          setSocialReply((prev) => ({
                            ...prev,
                            socialMessageId: contact.messages[0]?.id || ""
                          }));
                        }}
                      >
                        <div className="col-span-5 flex items-start gap-2">
                          <div className="mt-1 w-2 h-2 rounded-full bg-accent-600 shrink-0" />
                          <div className="min-w-0">
                            <div className="font-semibold text-sm truncate">{contact.name}</div>
                            <div className="text-xs text-slateLux-500 truncate">{contact.latestText}</div>
                          </div>
                        </div>
                        <div className="col-span-3 text-sm text-slateLux-700 truncate pr-2">{contact.company}</div>
                        <div className="col-span-2 text-xs flex items-center">
                          <span className="inline-flex w-6 h-6 rounded-md bg-white border border-pearl-200 items-center justify-center">
                            <SocialProviderIcon platform={contact.platform} />
                          </span>
                        </div>
                        <div className="col-span-2 text-xs text-slateLux-700 flex items-center gap-1.5 whitespace-nowrap">
                          <span className={`w-2.5 h-2.5 rounded-full ${contact.statusMeta?.dot || "bg-gold-500"}`} />
                          <span>{contact.statusMeta?.label || "Responsive"}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
                <section className="lg:col-span-6 p-3 sm:p-4 bg-white min-w-0 min-h-0 overflow-y-auto overflow-x-hidden">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <button className="text-slateLux-500 hover:text-slateLux-700 transition">←</button>
                    <button className="text-slateLux-500 hover:text-slateLux-700 transition">📷</button>
                    <button className="text-slateLux-500 hover:text-slateLux-700 transition">🗑</button>
                    <span className="inline-flex items-center gap-2 ml-auto text-[10px] tracking-[0.12em] text-slateLux-500 shrink-0">
                      MESSAGES FROM
                      <span className="inline-flex w-8 h-8 rounded-md bg-white border border-pearl-200 items-center justify-center">
                        <SocialProviderIcon platform={activeSocialPlatformId} />
                      </span>
                    </span>
                  </div>
                  <h3 className="text-2xl sm:text-[32px] leading-tight sm:leading-none font-semibold mb-1 break-words">
                    {activeSocialContact?.name || "Social Contact"}
                  </h3>
                  <div className="text-[11px] tracking-wide text-slateLux-500 mb-3">
                    {activeSocialContact?.handle ? activeSocialContact.handle.toUpperCase() : "HEAD OF OPERATIONS"}
                  </div>

                  <div className="space-y-3 max-h-[calc(100vh-320px)] overflow-y-auto pr-1">
                    {activeSocialConversation.map((msg) => (
                      <div key={msg.id}>
                        <div className="text-xs text-slateLux-500 mb-1">
                          {msg.authorName || msg.authorHandle} · {msg.receivedAt ? new Date(msg.receivedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                        </div>
                        <div className="bg-pearl-100 rounded-xl3 px-3 py-2 text-sm leading-relaxed text-slateLux-900">
                          {msg.text}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 flex items-end gap-2 border border-pearl-200 rounded-xl2 px-2 py-2 bg-white">
                    <span className="text-slateLux-500 pb-1">📎</span>
                    <textarea
                      ref={socialReplyInputRef}
                      rows={1}
                      className="flex-1 outline-none text-sm leading-5 resize-none max-h-[168px] overflow-y-auto bg-transparent"
                      placeholder={`Reply to ${activeSocialContact?.name || "contact"} via ${socialPlatformLabel(
                        activeSocialPlatformId
                      )}`}
                      value={socialReply.text}
                      onChange={handleSocialReplyInput}
                    />
                    <button
                      className="text-[11px] px-2 py-1 rounded border border-pearl-200 bg-pearl-50 hover:bg-pearl-100 transition font-semibold disabled:opacity-50"
                      onClick={() =>
                        runAction("socialReplySuggestions", async () => {
                          await loadSocialReplySuggestions(socialReply.socialMessageId);
                        })
                      }
                      disabled={!socialReply.socialMessageId}
                    >
                      {busyAction === "socialReplySuggestions" ? "AI Replies…" : "AI Replies"}
                    </button>
                    <button className="text-[22px] leading-none px-1" onClick={() => runAction("replySocial", replyToSocialMessage)}>➤</button>
                  </div>
                  {socialReplySuggestions.length > 0 ? (
                    <div ref={socialSuggestionPanelRef} className="mt-2 border border-pearl-200 rounded-xl2 p-2 bg-pearl-50 max-h-[180px] overflow-y-auto">
                      {socialAiPolicy.requiresHumanApproval ? (
                        <div className="mb-2 text-[11px] text-slateLux-500">
                          Suggestions require human review before sending.
                        </div>
                      ) : null}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="text-[11px] uppercase tracking-wide text-slateLux-500">
                          Suggested replies {socialReplySuggestionsSource ? `(${socialReplySuggestionsSource})` : ""}
                        </div>
                        {socialReplySuggestionStatus ? (
                          <span className="text-[10px] px-2 py-[2px] rounded-full bg-accent-600/10 text-accent-700">
                            {socialReplySuggestionStatus}
                          </span>
                        ) : null}
                        <div className="ml-auto inline-flex border border-pearl-200 rounded-md overflow-hidden text-[11px]">
                          <button
                            className={`px-2 py-1 transition ${socialReplySuggestionMode === "replace" ? "bg-accent-600 text-white" : "bg-white text-slateLux-700 hover:bg-pearl-50"}`}
                            onClick={() => setSocialReplySuggestionMode("replace")}
                          >
                            Replace
                          </button>
                          <button
                            className={`px-2 py-1 border-l border-pearl-200 transition ${socialReplySuggestionMode === "append" ? "bg-accent-600 text-white" : "bg-white text-slateLux-700 hover:bg-pearl-50"}`}
                            onClick={() => setSocialReplySuggestionMode("append")}
                          >
                            Append
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {socialReplySuggestions.map((suggestion, idx) => (
                          <div
                            key={`social-suggestion-${idx}`}
                            className="text-left text-[12px] leading-snug px-2 py-2 rounded-xl2 border border-pearl-200 bg-white max-w-[calc(50%-8px)]"
                          >
                            <div className="text-slateLux-700">{String(suggestion || "").slice(0, 160)}</div>
                            <div className="mt-2 flex items-center gap-2">
                              <button
                                className="px-2 py-1 rounded border border-pearl-200 text-[10px] font-semibold hover:bg-pearl-50 transition"
                                onClick={() => applySocialReplySuggestion(suggestion)}
                              >
                                Use
                              </button>
                              <button
                                className="px-2 py-1 rounded border border-pearl-200 text-[10px] font-semibold hover:bg-pearl-50 transition"
                                onClick={() => copySocialReplySuggestion(suggestion)}
                              >
                                Copy
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </section>
              </div>
            </div>
          ) : (
            <>
              {selectedMessage ? (
                <div className="bg-pearl-50 border border-pearl-200 rounded-xl2 px-3 py-2 mb-3">
                  <h2 className="text-[22px] leading-[1.2] font-semibold tracking-tight">{selectedMessage.subject}</h2>
                </div>
              ) : null}
              {selectedMessage ? (
                <div className="flex items-center gap-3 bg-pearl-50 border border-pearl-200 rounded-xl2 px-3 py-2 mb-4">
                  <div className="w-10 h-10 rounded-full bg-ink-900 text-white flex items-center justify-center font-semibold text-sm shrink-0">
                    {(selectedMessage.from_name || "U").slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold text-[15px] leading-tight">{selectedMessage.from_name || selectedMessage.from_email}</div>
                    <div className="text-[12px] text-slate-500 leading-tight">{selectedMessage.from_email ? `<${selectedMessage.from_email}>` : ""}</div>
                  </div>
                </div>
              ) : null}

              {selectedAttachments.length > 0 ? (
                <div className="mt-1 border border-pearl-200 rounded-xl2 p-3 bg-white">
                  <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <span>📎</span>
                    <span>{selectedAttachments.length} attachment(s)</span>
                  </div>
                  {selectedAttachments.map((attachment, index) => (
                    <button
                      key={`${attachment.fileName}-${index}`}
                      className="w-full flex items-center justify-between bg-pearl-50 rounded-xl2 px-3 py-2 mb-2 last:mb-0 border border-pearl-200 hover:bg-pearl-100 transition"
                      onClick={() => openQuickLook(attachment)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="inline-flex w-8 h-8 rounded bg-pearl-200 text-[10px] font-semibold items-center justify-center text-slateLux-700 shrink-0">
                          {getAttachmentGlyph(attachment.fileName, attachment.mimeType)}
                        </span>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{attachment.fileName}</div>
                          <div className="text-xs text-slateLux-500">{formatFileSize(attachment.size)}</div>
                        </div>
                      </div>
                      <span className="text-xs text-slateLux-700 shrink-0 flex items-center gap-2">
                        {attachment?.url ? (
                          <a
                            className="text-accent-700 hover:underline"
                            href={attachment.url}
                            onClick={(e) => e.stopPropagation()}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Download
                          </a>
                        ) : null}
                        <span>Quick Look</span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}

              {selectedMessage?.conversationTrail?.length > 0 ? (
                <div className="mt-4 border border-pearl-200 rounded-xl2 p-3 bg-white">
                  <button className="text-xs mb-2" onClick={() => setTimelineCollapsed((v) => !v)}>
                    {timelineCollapsed ? "Show thread timeline" : "Hide thread timeline"}
                  </button>
                  {!timelineCollapsed ? (
                    <div className="max-h-36 overflow-y-auto">
                      {selectedMessage.conversationTrail.map((item, index) => (
                        <div key={`${item.provider_message_id}-${index}`} className="text-xs py-1 border-b last:border-b-0">
                          <span className="font-semibold">{item.subject || "(No subject)"}</span>{" "}
                          <span className="text-slate-500">· {item.from_name || item.from_email}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {selectedMessage ? (
                loadingThread ? (
                  <div className="px-3 mt-6">
                    <SkeletonList rows={6} />
                  </div>
                ) : (
                  <div className="mt-8 whitespace-pre-wrap text-[16px] leading-[1.7] px-3">{selectedMessage.snippet || ""}</div>
                )
              ) : (
                <div className="flex min-h-[min(540px,calc(100vh-200px))] flex-col gap-4">
                  <div className="shrink-0">
                    <DailyBriefingCard
                      briefing={briefing}
                      loading={loadingBriefing}
                      variant="workspace"
                      onRefresh={() => loadBriefing()}
                    />
                  </div>
                  <div className="flex flex-1 flex-col items-center justify-center rounded-[2px] border border-dashed border-[#c8c6c4] bg-[#faf9f8] px-4 py-10 text-center">
                    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 shadow-sm">
                      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
                        <rect x="4" y="6" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" />
                        <path d="m5 8 7 5 7-5" stroke="currentColor" strokeWidth="1.6" />
                      </svg>
                    </div>
                    <div className="text-[15px] font-semibold text-slate-700">Select a message</div>
                    <div className="mt-1 max-w-[28ch] text-[13px] text-slate-500">
                      Choose a row in the list to open the message in this reading pane.
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        <aside
          className={`flex flex-col items-center overflow-y-auto border-l border-[#edebe9] bg-[#f3f2f1] py-2 ${isMobile ? "hidden" : ""} ${
            templateEditorFullBleed ? "col-start-4" : ""
          }`}
        >
          <div className="mb-2 h-px w-5 bg-[#edebe9]" />
          {socialRailItems.map((platform) => (
            <button
              key={platform.id}
              title={platform.connected ? `Open ${platform.id} inbox` : `Connect ${platform.id}`}
              className={`mb-1.5 flex h-9 w-9 items-center justify-center rounded-[2px] border text-xs font-semibold ${
                platform.connected
                  ? activeSocialAccountId === platform.accountId && activePage === "social"
                    ? "border-[#0078d4] bg-[#deecf9] text-[#0078d4]"
                    : "border-[#edebe9] bg-white text-[#323130] shadow-sm hover:bg-[#edebe9]"
                  : "border-[#edebe9] bg-[#faf9f8] text-[#a19f9d]"
              }`}
              onClick={() => runAction(`openSocial-${platform.id}`, async () => openSocialPlatform(platform.id))}
            >
              <span className={`inline-flex w-6 h-6 rounded items-center justify-center ${platform.connected ? "bg-white" : "bg-slate-200"}`}>
                <SocialProviderIcon platform={platform.id} />
              </span>
            </button>
          ))}
          <div className="my-1.5 h-px w-5 bg-[#edebe9]" />
          <button
            className="flex h-9 w-9 items-center justify-center rounded-[2px] border border-[#edebe9] bg-white text-[20px] font-light leading-none text-[#323130] shadow-sm transition hover:bg-[#edebe9]"
            onClick={() => setActivePage("settings")}
            title="More"
          >
            +
          </button>
        </aside>
      </section>

      {aiDraftOpen ? (
        <div className="fixed inset-0 z-[10010] bg-black/40 backdrop-blur-[2px] flex items-end md:items-center justify-center p-3">
          <button className="absolute inset-0" aria-label="Close AI draft" onClick={() => setAiDraftOpen(false)} />
          <div className="relative w-[min(920px,96vw)] bg-white rounded-xl3 border border-pearl-200 shadow-float overflow-hidden">
            <div className="px-5 py-4 border-b border-pearl-200 flex items-center gap-2">
              <div className="font-semibold text-sm text-slate-900">AI Draft</div>
              <div className="ml-auto flex items-center gap-2">
                <button
                  className="h-9 px-4 rounded-full border border-pearl-200 bg-white hover:bg-pearl-50 transition text-[12px] font-semibold text-slateLux-700"
                  onClick={() => setAiDraftOpen(false)}
                >
                  Close
                </button>
                <button
                  className="h-9 px-4 rounded-full bg-ink-900 text-white hover:bg-ink-800 transition text-[12px] font-semibold"
                  onClick={() => {
                    const el = composeTextRef.current;
                    if (!el) return;
                    el.innerHTML = escapeHtml(aiDraftResult || "").replace(/\n/g, "<br>");
                    syncComposeFromEditor();
                    setAiDraftOpen(false);
                    pushNotification("Draft applied.");
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
              <div className="space-y-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Intent</div>
                  <textarea
                    className="mt-1 w-full h-24 rounded-xl3 border border-pearl-200 bg-white p-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                    value={aiDraftIntent}
                    onChange={(e) => setAiDraftIntent(e.target.value)}
                  />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Tone</div>
                  <input
                    className="mt-1 w-full h-10 rounded-xl3 border border-pearl-200 bg-white px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                    value={aiDraftTone}
                    onChange={(e) => setAiDraftTone(e.target.value)}
                  />
                </div>
                <button
                  className="w-full h-10 rounded-xl3 border border-pearl-200 bg-white hover:bg-pearl-50 transition text-sm font-semibold text-slateLux-900 disabled:opacity-50"
                  disabled={aiDraftLoading}
                  onClick={generateAiDraft}
                >
                  {aiDraftLoading ? "Drafting…" : "Regenerate"}
                </button>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Draft</div>
                <textarea
                  className="mt-1 w-full h-[min(52vh,520px)] rounded-xl3 border border-pearl-200 bg-pearl-50 p-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                  value={aiDraftResult}
                  onChange={(e) => setAiDraftResult(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <QuickLookModal
        open={quickLookOpen}
        attachment={quickLookAttachment}
        onClose={() => {
          setQuickLookOpen(false);
          setQuickLookAttachment(null);
        }}
      />
      {briefingSheetOpen && isMobile ? (
        <div
          className="fixed inset-0 z-[55] flex flex-col justify-end bg-black/40 pt-[max(0.5rem,env(safe-area-inset-top,0px))]"
          role="dialog"
          aria-modal="true"
          aria-label="Daily briefing"
          onClick={(e) => {
            if (e.target === e.currentTarget) setBriefingSheetOpen(false);
          }}
        >
          <div className="mx-2 mb-[calc(0.5rem+env(safe-area-inset-bottom,0px))] max-h-[min(88dvh,720px)] rounded-t-2xl bg-pearl-50 shadow-lux overflow-hidden flex flex-col min-h-0">
            <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-pearl-200 bg-white">
              <span className="text-sm font-semibold text-slateLux-900">Daily briefing</span>
              <button
                type="button"
                className="text-2xl leading-none px-2 py-1 rounded-lg hover:bg-pearl-100 text-slateLux-700"
                onClick={() => setBriefingSheetOpen(false)}
                aria-label="Close briefing"
              >
                ×
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-3">
              <DailyBriefingCard
                briefing={briefing}
                loading={loadingBriefing}
                variant="workspace"
                onRefresh={() => loadBriefing()}
              />
            </div>
          </div>
        </div>
      ) : null}
      <MobileBottomNav
        activePage={activePage}
        onNavigate={(page) => {
          setActivePage(page);
          if (page !== "mail") {
            setMobileDetailOpen(false);
            setBriefingSheetOpen(false);
          }
          if (page !== "chat") setChatOpen(false);
          if (page === "chat") {
            setChatOpen(true);
            haptic(8);
          }
          if (page === "settings") {
            haptic(8);
          }
        }}
      />
      <div
        className={`fixed z-40 ${isMobile ? "inset-0 flex items-stretch justify-center p-2 pb-[calc(6rem+env(safe-area-inset-bottom,0px))] pt-[max(0.5rem,env(safe-area-inset-top,0px))]" : "bottom-5 right-5"}`}
      >
        {chatOpen ? (
          <div
            ref={chatWidgetRef}
            className={`bg-pearl-50 border border-pearl-200 rounded-xl3 shadow-lux flex flex-col overflow-hidden min-h-0 min-w-0 max-w-[100vw] touch-manipulation ${
              isMobile
                ? "w-full max-h-[calc(100dvh-6.25rem)] h-[min(100%,calc(100dvh-6.25rem))]"
                : "w-[min(430px,calc(100vw-1.5rem))] h-[min(600px,calc(100dvh-2.5rem))]"
            }`}
          >
            <div className="min-h-11 shrink-0 bg-accent-600 text-white px-2 sm:px-3 py-2 flex flex-wrap items-center gap-x-1 gap-y-1">
              <span className="font-semibold text-sm shrink-0">Team Chat</span>
              <button
                className={`ml-auto sm:ml-0 text-[10px] sm:text-xs rounded px-1.5 sm:px-2 py-0.5 sm:py-1 border border-white/30 ${chatSoundEnabled ? "bg-white/20" : "bg-transparent"}`}
                onClick={() => setChatSoundEnabled((v) => !v)}
                title="Toggle chat sound"
              >
                {chatSoundEnabled ? "Sound On" : "Sound Off"}
              </button>
              <button
                className={`text-[10px] sm:text-xs rounded px-1.5 sm:px-2 py-0.5 sm:py-1 border border-white/30 ${desktopNotifEnabled ? "bg-white/20" : "bg-transparent"}`}
                onClick={async () => {
                  if (typeof window === "undefined" || !("Notification" in window)) {
                    pushNotification("Desktop notifications are not supported in this browser.");
                    return;
                  }
                  if (Notification.permission === "granted") {
                    setDesktopNotifEnabled((v) => !v);
                    return;
                  }
                  const permission = await Notification.requestPermission();
                  setDesktopNotifEnabled(permission === "granted");
                  if (permission !== "granted") {
                    pushNotification("Desktop notification permission was denied.");
                  }
                }}
                title="Toggle desktop notifications"
              >
                Alerts
              </button>
              <button
                className="text-[10px] sm:text-xs rounded px-1.5 sm:px-2 py-0.5 sm:py-1 border border-white/30 bg-transparent"
                title="Mark all as read"
                onClick={() => setChatUnreadByConversation({})}
              >
                Mark read
              </button>
              <button className="text-lg leading-none ml-auto sm:ml-0" onClick={() => setChatOpen(false)} title="Close chat">×</button>
            </div>
            <div className="p-2 border-b border-pearl-200 bg-white shrink-0">
              <div className="flex flex-col sm:flex-row gap-2 relative">
                <div className="flex-1 relative">
                  <input
                    className="w-full border border-pearl-200 rounded-xl2 px-2 py-1.5 text-xs bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                    placeholder="Start by email or username"
                    value={chatIdentifier}
                    onFocus={() => setChatContactPickerOpen(true)}
                    onChange={(e) => {
                      setChatIdentifier(e.target.value);
                      setChatContactPickerOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        runAction("startChat", startChatByIdentifier);
                      }
                    }}
                  />
                  {chatContactPickerOpen && filteredChatContacts.length > 0 ? (
                    <div className="absolute top-[34px] left-0 right-0 z-20 bg-white border border-pearl-200 rounded-xl2 shadow-luxSoft max-h-48 overflow-y-auto">
                      {filteredChatContacts.map((contact) => (
                        <button
                          key={contact.id}
                          className="w-full text-left px-2 py-2 hover:bg-pearl-50 transition border-b border-pearl-200/60 last:border-b-0"
                          onClick={() => runAction("startChat", async () => startChatWithIdentifier(contact.username || contact.email))}
                        >
                          <div className="text-xs font-semibold text-slate-700 truncate">
                            {contact.displayName || contact.username || contact.email}
                          </div>
                          <div className="text-[10px] text-slate-500 truncate">
                            @{contact.username || "user"} · {contact.email}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <button
                  className="bg-accent-600 hover:bg-accent-700 transition text-white rounded-xl2 px-3 py-2 sm:py-1.5 text-xs font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/35 shrink-0 w-full sm:w-auto"
                  onClick={() => runAction("startChat", startChatByIdentifier)}
                >
                  Start
                </button>
              </div>
            </div>
            <div className={`flex min-h-0 flex-1 overflow-hidden ${isMobile ? "flex-col" : "flex-row"}`}>
              <div
                className={`border-pearl-200 overflow-y-auto bg-white min-h-0 min-w-0 ${
                  isMobile ? "flex-1 border-b" : "w-[168px] shrink-0 border-r"
                } ${isMobile && !chatMobileShowList ? "hidden" : ""}`}
              >
                {chatConversations.length === 0 ? (
                  <div className="text-[11px] text-slate-500 p-2">No chats yet.</div>
                ) : (
                  chatConversations.map((conversation) => (
                    <button
                      key={conversation.id}
                      className={`w-full text-left px-2 py-2 border-b border-pearl-200/60 ${
                        activeConversationId === conversation.id ? "bg-accent-600/10" : "hover:bg-pearl-50"
                      }`}
                      onClick={() => {
                        setActiveConversationId(conversation.id);
                        if (isMobile) setChatMobileShowList(false);
                        setChatUnreadByConversation((prev) => {
                          if (!prev[conversation.id]) return prev;
                          const next = { ...prev };
                          delete next[conversation.id];
                          return next;
                        });
                      }}
                    >
                      <div className="text-xs font-semibold truncate flex items-center gap-1">
                        {conversation.participant?.displayName || conversation.participant?.username || conversation.participant?.email}
                        {chatUnreadByConversation[conversation.id] ? (
                          <span className="inline-flex min-w-[16px] h-4 rounded-full bg-accent-600 text-white text-[10px] items-center justify-center px-1">
                            {chatUnreadByConversation[conversation.id]}
                          </span>
                        ) : null}
                      </div>
                      <div className="text-[10px] text-slate-500 truncate">{conversation.lastMessage || "..."}</div>
                    </button>
                  ))
                )}
              </div>
              <div
                className={`flex flex-col min-h-0 min-w-0 flex-1 ${isMobile && chatMobileShowList ? "hidden" : ""}`}
              >
                <div className="px-2.5 py-1.5 border-b border-pearl-200 bg-white shrink-0 flex items-start gap-2">
                  {isMobile ? (
                    <button
                      type="button"
                      className="mt-0.5 shrink-0 text-accent-600 font-semibold text-sm px-1 py-0.5 rounded hover:bg-pearl-50"
                      onClick={() => setChatMobileShowList(true)}
                      aria-label="Back to conversations"
                    >
                      ←
                    </button>
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold truncate">
                      {activeChatConversation?.participant?.displayName ||
                        activeChatConversation?.participant?.username ||
                        activeChatConversation?.participant?.email ||
                        "Conversation"}
                    </div>
                    <div className="text-[10px] text-slate-500">{chatPresenceLabel}</div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2.5 space-y-2 bg-pearl-50">
                  {chatMessages.length === 0 ? (
                    <div className="text-[11px] text-slate-500">Select a chat and send a message.</div>
                  ) : (
                    chatMessages.map((message) => (
                      <div
                        key={message.id}
                        className={`text-xs flex ${message.senderUserId === currentUser?.id ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-lg px-2.5 py-1.5 break-words shadow-sm ${
                            message.senderUserId === currentUser?.id ? "bg-accent-600/10" : "bg-white"
                          }`}
                        >
                          <div className="text-[10px] text-slate-500 mb-0.5">
                            {message.sender?.username || message.sender?.email} ·{" "}
                            {new Date(message.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </div>
                          <div>{message.body}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="border-t border-pearl-200 bg-white p-2 flex flex-col gap-2 sm:flex-row sm:items-end min-w-0 shrink-0">
                  <input
                    className="flex-1 min-w-0 border border-pearl-200 rounded-full bg-white px-3 py-2 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/25"
                    placeholder="Type a message"
                    value={chatDraft}
                    onChange={(e) => setChatDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        runAction("sendChat", sendChatMessage);
                      }
                    }}
                  />
                  <button
                    className="bg-accent-600 hover:bg-accent-700 transition text-white rounded-full px-4 py-2.5 sm:py-2 text-xs font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/35 w-full sm:w-auto shrink-0"
                    onClick={() => runAction("sendChat", sendChatMessage)}
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <button ref={chatToggleRef} className="h-11 px-4 rounded-full bg-accent-600 hover:bg-accent-700 transition text-white text-sm font-semibold shadow-luxSoft relative focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/35" onClick={() => setChatOpen(true)}>
            Open Chat
            {chatUnreadCount > 0 ? (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-danger-600 text-white text-[10px] px-1 flex items-center justify-center">
                {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
              </span>
            ) : null}
          </button>
        )}
      </div>

      <CommandBar
        open={commandBarOpen}
        query={commandQuery}
        setQuery={setCommandQuery}
        actions={commandActions}
        onClose={() => {
          setCommandBarOpen(false);
          setCommandQuery("");
        }}
      />
    </main>
  );
};

export default Inbox;
